import React, { useEffect, useRef, useState } from 'react';
import Papa from 'papaparse';
import { saveAs } from 'file-saver';
import {
  buildDeterministicSeed,
  findCampersWithInsufficientUniquePreferences,
  optimizeSchedule,
  validateSolutionConstraints,
} from './solver/optimizer';
import { runWorkerSolveJob } from './workers/schedulerWorker';
import {
  copyLocation as copyLocationRecord,
  createLocation as createLocationRecord,
  deleteLocation as deleteLocationRecord,
  createStaffTeam as createStaffTeamRecord,
  deleteStaffTeam as deleteStaffTeamRecord,
  fetchSettingsBootstrap,
  saveLocationCampers,
  saveLocationSkills,
  setActiveLocation as saveActiveLocationSelection,
  updateStaffTeam as updateStaffTeamRecord,
  updateLocation as updateLocationRecord,
} from './settings/api';
import {
  canGroupTribes,
  createDefaultTribes,
  deriveCoveredGradeRange,
  findCampersOutsideLocationCoverage,
  getTribeForGrade,
  getTribeNames,
  mapCampersToLocation,
  normalizeTribes,
  normalizeCoveredGradeRange,
  validateLocationGradeCoverage,
  validateTribes,
} from './settings/locationModel';
import {
  buildSkillsConfigFromSettings,
  mergeSkillRecords,
  normalizeSkillRecord,
} from './settings/skills';
import {
  getRequiredAssignmentCount,
  normalizeRequiredSchedule,
  REQUIRED_SCHEDULE_MODE_FIXED,
  REQUIRED_SCHEDULE_MODE_PER_BLOCK,
} from './settings/requiredSchedule';
import {
  buildCamperScheduleRows,
  buildScheduleExportFilename,
  buildSkillRosterWorkbook,
  filterWorkbookRows,
  getWorkbookSheetNames,
  readWorkbookFromArrayBuffer,
  sheetToRowObjects,
  writeWorkbookArrayBuffer,
} from './io/workbook';
import { analyzeScheduleQuality } from './analysis/scheduleQuality';

/*
 * Dynamic skills configuration. This object is populated with entries for
 * every skill encountered in the campers or staffers CSV files. Each entry
 * may specify allowed genders, allowed tribes, and whether the skill is
 * indoor or outdoor. Skills not present default to open to all campers and
 * indoor.
 *
 * The configuration is initialized with a few known rules but will be
 * overridden once the user uploads their CSVs. See normalizeSkillName and
 * buildDynamicSkillsConfig for how skills are unified and configured.
 */
let skillsConfig = {
  'GIRLS CLUB': {
    allowedGenders: ['Female'],
    allowedTribes: ['Kava', 'Sulu', 'Taka', 'Masu'],
    indoor: true,
  },
  'MAN CAVE': {
    allowedGenders: ['Male'],
    allowedTribes: ['Kava', 'Sulu', 'Taka', 'Masu'],
    indoor: true,
  },
  'KAVA ONLY': {
    allowedGenders: ['Male', 'Female'],
    allowedTribes: ['Kava'],
    indoor: true,
  },
  'GET MESSY': {
    allowedGenders: ['Male', 'Female'],
    allowedTribes: ['Sulu', 'Taka', 'Masu'],
    indoor: false,
  },
  'ONE EIGHT': {
    allowedGenders: ['Male', 'Female'],
    allowedTribes: ['Masu'],
    indoor: true,
  },
};

// Constants controlling default group sizes. The One Eight rotation index is no
// longer a constant; it will be configurable via the UI. We keep defaults
// here for initial state only.
const GROUP_MIN = 10;
const GROUP_MAX = 25;
const DEFAULT_ONE_EIGHT_ROTATION = 4; // zero‑based index for the fifth rotation (Rotation 5)
const DEFAULT_LOCATION_DRAFT = {
  id: null,
  name: '',
  defaultGroupMin: GROUP_MIN,
  defaultGroupMax: GROUP_MAX,
  coveredGradeMin: 0,
  coveredGradeMax: 8,
  tribes: createDefaultTribes(),
};

// Predefined staffer lists. These can be selected via the UI. Each entry
// consists of a name and up to two skills. These lists serve as the
// baseline for the editable staffer lists. The user can later modify
// these lists via the "Edit" buttons in the UI.
const defaultOrangeStaffers = [
  { name: 'Staffer 1', skills: ['ARCHERY'] },
  { name: 'Staffer 2', skills: ['BASKETBALL'] },
  { name: 'Staffer 3', skills: ['BUILD IT UP'] },
  { name: 'Staffer 4', skills: ['CRAFTS'] },
  { name: 'Staffer 5', skills: ['DANCE'] },
  { name: 'Staffer 6', skills: ['EPIC EATS'] },
  { name: 'Staffer 7', skills: ['FLAG FOOTBALL'] },
  { name: 'Staffer 8', skills: ['GET MESSY'] },
  { name: 'Staffer 9', skills: ['GIRLS CLUB'] },
  { name: 'Staffer 10', skills: ['KAVA ONLY'] },
  { name: 'Staffer 11', skills: ['MAN CAVE'] },
  { name: 'Staffer 12', skills: ['ON THE LAWN'] },
  { name: 'Staffer 13', skills: ['ONE EIGHT', 'MASU MADNESS'] },
  { name: 'Staffer 14', skills: ['PAINTING'] },
  { name: 'Staffer 15', skills: ['SCIENCE LAB'] },
  { name: 'Staffer 16', skills: ['SOCCER'] },
  { name: 'Staffer 17', skills: ['TUMBLE'] },
  { name: 'Staffer 18', skills: ['UNDER COVER'] },
];

const defaultYellowStaffers = [
  { name: 'Staffer 1', skills: ['ARCHERY'] },
  { name: 'Staffer 2', skills: ['BASKETBALL'] },
  { name: 'Staffer 3', skills: ['BUILD IT UP'] },
  { name: 'Staffer 4', skills: ['CRAFTS'] },
  { name: 'Staffer 5', skills: ['DANCE'] },
  { name: 'Staffer 6', skills: ['EPIC EATS'] },
  { name: 'Staffer 7', skills: ['FLAG FOOTBALL'] },
  { name: 'Staffer 8', skills: ['GET MESSY'] },
  { name: 'Staffer 9', skills: ['GIRLS CLUB'] },
  { name: 'Staffer 10', skills: ['KAVA ONLY'] },
  { name: 'Staffer 11', skills: ['MAN CAVE'] },
  { name: 'Staffer 12', skills: ['ON THE LAWN'] },
  { name: 'Staffer 13', skills: ['ONE EIGHT', 'MASU MADNESS'] },
  { name: 'Staffer 14', skills: ['PAINTING'] },
  { name: 'Staffer 15', skills: ['SCIENCE LAB'] },
  { name: 'Staffer 16', skills: ['SOCCER'] },
  { name: 'Staffer 17', skills: ['TUMBLE'] },
  { name: 'Staffer 18', skills: ['UNDER COVER'] },
];

const swat2Staffers = [
  { name: 'Staffer 19', skills: ['EPIC EATS', 'GAME ON'] },
  { name: 'Staffer 20', skills: ['CRAFTS', 'PAINTING'] },
  { name: 'Staffer 21', skills: ['ARCHERY', 'ON THE LAWN'] },
  { name: 'Staffer 22', skills: ['FLAG FOOTBALL', 'UNDER COVER'] },
];

const swat3Staffers = swat2Staffers.map((s) => ({ ...s }));
const swat4Staffers = swat2Staffers.map((s) => ({ ...s }));

// Clone helper for default staff lists. This ensures that when the
// lists are turned into state values they are independent of the
// defaults. Without cloning, editing one list would mutate the defaults.
function cloneStaffList(list) {
  return list.map((st) => ({ name: st.name, skills: [...(st.skills || [])] }));
}

// Build preset staffers exactly as the UI does for Orange/Yellow plus optional SWAT.
function buildPresetStaffers(staffListChoice = 'Orange', swatEnabled = false, swatChoice = 'SWAT 2') {
  let selectedStaffers = staffListChoice === 'Orange'
    ? cloneStaffList(defaultOrangeStaffers)
    : cloneStaffList(defaultYellowStaffers);
  if (swatEnabled) {
    let swatStaff;
    if (swatChoice === 'SWAT 2') swatStaff = swat2Staffers;
    else if (swatChoice === 'SWAT 3') swatStaff = swat3Staffers;
    else swatStaff = swat4Staffers;
    selectedStaffers = selectedStaffers.concat(cloneStaffList(swatStaff));
  }
  return selectedStaffers;
}

function buildStaffersFromTeams(staffTeams = [], activeStaffTeamIds = []) {
  const teamsById = new Map((staffTeams || []).map((team) => [team.id, team]));
  const selectedTeamIds = Array.isArray(activeStaffTeamIds) ? activeStaffTeamIds : [];
  return selectedTeamIds.flatMap((teamId) => {
    const team = teamsById.get(teamId);
    if (!team) return [];
    return cloneStaffList(team.staff || []);
  });
}

// Helper to parse a grade string into a numeric grade. Kindergarten and K
// variants are mapped to 0.
function parseGrade(gradeStr) {
  if (!gradeStr) return 0;
  const lower = String(gradeStr).trim().toLowerCase();
  if (lower.startsWith('k')) return 0;
  const match = lower.match(/(\d+)/);
  return match ? parseInt(match[1], 10) : 0;
}

// Map a numeric grade to a tribe name. The camp organizes campers into
// four tribes: Kava (K–1), Sulu (2–3), Taka (4–5), Masu (6–8). Grades above
// 8 are treated as Masu.
function getTribeFromGrade(numGrade, tribes = createDefaultTribes()) {
  return getTribeForGrade(numGrade, tribes);
}

// Normalize a skill name to a canonical form. This helper removes
// parenthetical notes such as grade or gender restrictions and unifies
// common synonyms and casing. All returned names are uppercase. For example
// "Girls Club (Females only)" and "Girl's Club" both normalize to
// "GIRLS CLUB". Likewise, "1:8 (6th-8th grade only)" becomes "ONE EIGHT".
function normalizeSkillName(name) {
  if (!name) return '';
  let s = name.trim();
  // Remove any apostrophes to simplify matching
  s = s.replace(/'/g, '');
  // Remove parentheses and their contents
  s = s.replace(/\s*\([^)]*\)/g, '').trim();
  // Collapse multiple spaces
  s = s.replace(/\s+/g, ' ');
  const upper = s.toUpperCase();
  // Synonym mappings
  if (upper.includes('1:8') || upper.includes('ONE EIGHT')) return 'ONE EIGHT';
  if (upper.includes('GIRL') && upper.includes('CLUB')) return 'GIRLS CLUB';
  if (upper.includes('MAN CAVE')) return 'MAN CAVE';
  if (upper.includes('KAVA ONLY')) return 'KAVA ONLY';
  if (upper.includes('GET MESSY')) return 'GET MESSY';
  if (upper.includes('MASU MADNESS')) return 'MASU MADNESS';
  if (upper.includes('UNDER COVER') || upper.includes('UNDERCOVER')) return 'UNDER COVER';
  if (upper.includes('ON THE LAWN')) return 'ON THE LAWN';
  if (upper.includes('BUILD IT UP')) return 'BUILD IT UP';
  // Unify "Get Crafty" and "Crafts" into a single canonical name: CRAFTS
  if (upper.includes('GET CRAFTY')) return 'CRAFTS';
  if (/\bCRAFTS?\b/.test(upper)) return 'CRAFTS';
  if (upper.includes('EPIC EATS')) return 'EPIC EATS';
  if (upper.includes('GIRLS CLUB')) return 'GIRLS CLUB';
  // For any remaining names, return the cleaned uppercase string
  return upper;
}

// Normalize a gender string to capitalized form. Unknown values default to
// 'Unspecified', which is handled as though it matches both genders.
function normalizeGender(gender) {
  if (!gender) return 'Unspecified';
  const g = gender.toString().trim().toLowerCase();
  if (g.startsWith('f')) return 'Female';
  if (g.startsWith('m')) return 'Male';
  return 'Unspecified';
}

// Determine if a skill is allowed for a given camper. Skills present in
// SKILLS_CONFIG may impose gender or tribe restrictions; unknown skills are
// considered unrestricted. The function expects both skill and gender in
// uppercase for comparison.
function isSkillAllowedForCamper(skill, camper, config = skillsConfig) {
  return isCamperGenderEligibleForSkill(skill, camper, config) && isCamperTribeEligibleForSkill(skill, camper, config);
}

function isCamperGenderEligibleForSkill(skill, camper, configMap = skillsConfig) {
  const config = configMap?.[skill];
  if (!config) return true;
  const allowedGenders = Array.isArray(config.allowedGenders) ? config.allowedGenders : [];
  if (allowedGenders.length === 0) return true;
  const allowsMale = allowedGenders.includes('Male');
  const allowsFemale = allowedGenders.includes('Female');
  const unrestricted = allowsMale && allowsFemale;
  if (camper.gender === 'Unspecified') return unrestricted;
  return allowedGenders.includes(camper.gender);
}

function isCamperTribeEligibleForSkill(skill, camper, configMap = skillsConfig) {
  const config = configMap?.[skill];
  if (!config) return true;
  const allowedTribes = Array.isArray(config.allowedTribes) ? config.allowedTribes : [];
  if (allowedTribes.length === 0) return true;
  return allowedTribes.includes(camper.tribe);
}

// Determine if a skill is outdoor. Skills not listed default to indoor.
function isSkillOutdoor(skill) {
  const config = skillsConfig[skill];
  return config ? !config.indoor : false;
}

function extractSourceCamperId(row) {
  for (const key of Object.keys(row || {})) {
    const lower = key.toLowerCase();
    if (!lower.includes('id')) continue;
    if (!(lower.includes('camper') || lower.includes('person') || lower === 'id')) continue;
    const value = row[key];
    if (value == null || String(value).trim() === '') continue;
    return String(value).trim();
  }
  return null;
}

function sanitizeIdentifierFragment(value) {
  return String(value || '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '') || 'unknown';
}

function buildImportBatchId(sourceLabel, sourceType = 'csv') {
  return `${sanitizeIdentifierFragment(sourceType)}:${sanitizeIdentifierFragment(sourceLabel)}`;
}

function getCamperAssignmentKey(camper) {
  return camper?.camperId || camper?.name || '';
}

function hashIdentifierValue(value) {
  const text = String(value || '');
  let hash = 0x811c9dc5;
  for (let i = 0; i < text.length; i += 1) {
    hash ^= text.charCodeAt(i);
    hash = Math.imul(hash, 0x01000193) >>> 0;
  }
  return hash.toString(16);
}

function normalizeLoadedCamper(camper, tribesInput = createDefaultTribes()) {
  const tribes = Array.isArray(tribesInput) ? tribesInput : createDefaultTribes();
  const numericGrade = Number.isFinite(Number(camper?.grade)) ? Number(camper.grade) : 0;
  const rawRow = camper?.rawRow && typeof camper.rawRow === 'object' ? camper.rawRow : {};
  const rawRowPreferences = getPreferenceColumns(rawRow).length > 0 ? collectNormalizedPreferences(rawRow) : [];
  const storedPreferences = Array.isArray(camper?.preferences)
    ? camper.preferences.map((preference) => normalizeSkillName(preference)).filter(Boolean)
    : [];
  return {
    camperId: camper?.camperId || `row:restored:${sanitizeIdentifierFragment(camper?.name || 'unknown')}`,
    name: String(camper?.name || '').trim(),
    sourceCamperId: camper?.sourceCamperId || null,
    gender: normalizeGender(camper?.gender),
    grade: numericGrade,
    tribe: camper?.tribe || getTribeFromGrade(numericGrade, tribes),
    preferences: rawRowPreferences.length > 0 ? rawRowPreferences : storedPreferences,
    assignments: camper?.assignments && typeof camper.assignments === 'object' ? camper.assignments : {},
    assignedSkills: new Set(Array.isArray(camper?.assignedSkills) ? camper.assignedSkills : []),
    sourceType: camper?.sourceType || 'csv',
    sourceLabel: camper?.sourceLabel || null,
    sourceRowNumber: camper?.sourceRowNumber ?? null,
    importBatchId: camper?.importBatchId || null,
    importedAt: camper?.importedAt || null,
    rawRow,
  };
}

function getPreferenceColumnRank(key, fallbackIndex) {
  const keyStr = key.toString().trim();
  const lower = keyStr.toLowerCase();
  if (/^\d+$/.test(keyStr)) return Number(keyStr);
  if (!(lower.includes('skill') || lower.includes('request'))) return Number.MAX_SAFE_INTEGER;
  const ordinalMatch = lower.match(/(?:^|[^0-9])(\d+)\s*(?:st|nd|rd|th)?(?:[^0-9]|$)/);
  return ordinalMatch ? Number(ordinalMatch[1]) : 1000 + fallbackIndex;
}

function getPreferenceColumns(row) {
  return Object.keys(row || {})
    .map((key, index) => {
      const keyStr = key.toString();
      const lower = keyStr.toLowerCase();
      const isNumberedPreferenceColumn = /^\d+$/.test(keyStr.trim());
      const isPreferenceColumn = lower.includes('skill') || lower.includes('request') || isNumberedPreferenceColumn;
      return isPreferenceColumn
        ? { key, index, rank: getPreferenceColumnRank(key, index) }
        : null;
    })
    .filter(Boolean)
    .sort((a, b) => a.rank - b.rank || a.index - b.index);
}

function collectNormalizedPreferences(row) {
  const preferences = [];
  getPreferenceColumns(row).forEach(({ key }) => {
    const val = row[key];
    if (!val) return;
    const normalized = normalizeSkillName(val.toString());
    if (normalized && !preferences.includes(normalized)) {
      preferences.push(normalized);
    }
  });
  return preferences;
}

// Parse a row from the campers CSV into a camper object. The camper
// definition includes name, gender, grade, tribe, cleaned preferences, and
// initial assignment mapping.
function parseCamperRow(row, tribesInput = createDefaultTribes(), options = {}) {
  const tribes = Array.isArray(tribesInput) ? tribesInput : createDefaultTribes();
  // Extract the name, gender, and grade from columns containing those keywords
  let name = '';
  let genderStr = '';
  let gradeStr = '';
  Object.keys(row).forEach((key) => {
    const lower = key.toLowerCase();
    const val = row[key];
    if (!val) return;
    if (lower.includes('name')) {
      name = val.toString().trim();
    } else if (lower.includes('gender')) {
      genderStr = val.toString().trim();
    } else if (lower.includes('grade')) {
      gradeStr = val.toString().trim();
    }
  });
  const gender = normalizeGender(genderStr);
  const numericGrade = parseGrade(gradeStr);
  const tribe = getTribeFromGrade(numericGrade, tribes);
  const sourceCamperId = options.sourceCamperId || extractSourceCamperId(row);
  const sourceRowNumber = Number.isFinite(Number(options.sourceRowNumber)) ? Number(options.sourceRowNumber) : null;
  const importBatchId = options.importBatchId || buildImportBatchId(options.sourceLabel || 'manual-import', options.sourceType || 'csv');
  const rowFingerprint = hashIdentifierValue(JSON.stringify(row || {}));
  const camperId = sourceCamperId
    ? `source:${sanitizeIdentifierFragment(sourceCamperId)}`
    : sourceRowNumber != null
      ? `row:${sanitizeIdentifierFragment(importBatchId)}:${sourceRowNumber}`
      : `row:${sanitizeIdentifierFragment(importBatchId)}:${rowFingerprint}`;
  // Collect preference columns: any column containing 'skill' or 'request'
  const preferences = collectNormalizedPreferences(row);
  return {
    camperId,
    name,
    sourceCamperId,
    gender,
    grade: numericGrade,
    tribe,
    preferences,
    assignments: {}, // rotation index -> groupKey
    assignedSkills: new Set(), // track which skills assigned to avoid repeats
    sourceType: options.sourceType || 'csv',
    sourceLabel: options.sourceLabel || null,
    sourceRowNumber,
    importBatchId,
    importedAt: options.importedAt || null,
    rawRow: row,
  };
}

function finalizeImportedCampers(rows, tribesInput = createDefaultTribes(), options = {}) {
  const importBatchId = options.importBatchId || buildImportBatchId(options.sourceLabel || 'manual-import', options.sourceType || 'csv');
  const parsed = rows
    .map((row, index) => parseCamperRow(row, tribesInput, {
      ...options,
      importBatchId,
      sourceRowNumber: index + 1,
    }))
    .filter((camper) => camper.name);
  const seenNames = new Map();
  return parsed.map((camper) => {
    const count = (seenNames.get(camper.name) || 0) + 1;
    seenNames.set(camper.name, count);
    if (count === 1) return camper;
    return {
      ...camper,
      name: `${camper.name} (${count})`,
    };
  });
}

// Parse a staffers CSV row. Each row is assumed to represent a single staff
// member with up to two skills. Skills are normalized to uppercase.
function parseStafferRow(row) {
  let name = '';
  const skills = [];
  for (const key of Object.keys(row)) {
    const val = row[key];
    if (!val) continue;
    const keyLower = key.toLowerCase();
    if (keyLower.includes('name')) {
      name = val.toString().trim();
      continue;
    }
    // Only treat columns labelled as skills
    if (keyLower.includes('skill')) {
      const skill = val.toString().trim();
      if (skill) skills.push(normalizeSkillName(skill));
    }
  }
  return { name, skills };
}

// Shared pre-scheduling input cleanup used by both UI and tests.
function prepareSchedulingInputs(campers, currentStaffers, options = {}) {
  const tribeNames = options.tribeNames || ['Kava', 'Sulu', 'Taka', 'Masu'];
  const savedSkillSettings = options.skillSettings || [];
  const skillSet = new Set();
  campers.forEach((camper) => {
    (camper.preferences || []).forEach((pref) => skillSet.add(pref));
  });
  currentStaffers.forEach((staffer) => {
    if (Array.isArray(staffer.skills)) {
      staffer.skills.forEach((s) => skillSet.add(s));
    }
  });
  skillSet.add('ONE EIGHT');
  skillSet.add('MASU MADNESS');
  const mergedSkillSettings = mergeSkillRecords(Array.from(skillSet), savedSkillSettings, tribeNames);
  const dynamicConfig = buildDynamicSkillsConfig(Array.from(skillSet), {
    tribeNames,
    savedSkillSettings: mergedSkillSettings,
  });
  skillsConfig = { ...dynamicConfig };
  const cleanedCampers = campers.map((camper) => {
    const filtered = (camper.preferences || []).filter((pref) => isSkillAllowedForCamper(pref, camper));
    return { ...camper, preferences: filtered };
  });
  return { cleanedCampers, skillSet, dynamicConfig, mergedSkillSettings };
}

function runOptimizerSchedulingPipeline(campers, currentStaffers, options = {}) {
  const {
    groupMin = GROUP_MIN,
    groupMax = GROUP_MAX,
    oneEightRotation = DEFAULT_ONE_EIGHT_ROTATION,
    maxRuntimeMs = 15000,
    seed = null,
    dynamicMinDelta = 7,
    tribeNames = ['Kava', 'Sulu', 'Taka', 'Masu'],
    skillSettings = [],
    locationConfig = null,
  } = options;

  const { cleanedCampers, dynamicConfig, mergedSkillSettings } = prepareSchedulingInputs(campers, currentStaffers, {
    tribeNames,
    skillSettings,
  });
  const solverInput = {
    campers: cleanedCampers,
    staffers: currentStaffers,
    skillsConfig: dynamicConfig,
    skillSettings: mergedSkillSettings,
    settings: {
      groupMin,
      groupMax,
      oneEightRotation,
      dynamicMinDelta,
      locationConfig,
    },
  };
  const deterministicSeed = seed ?? buildDeterministicSeed(solverInput);
  const { solution, diagnostics } = optimizeSchedule(solverInput, {
    seed: deterministicSeed,
    maxRuntimeMs,
  });
  return {
    cleanedCampers,
    assignments: solution.assignments,
    groups: solution.groups,
    unplaced: solution.unplaced,
    preferenceHits: solution.preferenceHits,
    staffAssignments: solution.staffAssignments,
    diagnostics,
    skillsConfigSnapshot: dynamicConfig,
    stageMetrics: [],
  };
}

// Shared end-to-end scheduler pipeline. Runtime path is optimizer-only.
function runSchedulingPipeline(campers, currentStaffers, options = {}) {
  return runOptimizerSchedulingPipeline(campers, currentStaffers, options);
}

// Build a dynamic skills configuration based on the set of all skills
// encountered. By default every skill is open to all genders and tribes
// and considered indoor. Specific skills override these defaults. Outdoor
// skills are defined by the OUTDOOR_SKILLS array; additional restrictions
// based on keywords are applied.
function buildDynamicSkillsConfig(allSkills, options = {}) {
  const tribeNames = options.tribeNames || ['Kava', 'Sulu', 'Taka', 'Masu'];
  const savedSkillSettings = mergeSkillRecords(allSkills, options.savedSkillSettings || [], tribeNames);
  return buildSkillsConfigFromSettings(savedSkillSettings, tribeNames);
}

// Compute pack statistics based on current campers and group settings. Packs are
// conceptual groupings within each tribe. If genderedPacks is true, each
// tribe is further split by gender. Returns an object mapping tribes to
// counts and recommended number of packs, along with average pack size.
function computePackStats(campers, groupMax, genderedPacks, tribeNames = []) {
  const stats = {};
  const tribes = tribeNames.length > 0 ? tribeNames : ['Kava', 'Sulu', 'Taka', 'Masu'];
  tribes.forEach((tribe) => {
    const tribeCampers = campers.filter((c) => c.tribe === tribe);
    const total = tribeCampers.length;
    if (genderedPacks) {
      const males = tribeCampers.filter((c) => c.gender === 'Male').length;
      const females = tribeCampers.filter((c) => c.gender === 'Female').length;
      const malePacks = males > 0 ? Math.ceil(males / groupMax) : 0;
      const femalePacks = females > 0 ? Math.ceil(females / groupMax) : 0;
      const packs = malePacks + femalePacks;
      const avgSize = packs > 0 ? total / packs : 0;
      stats[tribe] = {
        total,
        packs,
        avgSize: avgSize.toFixed(1),
        maleCampers: males,
        femaleCampers: females,
        malePacks,
        femalePacks,
      };
    } else {
      const packs = total > 0 ? Math.ceil(total / groupMax) : 0;
      const avgSize = packs > 0 ? total / packs : 0;
      stats[tribe] = {
        total,
        packs,
        avgSize: avgSize.toFixed(1),
      };
    }
  });
  return stats;
}

// Compute rotation statistics for all groups. Returns an array where each
// element corresponds to a rotation and contains details about group sizes
// and whether they exceed max or fall below min. Also includes a total
// assigned count for the rotation.
function computeRotationStats(groups, groupMin, groupMax) {
  const stats = [];
  groups.forEach((rotationGroups) => {
    const rotationStat = { groups: {}, totalAssigned: 0 };
    Object.keys(rotationGroups).forEach((key) => {
      const grp = rotationGroups[key];
      if (!isGroupEnabled(grp)) return;
      const count = grp.campers.length;
      rotationStat.groups[key] = {
        count,
        overMax: count > groupMax,
        underMin: count > 0 && count < groupMin,
      };
      rotationStat.totalAssigned += count;
    });
    stats.push(rotationStat);
  });
  return stats;
}

function getSkillMinSizeForUi(skill, defaultGroupMin, skillsConfigMap = skillsConfig, rotationIndex = null) {
  const config = skillsConfigMap?.[skill];
  if (!config) return defaultGroupMin;
  if (rotationIndex != null && Array.isArray(config.rotationOverrides)) {
    const rotationOverride = config.rotationOverrides.find(
      (entry) => Number(entry.rotationIndex) === rotationIndex && entry.enabled !== false
    );
    if (rotationOverride?.override?.minGroupSize != null) return rotationOverride.override.minGroupSize;
  }
  if (config.minGroupSize != null) return config.minGroupSize;
  return defaultGroupMin;
}

function buildRotationSkillMatrix(groups = [], settings = {}, skillsConfigMap = skillsConfig) {
  const defaultGroupMin = settings.groupMin ?? 10;
  const defaultGroupMax = settings.groupMax ?? 25;
  const skillNames = new Set();
  const rotations = groups.map((rotationGroups, rotationIndex) => {
    const bySkill = {};
    Object.keys(rotationGroups || {}).forEach((groupKey) => {
      const group = rotationGroups[groupKey];
      if (!group || !isGroupEnabled(group)) return;
      const skill = group.skill;
      skillNames.add(skill);
      if (!bySkill[skill]) {
        bySkill[skill] = {
          skill,
          rotationIndex,
          groupCount: 0,
          camperCount: 0,
          staffedGroups: 0,
          unstaffedGroups: 0,
          overMaxGroups: 0,
          underMinGroups: 0,
          manualExceptionGroups: 0,
        };
      }
      const cell = bySkill[skill];
      const camperCount = (group.campers || []).length;
      const maxSize = getGroupMaxSize(group, defaultGroupMax);
      const minSize = getSkillMinSizeForUi(skill, defaultGroupMin, skillsConfigMap, rotationIndex);
      const overMax = camperCount > maxSize;
      const underMin = camperCount > 0 && camperCount < minSize;
      const unstaffed = camperCount > 0 && !group.staff;
      cell.groupCount += 1;
      cell.camperCount += camperCount;
      if (group.staff) cell.staffedGroups += 1;
      if (unstaffed) cell.unstaffedGroups += 1;
      if (overMax) cell.overMaxGroups += 1;
      if (underMin) cell.underMinGroups += 1;
      if (overMax || unstaffed) cell.manualExceptionGroups += 1;
    });
    return bySkill;
  });

  return {
    skills: Array.from(skillNames).sort((a, b) => a.localeCompare(b)),
    rotations,
  };
}

function buildConstraintAuditSummary(audit) {
  if (!audit) return null;
  return {
    blockingCount: audit.blockingIssues.length,
    manualExceptionCount: audit.manualExceptions.length,
    statusLabel: audit.canExport ? 'Constraint audit clear' : 'Constraint audit needs attention',
    sections: [
      {
        key: 'blocking',
        title: 'Blocking issues',
        items: audit.blockingIssues,
        emptyLabel: 'No blocking issues.',
      },
      {
        key: 'manual',
        title: 'Manual exceptions',
        items: audit.manualExceptions,
        emptyLabel: 'No manual exceptions.',
      },
    ],
  };
}

function RequiredScheduleModal({ skill, onUpdate, onClose }) {
  if (!skill) return null;
  const schedule = normalizeRequiredSchedule(skill.requiredSchedule, skill.fixedRotations);
  const shortestBlock = Math.min(...schedule.blocks.map((block) => block.length));
  const unusedRotations = [0, 1, 2, 3, 4, 5]
    .filter((rotation) => !schedule.blocks.some((block) => block.includes(rotation)));
  const updateSchedule = (updater) => onUpdate((previousSkill) => {
    const previousSchedule = normalizeRequiredSchedule(previousSkill.requiredSchedule, previousSkill.fixedRotations);
    return {
      ...previousSkill,
      requiredSchedule: updater(previousSchedule, previousSkill),
    };
  });

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" role="presentation">
      <div
        className="max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-lg bg-white shadow-xl"
        role="dialog"
        aria-modal="true"
        aria-labelledby="required-schedule-title"
      >
        <div className="flex items-start justify-between gap-4 border-b px-5 py-4">
          <div>
            <h2 id="required-schedule-title" className="text-lg font-semibold">Required Schedule: {skill.name}</h2>
            <p className="mt-1 text-sm text-gray-600">Choose how eligible campers receive this required skill.</p>
          </div>
          <button type="button" onClick={onClose} className="rounded px-2 py-1 text-gray-500 hover:bg-gray-100" aria-label="Close required schedule">
            ✕
          </button>
        </div>
        <div className="space-y-4 px-5 py-4 text-sm">
          <label className="block">
            <span className="mb-1 block font-medium">Requirement type</span>
            <select
              value={schedule.mode}
              onChange={(event) => updateSchedule((previous) => normalizeRequiredSchedule({
                ...previous,
                mode: event.target.value === REQUIRED_SCHEDULE_MODE_PER_BLOCK
                  ? REQUIRED_SCHEDULE_MODE_PER_BLOCK
                  : REQUIRED_SCHEDULE_MODE_FIXED,
              }, skill.fixedRotations))}
              className="w-full rounded border border-gray-300 px-3 py-2"
            >
              <option value={REQUIRED_SCHEDULE_MODE_FIXED}>Every selected run rotation</option>
              <option value={REQUIRED_SCHEDULE_MODE_PER_BLOCK}>Once per rotation block</option>
            </select>
          </label>

          {schedule.mode === REQUIRED_SCHEDULE_MODE_FIXED ? (
            <div className="rounded border border-blue-200 bg-blue-50 px-3 py-2 text-blue-900">
              Select the required rotations using the Run Rotations checkboxes in the Skill Options table.
            </div>
          ) : (
            <div className="space-y-4 rounded border bg-gray-50 p-4">
              <div>
                <div className="font-medium">Rotation blocks</div>
                <p className="mb-2 text-xs text-gray-600">Each eligible camper receives this skill once in every block.</p>
                <div className="space-y-2">
                  {schedule.blocks.map((block, blockIndex) => (
                    <div key={`block-${blockIndex}`} className="flex flex-wrap items-center gap-2 rounded border bg-white p-2">
                      <span className="w-14 font-medium">Block {blockIndex + 1}</span>
                      {[0, 1, 2, 3, 4, 5].map((rotationIndex) => (
                        <label key={rotationIndex} className="flex items-center gap-1 text-xs">
                          <input
                            type="checkbox"
                            checked={block.includes(rotationIndex)}
                            onChange={(event) => updateSchedule((previous) => {
                              const blocks = previous.blocks.map((entry) => entry.slice());
                              blocks[blockIndex] = event.target.checked
                                ? Array.from(new Set([...blocks[blockIndex], rotationIndex])).sort((a, b) => a - b)
                                : blocks[blockIndex].filter((value) => value !== rotationIndex);
                              return { ...previous, blocks };
                            })}
                          />
                          R{rotationIndex + 1}
                        </label>
                      ))}
                      {schedule.blocks.length > 1 && (
                        <button
                          type="button"
                          className="ml-auto text-xs text-red-600 hover:underline"
                          onClick={() => updateSchedule((previous) => ({
                            ...previous,
                            blocks: previous.blocks.filter((_, index) => index !== blockIndex),
                          }))}
                        >
                          Remove
                        </button>
                      )}
                    </div>
                  ))}
                </div>
                {unusedRotations.length >= schedule.blocks[0].length && (
                  <button
                    type="button"
                    className="mt-2 text-sm text-blue-700 hover:underline"
                    onClick={() => updateSchedule((previous) => {
                      const used = new Set(previous.blocks.flat());
                      const available = [0, 1, 2, 3, 4, 5].filter((rotation) => !used.has(rotation));
                      return {
                        ...previous,
                        blocks: [...previous.blocks, available.slice(0, previous.blocks[0].length)],
                      };
                    })}
                  >
                    + Add block
                  </button>
                )}
              </div>

              <div>
                <div className="font-medium">Allowed positions in each block</div>
                <div className="mt-2 flex flex-wrap gap-3">
                  {Array.from({ length: shortestBlock }, (_, position) => (
                    <label key={position} className="flex items-center gap-1">
                      <input
                        type="checkbox"
                        checked={schedule.allowedPositions.includes(position)}
                        onChange={(event) => updateSchedule((previous) => ({
                          ...previous,
                          allowedPositions: event.target.checked
                            ? Array.from(new Set([...previous.allowedPositions, position])).sort((a, b) => a - b)
                            : previous.allowedPositions.filter((value) => value !== position),
                        }))}
                      />
                      Position {position + 1}
                    </label>
                  ))}
                </div>
              </div>

              <label className="flex flex-wrap items-center gap-2">
                <span className="font-medium">Preferred position</span>
                <select
                  value={schedule.preferredPosition}
                  onChange={(event) => updateSchedule((previous) => ({
                    ...previous,
                    preferredPosition: Number(event.target.value),
                  }))}
                  className="rounded border border-gray-300 px-2 py-1"
                >
                  {schedule.allowedPositions.map((position) => (
                    <option key={position} value={position}>Position {position + 1}</option>
                  ))}
                </select>
              </label>

              <label className="flex items-start gap-2">
                <input
                  type="checkbox"
                  checked={schedule.samePositionAcrossBlocks}
                  onChange={(event) => updateSchedule((previous) => ({
                    ...previous,
                    samePositionAcrossBlocks: event.target.checked,
                  }))}
                  className="mt-1"
                />
                <span>
                  <span className="block font-medium">Keep the same position across blocks</span>
                  <span className="text-xs text-gray-600">For example, Rotation 1 pairs with Rotation 4.</span>
                </span>
              </label>
            </div>
          )}
        </div>
        <div className="flex justify-end border-t px-5 py-4">
          <button type="button" onClick={onClose} className="rounded bg-blue-600 px-4 py-2 text-white hover:bg-blue-700">Done</button>
        </div>
      </div>
    </div>
  );
}

function isGroupEnabled(group) {
  return group == null || group.enabled !== false;
}

function isManualPlacementTargetGroup(group) {
  return !!group && isGroupEnabled(group) && (group.campers || []).length > 0;
}

function getGroupMaxSize(group, defaultGroupMax) {
  if (!group) return defaultGroupMax;
  if (typeof group.maxSize === 'number' && Number.isFinite(group.maxSize)) return group.maxSize;
  return defaultGroupMax;
}

function getManualSkillRepeatLimit(skill, camper, config = skillsConfig) {
  const skillConfig = config?.[skill];
  if (!skillConfig) return 1;
  if (skillConfig.repeatable === true) return 6;
  if (!skillConfig.requiredForEligible) return 1;
  if (!isCamperGenderEligibleForSkill(skill, camper, config) || !isCamperTribeEligibleForSkill(skill, camper, config)) {
    return 1;
  }
  return getRequiredAssignmentCount(skillConfig);
}

function getAssignedSkillCountForCamper(
  camper,
  skill,
  assignmentsOverride,
  groupsOverride,
  excludedRotation = null
) {
  if (!camper || !skill || !assignmentsOverride || !groupsOverride) return 0;
  const camAssignments = assignmentsOverride[getCamperAssignmentKey(camper)] || {};
  return Object.keys(camAssignments).reduce((count, rotationKey) => {
    const rotationIndex = Number(rotationKey);
    if (rotationIndex === excludedRotation) return count;
    const groupKey = camAssignments[rotationIndex];
    const group = groupsOverride[rotationIndex] && groupsOverride[rotationIndex][groupKey];
    if (group && group.skill === skill) return count + 1;
    return count;
  }, 0);
}

function isGroupAllowedForCamper(group, camper) {
  if (!group || !camper) return false;
  if (!isGroupEnabled(group)) return false;
  // Optional per-group grade gate (WIN-style “open to grades”).
  if (group.allowedGrades) {
    const grades = Array.isArray(group.allowedGrades) ? group.allowedGrades : [];
    if (grades.length > 0 && !grades.includes(camper.grade)) return false;
  } else {
    const min = typeof group.allowedGradeMin === 'number' ? group.allowedGradeMin : null;
    const max = typeof group.allowedGradeMax === 'number' ? group.allowedGradeMax : null;
    if (min != null && camper.grade < min) return false;
    if (max != null && camper.grade > max) return false;
  }
  return true;
}

function stafferCanTeachSkill(staffer, skill) {
  if (!staffer || !skill || !Array.isArray(staffer.skills)) return false;
  return staffer.skills.includes(skill);
}

// Given the current unplaced list, the camper being modified, and their updated assignments,
// return a new unplaced list that reflects whether the camper still needs manual placement.
function updateUnplacedListAfterRemoval(currentUnplaced, camper, updatedAssignments) {
  const camperKey = getCamperAssignmentKey(camper);
  const filtered = currentUnplaced.filter((c) => getCamperAssignmentKey(c) !== camperKey);
  if (!updatedAssignments || Object.keys(updatedAssignments).length < 6) {
    filtered.push(camper);
  }
  return filtered;
}

function applyCamperPlacementToState(
  camper,
  rotationIndex,
  groupKey,
  targetAssignments,
  targetGroups
) {
  if (!camper || rotationIndex == null || !targetAssignments || !targetGroups) return false;
  const camperKey = getCamperAssignmentKey(camper);
  const camperAssignments = { ...(targetAssignments[camperKey] || {}) };
  const previousGroupKey = camperAssignments[rotationIndex];
  const rotationGroups = targetGroups[rotationIndex] || {};

  if (previousGroupKey && rotationGroups[previousGroupKey]) {
    const previousGroup = rotationGroups[previousGroupKey];
    previousGroup.campers = (previousGroup.campers || []).filter((c) => getCamperAssignmentKey(c) !== camperKey);
    previousGroup.tribes = previousGroup.tribes || {};
    previousGroup.tribes[camper.tribe] = (previousGroup.tribes[camper.tribe] || 1) - 1;
    if (previousGroup.tribes[camper.tribe] <= 0) delete previousGroup.tribes[camper.tribe];
    delete camperAssignments[rotationIndex];
  } else if (previousGroupKey) {
    delete camperAssignments[rotationIndex];
  }

  if (groupKey) {
    const targetGroup = rotationGroups[groupKey];
    if (!targetGroup) {
      targetAssignments[camperKey] = camperAssignments;
      return false;
    }
    targetGroup.tribes = targetGroup.tribes || {};
    if (!(targetGroup.campers || []).some((c) => getCamperAssignmentKey(c) === camperKey)) {
      targetGroup.campers.push(camper);
    }
    targetGroup.tribes[camper.tribe] = (targetGroup.tribes[camper.tribe] || 0) + 1;
    camperAssignments[rotationIndex] = groupKey;
  }

  targetAssignments[camperKey] = camperAssignments;
  return true;
}

function projectedRotationAverageAfterPlacement(rotationIndex, groupsOverride) {
  if (!groupsOverride || rotationIndex == null) return 0;
  const rotationGroups = groupsOverride[rotationIndex] || {};
  const enabledGroups = Object.keys(rotationGroups)
    .map((gKey) => rotationGroups[gKey])
    .filter((group) => group && isGroupEnabled(group));
  if (enabledGroups.length === 0) return 0;
  const totalCampers = enabledGroups.reduce((sum, group) => sum + (group.campers || []).length, 0);
  return (totalCampers + 1) / enabledGroups.length;
}

function buildManualPlacementCandidate({
  camper,
  rotationIndex,
  groupKey,
  assignmentsOverride,
  groupsOverride,
  defaultGroupMin,
  defaultGroupMax,
  activeLocation,
  skillConfig = skillsConfig,
}) {
  if (!camper || !groupsOverride || rotationIndex == null) return null;
  const rotationGroups = groupsOverride[rotationIndex] || {};
  const group = rotationGroups[groupKey];
  if (!isManualPlacementTargetGroup(group)) return null;
  if (!isCamperGenderEligibleForSkill(group.skill, camper, skillConfig)) return null;
  if (!isGroupAllowedForCamper(group, camper)) return null;

  const repeatLimit = getManualSkillRepeatLimit(group.skill, camper, skillConfig);
  const assignedCount = getAssignedSkillCountForCamper(
    camper,
    group.skill,
    assignmentsOverride,
    groupsOverride,
    rotationIndex
  );
  if (assignedCount >= repeatLimit) return null;

  const skill = group.skill;
  const requested = (camper.preferences || []).includes(skill);
  const preferenceRankIdx = (camper.preferences || []).findIndex((pref) => pref === skill);
  const preferenceRank = preferenceRankIdx >= 0 ? preferenceRankIdx + 1 : null;
  const maxSize = getGroupMaxSize(group, defaultGroupMax);
  const minSize = getSkillMinSizeForUi(skill, defaultGroupMin ?? 10, skillConfig, rotationIndex);
  const projectedSize = (group.campers || []).length + 1;
  const overflow = Math.max(0, projectedSize - maxSize);
  const underMinBreak = projectedSize > 0 && projectedSize < minSize;
  const manualOverfillPolicy = skillConfig?.[skill]?.manualOverfillPolicy || 'disallow';
  if (overflow > 0 && manualOverfillPolicy !== 'allow') return null;

  const projectedAverage = projectedRotationAverageAfterPlacement(rotationIndex, groupsOverride);
  const balanceDelta = Math.abs(projectedSize - projectedAverage);
  const groupMaxBreak = overflow > 0;
  const tribeOverrideBreak = !isCamperTribeEligibleForSkill(skill, camper, skillConfig);
  const existingTribes = Object.keys(group.tribes || {}).filter((tribe) => (group.tribes?.[tribe] || 0) > 0);
  const nonAdjacentTribeBreak = existingTribes.length > 0
    ? !canGroupTribes(existingTribes, camper.tribe, activeLocation?.tribes || createDefaultTribes())
    : false;
  if (nonAdjacentTribeBreak) return null;
  const notRequestedBreak = !requested;
  const ruleBreaks = [];

  if (notRequestedBreak) {
    ruleBreaks.push('not requested');
  }
  if (groupMaxBreak) {
    ruleBreaks.push(`over max (${projectedSize}/${maxSize})`);
  }
  if (underMinBreak) {
    ruleBreaks.push(`below min (${projectedSize}/${minSize})`);
  }
  if (tribeOverrideBreak) {
    ruleBreaks.push('tribe override');
  }
  return {
    rotationIndex,
    groupKey,
    skill,
    requested,
    preferenceRank,
    groupMaxBreak,
    underMinBreak,
    tribeOverrideBreak,
    notRequestedBreak,
    breakCount: [groupMaxBreak, underMinBreak, tribeOverrideBreak, notRequestedBreak].filter(Boolean).length,
    notRequested: notRequestedBreak,
    overflow,
    balanceDelta,
    ruleBreaks,
    projectedSize,
    maxSize,
    minSize,
  };
}

function selectRecommendedManualCandidate(candidates = []) {
  return candidates.find((candidate) => candidate.requested && !candidate.underMinBreak) || null;
}

// Assign staffers to each group in each rotation. Each staffer can only
// supervise one group per rotation and must be capable of teaching the
// group's skill. Returns a mapping of staffer names to an array of group
// keys indexed by rotation (null if off). Also annotates the group objects
// with a 'staff' property.
// Assign staffers to groups using a scarcity‑aware strategy. Unique skills are
// assigned first, then remaining groups are staffed in order of increasing
// staff availability.
function assignStaffersToGroups(groups, staffers) {
  const rotationCount = groups.length;
  // Build skill → set of staffers who can teach it
  const skillStaffers = {};
  staffers.forEach((st) => {
    (st.skills || []).forEach((sk) => {
      if (!skillStaffers[sk]) skillStaffers[sk] = new Set();
      skillStaffers[sk].add(st.name);
    });
  });
  // Initialize assignments: stafferName → array of 6 rotation assignments (group keys)
  const staffAssignments = {};
  staffers.forEach((st) => {
    staffAssignments[st.name] = Array(rotationCount).fill(null);
  });
  // Seed assignments from any existing group.staff markers so mapping stays consistent
  for (let r = 0; r < rotationCount; r++) {
    const rotationGroups = groups[r] || {};
    Object.keys(rotationGroups).forEach((gKey) => {
      const group = rotationGroups[gKey];
      if (group.staff) {
        if (!staffAssignments[group.staff]) {
          staffAssignments[group.staff] = Array(rotationCount).fill(null);
        }
        staffAssignments[group.staff][r] = gKey;
      }
    });
  }
  // For convenience, precompute which staffer is unique for a skill
  const uniqueSkillTeacher = {};
  Object.keys(skillStaffers).forEach((sk) => {
    if (skillStaffers[sk].size === 1) {
      uniqueSkillTeacher[sk] = Array.from(skillStaffers[sk])[0];
    }
  });
  // Iterate through each rotation
  for (let r = 0; r < rotationCount; r++) {
    const rotationGroups = groups[r] || {};
    const groupKeys = Object.keys(rotationGroups);
    // Sort group keys by scarcity: skills with fewer qualified staffers first, then larger groups first
    groupKeys.sort((a, b) => {
      const ga = rotationGroups[a];
      const gb = rotationGroups[b];
      const gaEnabled = isGroupEnabled(ga);
      const gbEnabled = isGroupEnabled(gb);
      if (gaEnabled !== gbEnabled) return gaEnabled ? -1 : 1;
      const skA = ga.skill;
      const skB = gb.skill;
      const sa = skillStaffers[skA] ? skillStaffers[skA].size : 0;
      const sb = skillStaffers[skB] ? skillStaffers[skB].size : 0;
      if (sa !== sb) return sa - sb;
      // Tie break by larger group first
      return gb.campers.length - ga.campers.length;
    });
    // Assign each group
    groupKeys.forEach((gKey) => {
      const group = rotationGroups[gKey];
      if (!isGroupEnabled(group)) {
        group.staff = null;
        return;
      }
      if ((group.campers || []).length === 0) {
        group.staff = null;
        return;
      }
      if (group.staff) return; // already assigned, seeded above
      const skill = group.skill;
      const normalized = skill;
      // Determine candidate staffers: those who can teach the skill and are free in this rotation
      let candidates = [];
      if (skillStaffers[normalized]) {
        candidates = Array.from(skillStaffers[normalized]).filter((name) => staffAssignments[name][r] === null);
      }
      if (candidates.length === 0) {
        // No qualified staffer free; leave unstaffed
        group.staff = null;
        return;
      }
      // If skill has a unique teacher, assign them if available
      if (uniqueSkillTeacher[normalized]) {
        const must = uniqueSkillTeacher[normalized];
        if (staffAssignments[must][r] === null) {
          staffAssignments[must][r] = gKey;
          group.staff = must;
          return;
        }
      }
      // Choose candidate with fewest alternative options in this rotation
      const altOptionsCount = (staffName) => {
        let count = 0;
        for (const otherKey of groupKeys) {
          const otherGroup = rotationGroups[otherKey];
          const otherSkill = otherGroup.skill;
          // skip already staffed groups
          if (otherGroup.staff) continue;
          if (skillStaffers[otherSkill] && skillStaffers[otherSkill].has(staffName) && staffAssignments[staffName][r] === null) {
            count++;
          }
        }
        return count;
      };
      const skillPriority = (staffName) => {
        const staffer = staffers.find((entry) => entry.name === staffName);
        if (!staffer || !Array.isArray(staffer.skills)) return Number.POSITIVE_INFINITY;
        const directIndex = staffer.skills.findIndex((candidate) => candidate === skill);
        return directIndex >= 0 ? directIndex : Number.POSITIVE_INFINITY;
      };
      candidates.sort((a, b) => {
        const priorityDelta = skillPriority(a) - skillPriority(b);
        if (priorityDelta !== 0) return priorityDelta;
        return altOptionsCount(a) - altOptionsCount(b);
      });
      const chosen = candidates[0];
      staffAssignments[chosen][r] = gKey;
      group.staff = chosen;
    });
  }
  return staffAssignments;
}
// Convert staff assignments into a CSV string. Includes the roster of campers for each group.
function staffAssignmentsToCSV(staffAssignments, groups) {
  const headers = ['Staff Name'];
  // For each rotation, create two columns: Skill and Roster
  for (let r = 0; r < groups.length; r++) {
    headers.push(`Rotation ${r + 1} Skill`);
    headers.push(`Rotation ${r + 1} Roster`);
  }
  const rows = [];
  Object.keys(staffAssignments).forEach((staffName) => {
    const row = [staffName];
    const assignments = staffAssignments[staffName];
    for (let r = 0; r < assignments.length; r++) {
      const groupKey = assignments[r];
      if (groupKey) {
        const group = groups[r][groupKey];
        const skillName = group.skill;
        const roster = group.campers.map((c) => c.name).join('; ');
        row.push(`${skillName} (${groupKey})`);
        row.push(roster);
      } else {
        row.push('');
        row.push('');
      }
    }
    rows.push(row);
  });
  return Papa.unparse({ fields: headers, data: rows });
}

function assignmentsToCSV(campers, assignments, groups) {
  const header = [
    'First Name',
    'Last Name',
    'CAMPER Tribe',
    'CAMPER Gender',
    'Rotation 1',
    'Rotation 2',
    'Rotation 3',
    'Rotation 4',
    'Rotation 5',
    'Rotation 6',
  ];
  const rows = buildCamperScheduleRows(campers, assignments, groups);
  const csv = Papa.unparse({ fields: header, data: rows });
  return csv;
}

function normalizePreferenceHits(hits) {
  const normalized = { none: 0 };
  for (let rank = 1; rank <= 10; rank++) {
    normalized[rank] = 0;
  }
  if (!hits) return normalized;
  Object.keys(hits).forEach((key) => {
    const value = Number(hits[key]) || 0;
    if (key === 'none') {
      normalized.none += value;
      return;
    }
    const numericKey = Number(key);
    if (Number.isInteger(numericKey) && numericKey >= 1 && numericKey <= 10) {
      normalized[numericKey] += value;
    }
  });
  return normalized;
}

function summarizeDemandDiagnostics(demandDiagnostics, limit = 5) {
  const skills = demandDiagnostics?.skills || {};
  const entries = Object.keys(skills).map((skill) => ({
    skill,
    ...skills[skill],
  }));
  const saturatedHighDemand = entries
    .filter((entry) => entry.saturatedWithUnmetDemand || (entry.unmetWeightedDemand || 0) > 0)
    .sort((a, b) => (b.unmetWeightedDemand || 0) - (a.unmetWeightedDemand || 0) || a.skill.localeCompare(b.skill))
    .slice(0, limit);
  const underfilledLowDemand = entries
    .filter((entry) => (entry.underMinRuns || 0) > 0 || ((entry.plannedRuns || 0) > 0 && (entry.requestCount || 0) < (entry.plannedCapacity || 0) * 0.4))
    .sort((a, b) => (b.underMinRuns || 0) - (a.underMinRuns || 0) || (a.requestCount || 0) - (b.requestCount || 0) || a.skill.localeCompare(b.skill))
    .slice(0, limit);
  const targetRunOutcomes = entries
    .filter((entry) => Number.isFinite(entry.targetRuns))
    .sort((a, b) => (
      (b.targetRunShortfall || 0) - (a.targetRunShortfall || 0)
      || (b.targetRunAttrition || 0) - (a.targetRunAttrition || 0)
      || (b.eligibleRequestCount || 0) - (a.eligibleRequestCount || 0)
      || a.skill.localeCompare(b.skill)
    ));
  const targetRunFailures = targetRunOutcomes.filter((entry) => (
    (entry.targetRunShortfall || 0) > 0
    || (entry.targetRunAttrition || 0) > 0
  ));
  return {
    saturatedHighDemand,
    underfilledLowDemand,
    targetRunOutcomes,
    targetRunFailures,
    phases: demandDiagnostics?.phases || {},
    hasIssues: saturatedHighDemand.length > 0
      || underfilledLowDemand.length > 0
      || targetRunFailures.length > 0,
  };
}

function serializeSolverDiagnostics(diagnostics) {
  return JSON.stringify(diagnostics || {}, null, 2);
}

function summarizeDeepRepairDiagnostics(deepRepair) {
  if (!deepRepair || !deepRepair.attempted) return null;
  return {
    holesAtStart: deepRepair.holesAtStart || 0,
    holesResolved: deepRepair.holesResolved || 0,
    holesRemaining: deepRepair.holesRemaining || 0,
    directInsertions: deepRepair.directInsertions || 0,
    capacityChainsAccepted: deepRepair.capacityChainsAccepted || 0,
    crossRotationPivotsAccepted: deepRepair.crossRotationPivotsAccepted || 0,
    minimumBackfillChainsAccepted: deepRepair.minimumBackfillChainsAccepted || 0,
    nodesExpanded: deepRepair.nodesExpanded || 0,
    maxDepthAttempted: deepRepair.maxDepthAttempted || 0,
    budgetExhausted: !!deepRepair.budgetExhausted,
    cancelled: !!deepRepair.cancelled,
  };
}

function buildStoppedDeepRepairCheckpointResult(checkpoint) {
  if (!checkpoint) return null;
  return {
    ...checkpoint,
    cancelled: true,
    optimizationStopped: !!checkpoint.optimizationStopped,
    diagnostics: {
      ...(checkpoint.diagnostics || {}),
      deepRepair: {
        ...(checkpoint.diagnostics?.deepRepair || {}),
        cancelled: true,
      },
    },
  };
}

function normalizeStaffingForRenderedSchedule(inputGroups, inputStaffAssignments) {
  const groups = (inputGroups || []).map((rotationGroups) => {
    const copy = {};
    Object.keys(rotationGroups || {}).forEach((gKey) => {
      const g = rotationGroups[gKey] || {};
      const campers = Array.isArray(g.campers) ? [...g.campers] : [];
      copy[gKey] = {
        ...g,
        campers,
        tribes: { ...(g.tribes || {}) },
      };
      // Empty groups are inactive shells, not manual/export targets.
      if (campers.length === 0) {
        copy[gKey].enabled = false;
      }
      if (!isGroupEnabled(copy[gKey])) {
        copy[gKey].staff = null;
      }
    });
    return copy;
  });

  const rotationCount = groups.length || 6;
  const staffAssignments = {};
  Object.keys(inputStaffAssignments || {}).forEach((staffName) => {
    staffAssignments[staffName] = Array(rotationCount).fill(null);
  });

  groups.forEach((rotationGroups, r) => {
    Object.keys(rotationGroups || {}).forEach((gKey) => {
      const group = rotationGroups[gKey];
      if (!isGroupEnabled(group)) return;
      if ((group.campers || []).length === 0) return;
      if (!group.staff) return;
      if (!staffAssignments[group.staff]) {
        staffAssignments[group.staff] = Array(rotationCount).fill(null);
      }
      // Keep at most one assignment per staffer per rotation in rendered state.
      if (staffAssignments[group.staff][r] == null) {
        staffAssignments[group.staff][r] = gKey;
      } else {
        group.staff = null;
      }
    });
  });

  return { groups, staffAssignments };
}

function buildExportValidationReport({
  campers = [],
  staffers = [],
  assignments = {},
  groups = [],
  skillsConfigMap = skillsConfig,
  settings = {},
}) {
  const checks = validateSolutionConstraints(
    { assignments, groups },
    {
      campers,
      staffers,
      skillsConfig: skillsConfigMap,
      settings,
    }
  );
  const blockingIssues = [];
  const manualExceptions = [];
  const addBlockingIssue = (code, count, message) => {
    if (!count) return;
    blockingIssues.push({ code, count, message });
  };
  const addManualException = (code, count, message) => {
    if (!count) return;
    manualExceptions.push({ code, count, message });
  };

  const incompleteCampers = campers.filter((camper) => {
    const camAssignments = assignments[getCamperAssignmentKey(camper)] || {};
    return Object.keys(camAssignments).length !== 6;
  }).length;

  let genderEligibilityViolations = 0;
  let tribeEligibilityExceptions = 0;
  let unqualifiedStaffAssignments = 0;
  let emptyStaffedGroups = 0;
  let overMaxExceptions = 0;

  const staffersByName = new Map((staffers || []).map((staffer) => [staffer.name, staffer]));
  groups.forEach((rotationGroups, rotationIndex) => {
    Object.keys(rotationGroups || {}).forEach((groupKey) => {
      const group = rotationGroups[groupKey];
      if (!group || !isGroupEnabled(group)) return;
      const camperCount = (group.campers || []).length;
      const maxSize = getGroupMaxSize(group, settings.groupMax ?? 25);
      if (camperCount === 0 && group.staff) {
        emptyStaffedGroups += 1;
      }
      if (camperCount > maxSize) {
        overMaxExceptions += 1;
      }
      if (camperCount > 0 && group.staff) {
        const staffer = staffersByName.get(group.staff);
        if (!staffer || !stafferCanTeachSkill(staffer, group.skill)) {
          unqualifiedStaffAssignments += 1;
        }
      }
    });
  });

  campers.forEach((camper) => {
    const camAssignments = assignments[getCamperAssignmentKey(camper)] || {};
    Object.keys(camAssignments).forEach((rotationKey) => {
      const rotationIndex = Number(rotationKey);
      const groupKey = camAssignments[rotationIndex];
      const group = groups[rotationIndex] && groups[rotationIndex][groupKey];
      if (!group || !isGroupEnabled(group)) return;
      if (!isCamperGenderEligibleForSkill(group.skill, camper, skillsConfigMap)) {
        genderEligibilityViolations += 1;
        return;
      }
      if (!isCamperTribeEligibleForSkill(group.skill, camper, skillsConfigMap)) {
        tribeEligibilityExceptions += 1;
      }
    });
  });

  addBlockingIssue('incomplete-campers', incompleteCampers, `${incompleteCampers} campers are missing one or more rotations.`);
  addBlockingIssue('duplicate-rotation', checks.onePerRotationViolations, `${checks.onePerRotationViolations} duplicate camper rotation assignments were found.`);
  addBlockingIssue('gender-eligibility', genderEligibilityViolations, `${genderEligibilityViolations} camper assignments violate gender eligibility.`);
  addBlockingIssue('repeated-skills', checks.repeatedSkillViolations, `${checks.repeatedSkillViolations} invalid repeated skill assignments were found.`);
  addBlockingIssue('unqualified-staff', unqualifiedStaffAssignments, `${unqualifiedStaffAssignments} staffed groups have unqualified staff assignments.`);
  addBlockingIssue('staff-double-booked', checks.staffPerRotationViolations, `${checks.staffPerRotationViolations} staff double-booking conflicts were found.`);
  addBlockingIssue('unstaffed-groups', checks.unstaffedGroupViolations, `${checks.unstaffedGroupViolations} non-empty groups are missing staff.`);
  addBlockingIssue('under-min-groups', checks.groupMinViolations, `${checks.groupMinViolations} groups are below minimum size without an exception path.`);
  addBlockingIssue('tribe-adjacency', checks.tribeAdjacencyViolations, `${checks.tribeAdjacencyViolations} groups mix non-adjacent tribes and must be repaired before export.`);
  addBlockingIssue('required-skills', checks.requiredSkillViolations, `${checks.requiredSkillViolations} required-skill assignments are missing.`);
  addBlockingIssue('empty-staffed-groups', emptyStaffedGroups, `${emptyStaffedGroups} empty groups still have staff assigned.`);

  addManualException('tribe-eligibility', tribeEligibilityExceptions, `${tribeEligibilityExceptions} assignments use a tribe override.`);
  addManualException('manual-overfill', overMaxExceptions, `${overMaxExceptions} groups exceed max size as manual overfill exceptions.`);

  return {
    canExport: blockingIssues.length === 0,
    blockingIssues,
    manualExceptions,
    checks,
  };
}

// Main React component implementing the application UI and tying together
// parsing, scheduling, manual assignment, and export. Use of Tailwind
// utilities throughout provides a clean interface without custom CSS.
export default function App() {
  const [campers, setCampers] = useState([]);
  const [staffers, setStaffers] = useState([]);
  const [assignments, setAssignments] = useState(null);
  const [groups, setGroups] = useState(null);
  const [unplaced, setUnplaced] = useState([]);
  const [preferenceHits, setPreferenceHits] = useState(null);
  const [message, setMessage] = useState('');
  const [packStats, setPackStats] = useState(null);
  const [rotationStats, setRotationStats] = useState(null);
  const [staffAssignmentsState, setStaffAssignmentsState] = useState(null);
  // Configurable settings: minimum and maximum group sizes and whether packs
  // should be gendered. These values can be adjusted via the UI.
  const [groupMin, setGroupMin] = useState(10);
  // Default updated to 10 to reduce under-min groups by default
  const [groupMax, setGroupMax] = useState(25);
  const [genderedPacks, setGenderedPacks] = useState(false);

  // Configurable One Eight rotation (0‑based index). Default value comes
  // from DEFAULT_ONE_EIGHT_ROTATION constant. The UI allows the user to
  // select which rotation (1–6) hosts the mandatory One Eight session for
  // Masu campers.
  const [oneEightRotation, setOneEightRotation] = useState(DEFAULT_ONE_EIGHT_ROTATION);

  // Manage unstaffed groups after scheduling
  const [unstaffedGroups, setUnstaffedGroups] = useState([]);
  const [showUnstaffedModal, setShowUnstaffedModal] = useState(false);

  // Persisted staff teams are shared across locations and selectable per run.
  const [staffTeams, setStaffTeams] = useState([]);
  const [activeStaffTeamIds, setActiveStaffTeamIds] = useState([]);
  const [showStaffListModal, setShowStaffListModal] = useState(false);
  const [editingStaffTeamId, setEditingStaffTeamId] = useState(null);
  const [editingStaffTeamName, setEditingStaffTeamName] = useState('');
  const [editedStaffList, setEditedStaffList] = useState([]);
  const [selectedUnstaffedStaffers, setSelectedUnstaffedStaffers] = useState({});
  // Modal to manage unplaced campers interactively
  const [showUnplacedModal, setShowUnplacedModal] = useState(false);
  const [locations, setLocations] = useState([]);
  const [activeLocationId, setActiveLocationId] = useState(null);
  const [locationDraft, setLocationDraft] = useState(DEFAULT_LOCATION_DRAFT);
  const [newLocationName, setNewLocationName] = useState('');
  const [isLoadingSettings, setIsLoadingSettings] = useState(true);
  const [isSavingLocation, setIsSavingLocation] = useState(false);
  const [isCreatingLocation, setIsCreatingLocation] = useState(false);
  const [locationSettingsError, setLocationSettingsError] = useState('');
  const [skillSettings, setSkillSettings] = useState([]);
  const [skillSettingsError, setSkillSettingsError] = useState('');
  const [requiredScheduleSkillName, setRequiredScheduleSkillName] = useState(null);
  const [isSavingSkills, setIsSavingSkills] = useState(false);
  const [pendingWorkbookImport, setPendingWorkbookImport] = useState(null);
  const [analysisScheduleRows, setAnalysisScheduleRows] = useState([]);
  const [analysisRegistrationRows, setAnalysisRegistrationRows] = useState([]);
  const [analysisRegistrationSource, setAnalysisRegistrationSource] = useState('');
  const [analysisScheduleSource, setAnalysisScheduleSource] = useState('');
  const [analysisReport, setAnalysisReport] = useState(null);
  const [analysisMessage, setAnalysisMessage] = useState('');
  const [pendingAnalysisWorkbook, setPendingAnalysisWorkbook] = useState(null);

  // View mode: 'summary' shows the schedule summary and unplaced campers,
  // 'detailed' shows the per‑rotation/group view with rosters.
  // 'schedule' shows a WIN-style Skill Schedule editor.
  const [viewMode, setViewMode] = useState('summary');
  // Selected rotation and skill for detailed view
  const [selectedRotationDetail, setSelectedRotationDetail] = useState(0);
  const [selectedSkillDetail, setSelectedSkillDetail] = useState(null);
  // Selected camper for detailed view (to show schedule and editing)
  const [selectedCamperDetail, setSelectedCamperDetail] = useState(null);
  const [selectedStafferDetail, setSelectedStafferDetail] = useState(null);

  const [maxRuntimeSeconds, setMaxRuntimeSeconds] = useState(120);
  const [isScheduling, setIsScheduling] = useState(false);
  const [scheduleProgress, setScheduleProgress] = useState(null);
  const [solverDiagnostics, setSolverDiagnostics] = useState(null);
  const [lastRunSummary, setLastRunSummary] = useState(null);
  const schedulerWorkerRef = useRef(null);
  const activeLocation = locations.find((location) => location.id === activeLocationId) || null;
  const activeTribeNames = getTribeNames(activeLocation);
  const configuredSkillsConfig = buildSkillsConfigFromSettings(skillSettings, activeTribeNames);

  const buildLocationDraft = (location) => ({
    id: location?.id ?? null,
    name: location?.name || '',
    defaultGroupMin: location?.defaultGroupMin ?? GROUP_MIN,
    defaultGroupMax: location?.defaultGroupMax ?? GROUP_MAX,
    coveredGradeMin: normalizeCoveredGradeRange(location).coveredGradeMin ?? deriveCoveredGradeRange(location?.tribes || createDefaultTribes()).coveredGradeMin,
    coveredGradeMax: normalizeCoveredGradeRange(location).coveredGradeMax ?? deriveCoveredGradeRange(location?.tribes || createDefaultTribes()).coveredGradeMax,
    tribes: normalizeTribes(location?.tribes || createDefaultTribes()),
  });

  const applyBootstrapState = (bootstrap) => {
    const bootstrapLocations = Array.isArray(bootstrap?.locations) ? bootstrap.locations : [];
    const nextActiveId = bootstrap?.activeLocationId ?? bootstrapLocations[0]?.id ?? null;
    const nextActiveLocation = bootstrapLocations.find((location) => location.id === nextActiveId) || bootstrapLocations[0] || null;
    const nextStaffTeams = Array.isArray(bootstrap?.staffTeams) ? bootstrap.staffTeams : [];
    setLocations(bootstrapLocations);
    setActiveLocationId(nextActiveId);
    setStaffTeams(nextStaffTeams);
    setActiveStaffTeamIds((prev) => {
      const validIds = new Set(nextStaffTeams.map((team) => Number(team.id)));
      const persistedIds = Array.isArray(bootstrap?.activeStaffTeamIds) ? bootstrap.activeStaffTeamIds.map(Number) : [];
      const baseSelection = persistedIds.length > 0 ? persistedIds : prev;
      const nextSelection = (Array.isArray(baseSelection) ? baseSelection : [])
        .map(Number)
        .filter((id) => validIds.has(id));
      if (nextSelection.length > 0) return Array.from(new Set(nextSelection));
      return nextStaffTeams[0] ? [nextStaffTeams[0].id] : [];
    });
    setLocationDraft(buildLocationDraft(nextActiveLocation || DEFAULT_LOCATION_DRAFT));
    const mergedSkills = mergeSkillRecords(
      (bootstrap?.skills || []).map((skill) => skill.name),
      bootstrap?.skills || [],
      nextActiveLocation?.tribes?.map((tribe) => tribe.name) || activeTribeNames
    );
    setSkillSettings(mergedSkills);
    const oneEightSkill = mergedSkills.find((skill) => skill.name === 'ONE EIGHT');
    if (oneEightSkill?.fixedRotations?.length) {
      setOneEightRotation(oneEightSkill.fixedRotations[0]);
    }
    if (nextActiveLocation) {
      setGroupMin(nextActiveLocation.defaultGroupMin ?? GROUP_MIN);
      setGroupMax(nextActiveLocation.defaultGroupMax ?? GROUP_MAX);
      const bootstrapCampers = Array.isArray(bootstrap?.savedCampers)
        ? bootstrap.savedCampers.map((camper) => normalizeLoadedCamper(camper, nextActiveLocation.tribes))
        : null;
      if (bootstrapCampers) {
        setCampers(mapCampersToLocation(bootstrapCampers, nextActiveLocation));
      } else {
        setCampers((prev) => mapCampersToLocation(prev, nextActiveLocation));
      }
    }
  };

  const currentScheduleAudit = assignments && groups
    ? buildExportValidationReport({
      campers,
      staffers,
      assignments: assignments || {},
      groups: groups || [],
      skillsConfigMap: configuredSkillsConfig,
      settings: {
        groupMin,
        groupMax,
        oneEightRotation,
        locationConfig: activeLocation ? { tribes: activeLocation.tribes || [] } : undefined,
      },
    })
    : null;
  const constraintAuditSummary = buildConstraintAuditSummary(currentScheduleAudit);
  const rotationSkillMatrix = groups
    ? buildRotationSkillMatrix(groups, { groupMin, groupMax }, configuredSkillsConfig)
    : null;
  const demandDiagnosticSummary = summarizeDemandDiagnostics(solverDiagnostics?.demandDiagnostics);
  const deepRepairSummary = summarizeDeepRepairDiagnostics(solverDiagnostics?.deepRepair);

  useEffect(() => {
    let cancelled = false;
    async function loadSettings() {
      setIsLoadingSettings(true);
      setLocationSettingsError('');
      try {
        const bootstrap = await fetchSettingsBootstrap();
        if (cancelled) return;
        applyBootstrapState(bootstrap);
      } catch (error) {
        if (cancelled) return;
        setLocationSettingsError(error.message || 'Failed to load location settings.');
      } finally {
        if (!cancelled) setIsLoadingSettings(false);
      }
    }
    loadSettings();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    skillsConfig = configuredSkillsConfig;
  }, [configuredSkillsConfig]);

  useEffect(() => {
    const allSkillNames = new Set();
    campers.forEach((camper) => {
      (camper.preferences || []).forEach((skill) => allSkillNames.add(skill));
    });
    const selectedStaffers = buildStaffersFromTeams(staffTeams, activeStaffTeamIds);
    selectedStaffers.forEach((staffer) => {
      (staffer.skills || []).forEach((skill) => allSkillNames.add(normalizeSkillName(skill)));
    });
    if (allSkillNames.size === 0) return;
    setSkillSettings((prev) => {
      const merged = mergeSkillRecords(Array.from(allSkillNames), prev, activeTribeNames);
      if (JSON.stringify(merged) === JSON.stringify(prev)) return prev;
      return merged;
    });
  }, [campers, staffTeams, activeStaffTeamIds, activeLocationId]);

  const buildSelectedStaffers = () => buildStaffersFromTeams(staffTeams, activeStaffTeamIds);

  const handleAnalysisScheduleUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      complete: (results) => {
        setAnalysisScheduleRows(results.data || []);
        setAnalysisScheduleSource(file.name);
        setAnalysisReport(null);
        setAnalysisMessage(`Loaded schedule file: ${file.name}`);
      },
      error: (err) => {
        setAnalysisMessage(`Error parsing schedule CSV: ${err.message}`);
      },
    });
  };

  const handleAnalysisRegistrationUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const lowerName = file.name.toLowerCase();
    if (lowerName.endsWith('.xlsx') || lowerName.endsWith('.xls')) {
      const reader = new FileReader();
      reader.onload = () => {
        try {
          const workbook = readWorkbookFromArrayBuffer(reader.result);
          const sheetNames = getWorkbookSheetNames(workbook);
          if (sheetNames.length === 0) {
            setAnalysisMessage('Registration workbook has no sheets.');
            return;
          }
          if (sheetNames.length === 1) {
            const rows = filterWorkbookRows(sheetToRowObjects(workbook, sheetNames[0]));
            setAnalysisRegistrationRows(rows);
            setAnalysisRegistrationSource(`${file.name} / ${sheetNames[0]}`);
            setAnalysisReport(null);
            setAnalysisMessage(`Loaded registration workbook: ${file.name}`);
            return;
          }
          setPendingAnalysisWorkbook({
            fileName: file.name,
            workbook,
            sheetNames,
            selectedSheet: sheetNames[0],
          });
          setAnalysisReport(null);
          setAnalysisMessage(`Select a sheet from ${file.name}.`);
        } catch (error) {
          setAnalysisMessage(`Error parsing registration workbook: ${error.message}`);
        }
      };
      reader.onerror = () => {
        setAnalysisMessage('Could not read registration workbook.');
      };
      reader.readAsArrayBuffer(file);
      return;
    }
    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      complete: (results) => {
        setAnalysisRegistrationRows(filterWorkbookRows(results.data || []));
        setAnalysisRegistrationSource(file.name);
        setAnalysisReport(null);
        setAnalysisMessage(`Loaded registration CSV: ${file.name}`);
      },
      error: (err) => {
        setAnalysisMessage(`Error parsing registration CSV: ${err.message}`);
      },
    });
  };

  const runAnalysisReport = () => {
    if (analysisScheduleRows.length === 0 || analysisRegistrationRows.length === 0) {
      setAnalysisMessage('Upload both the final schedule CSV and the registration file first.');
      return;
    }
    const report = analyzeScheduleQuality(analysisRegistrationRows, analysisScheduleRows, {
      skillConfig: configuredSkillsConfig,
    });
    setAnalysisReport(report);
    setAnalysisMessage('Analysis complete.');
  };

  const handleLocationSelect = async (locationIdValue) => {
    const nextId = Number(locationIdValue);
    const selectedLocation = locations.find((location) => location.id === nextId);
    if (!selectedLocation) return;
    setActiveLocationId(nextId);
    setLocationDraft(buildLocationDraft(selectedLocation));
    setGroupMin(selectedLocation.defaultGroupMin ?? GROUP_MIN);
    setGroupMax(selectedLocation.defaultGroupMax ?? GROUP_MAX);
    setCampers((prev) => mapCampersToLocation(prev, selectedLocation));
    try {
      await saveActiveLocationSelection(nextId);
      const bootstrap = await fetchSettingsBootstrap();
      applyBootstrapState(bootstrap);
      setLocationSettingsError('');
    } catch (error) {
      setLocationSettingsError(error.message || 'Failed to save active location.');
    }
  };

  const updateLocationDraftField = (field, value) => {
    setLocationDraft((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const updateLocationDraftTribe = (index, field, value) => {
    setLocationDraft((prev) => {
      const nextTribes = normalizeTribes(prev.tribes).map((tribe, tribeIndex) => {
        if (tribeIndex !== index) return tribe;
        return {
          ...tribe,
          [field]: field === 'name' ? value : Number.isFinite(Number(value)) ? Number(value) : 0,
        };
      });
      return { ...prev, tribes: nextTribes };
    });
  };

  const moveLocationDraftTribe = (index, direction) => {
    setLocationDraft((prev) => {
      const tribes = normalizeTribes(prev.tribes);
      const targetIndex = index + direction;
      if (targetIndex < 0 || targetIndex >= tribes.length) return prev;
      const nextTribes = tribes.slice();
      [nextTribes[index], nextTribes[targetIndex]] = [nextTribes[targetIndex], nextTribes[index]];
      return {
        ...prev,
        tribes: nextTribes.map((tribe, order) => ({ ...tribe, order })),
      };
    });
  };

  const addLocationDraftTribe = () => {
    setLocationDraft((prev) => ({
      ...prev,
      tribes: [
        ...normalizeTribes(prev.tribes),
        {
          id: null,
          name: '',
          order: prev.tribes.length,
          startGrade: 0,
          endGrade: 0,
        },
      ],
    }));
  };

  const removeLocationDraftTribe = (index) => {
    setLocationDraft((prev) => {
      if (prev.tribes.length <= 1) return prev;
      const nextTribes = normalizeTribes(prev.tribes)
        .filter((_, tribeIndex) => tribeIndex !== index)
        .map((tribe, order) => ({ ...tribe, order }));
      return { ...prev, tribes: nextTribes };
    });
  };

  const handleCreateLocation = async () => {
    const name = newLocationName.trim();
    if (!name) {
      setLocationSettingsError('Location name is required.');
      return;
    }
    setIsCreatingLocation(true);
    setLocationSettingsError('');
    try {
      await createLocationRecord({
        name,
        defaultGroupMin: GROUP_MIN,
        defaultGroupMax: GROUP_MAX,
        coveredGradeMin: 0,
        coveredGradeMax: 8,
        tribes: createDefaultTribes(),
      });
      const bootstrap = await fetchSettingsBootstrap();
      applyBootstrapState(bootstrap);
      setNewLocationName('');
    } catch (error) {
      const details = error.validationErrors?.length ? ` ${error.validationErrors.join(' ')}` : '';
      setLocationSettingsError(`${error.message || 'Failed to create location.'}${details}`);
    } finally {
      setIsCreatingLocation(false);
    }
  };

  const handleSaveLocationSettings = async () => {
    if (!locationDraft.id) return;
    const validation = validateLocationGradeCoverage(locationDraft);
    if (!validation.valid) {
      setLocationSettingsError(validation.errors.join(' '));
      return;
    }
    setIsSavingLocation(true);
    setLocationSettingsError('');
    try {
      await updateLocationRecord(locationDraft.id, {
        name: locationDraft.name.trim(),
        defaultGroupMin: locationDraft.defaultGroupMin,
        defaultGroupMax: locationDraft.defaultGroupMax,
        coveredGradeMin: validation.coveredGradeMin,
        coveredGradeMax: validation.coveredGradeMax,
        tribes: validateTribes(locationDraft.tribes).tribes,
      });
      const bootstrap = await fetchSettingsBootstrap();
      applyBootstrapState(bootstrap);
      setMessage('Location settings saved.');
    } catch (error) {
      const details = error.validationErrors?.length ? ` ${error.validationErrors.join(' ')}` : '';
      setLocationSettingsError(`${error.message || 'Failed to save location settings.'}${details}`);
    } finally {
      setIsSavingLocation(false);
    }
  };

  const handleCopyLocation = async () => {
    if (!activeLocationId || !activeLocation) return;
    const suggestedName = `${activeLocation.name} Copy`;
    const requestedName = window.prompt('Name for the copied location:', suggestedName);
    if (!requestedName || !requestedName.trim()) return;
    const copyCampers = window.confirm('Copy the saved campers from this location too?');
    setLocationSettingsError('');
    try {
      await copyLocationRecord(activeLocationId, {
        name: requestedName.trim(),
        copyCampers,
      });
      const bootstrap = await fetchSettingsBootstrap();
      applyBootstrapState(bootstrap);
      setMessage(`Copied location "${activeLocation.name}" to "${requestedName.trim()}".`);
    } catch (error) {
      const details = error.validationErrors?.length ? ` ${error.validationErrors.join(' ')}` : '';
      setLocationSettingsError(`${error.message || 'Failed to copy location.'}${details}`);
    }
  };

  const handleDeleteLocation = async () => {
    if (!activeLocationId || !activeLocation) return;
    const confirmed = window.confirm(`Delete location "${activeLocation.name}"? Staff teams will be kept.`);
    if (!confirmed) return;
    setLocationSettingsError('');
    try {
      await deleteLocationRecord(activeLocationId);
      const bootstrap = await fetchSettingsBootstrap();
      applyBootstrapState(bootstrap);
      setMessage(`Deleted location "${activeLocation.name}".`);
    } catch (error) {
      const details = error.validationErrors?.length ? ` ${error.validationErrors.join(' ')}` : '';
      setLocationSettingsError(`${error.message || 'Failed to delete location.'}${details}`);
    }
  };

  const updateSkillSetting = (skillName, updater) => {
    setSkillSettings((prev) => prev.map((skill) => {
      if (skill.name !== skillName) return skill;
      return normalizeSkillRecord(updater(skill), activeTribeNames);
    }));
  };

  const updateSkillRotationOverride = (skillName, rotationIndex, overrideUpdater) => {
    updateSkillSetting(skillName, (prev) => {
      const existingOverrides = Array.isArray(prev.rotationOverrides) ? prev.rotationOverrides.slice() : [];
      const existingIndex = existingOverrides.findIndex((entry) => Number(entry.rotationIndex) === rotationIndex);
      const current = existingIndex >= 0
        ? existingOverrides[existingIndex]
        : { rotationIndex, enabled: true, override: {} };
      const nextOverride = overrideUpdater(current);
      if (existingIndex >= 0) existingOverrides[existingIndex] = nextOverride;
      else existingOverrides.push(nextOverride);
      return {
        ...prev,
        rotationOverrides: existingOverrides.sort((a, b) => a.rotationIndex - b.rotationIndex),
      };
    });
  };

  const validateSkillSettings = (skills) => {
    const errors = [];
    (skills || []).forEach((skill) => {
      if (!skill?.name) {
        errors.push('Every skill must have a name.');
        return;
      }
      if (!Array.isArray(skill.allowedGenders) || skill.allowedGenders.length === 0) {
        errors.push(`${skill.name}: select at least one allowed gender.`);
      }
      if (!Array.isArray(skill.allowedTribes) || skill.allowedTribes.length === 0) {
        errors.push(`${skill.name}: select at least one allowed tribe.`);
      }
      if (skill.minGroupSize != null && skill.minGroupSize < 1) {
        errors.push(`${skill.name}: minimum group size must be at least 1.`);
      }
      if (skill.maxGroupSize != null && skill.maxGroupSize < 1) {
        errors.push(`${skill.name}: maximum group size must be at least 1.`);
      }
      if (skill.minGroupSize != null && skill.maxGroupSize != null && skill.minGroupSize > skill.maxGroupSize) {
        errors.push(`${skill.name}: minimum group size cannot exceed maximum group size.`);
      }
      const requiredSchedule = normalizeRequiredSchedule(skill.requiredSchedule, skill.fixedRotations);
      if (skill.requiredForEligible && requiredSchedule.mode === REQUIRED_SCHEDULE_MODE_PER_BLOCK) {
        if (requiredSchedule.blocks.length === 0) {
          errors.push(`${skill.name}: add at least one required rotation block.`);
        }
        const rotationUse = requiredSchedule.blocks.flat();
        if (new Set(rotationUse).size !== rotationUse.length) {
          errors.push(`${skill.name}: a rotation cannot appear in more than one required block.`);
        }
        if (requiredSchedule.blocks.some((block) => block.length !== requiredSchedule.blocks[0].length)) {
          errors.push(`${skill.name}: required rotation blocks must have the same number of positions.`);
        }
      }
      (skill.enableRotationOverrides ? (skill.rotationOverrides || []) : []).forEach((override) => {
        const min = override?.override?.minGroupSize;
        const max = override?.override?.maxGroupSize;
        if (min != null && min < 1) {
          errors.push(`${skill.name} rotation ${Number(override.rotationIndex) + 1}: minimum group size must be at least 1.`);
        }
        if (max != null && max < 1) {
          errors.push(`${skill.name} rotation ${Number(override.rotationIndex) + 1}: maximum group size must be at least 1.`);
        }
        if (min != null && max != null && min > max) {
          errors.push(`${skill.name} rotation ${Number(override.rotationIndex) + 1}: minimum group size cannot exceed maximum group size.`);
        }
      });
    });
    return errors;
  };

  const handleSaveSkillSettings = async () => {
    if (!activeLocationId) return;
    setIsSavingSkills(true);
    setSkillSettingsError('');
    try {
      const payload = skillSettings
        .filter((skill) => skill.name)
        .map((skill) => ({
          ...skill,
          rotationOverrides: skill.enableRotationOverrides ? skill.rotationOverrides || [] : [],
        }));
      const validationErrors = validateSkillSettings(payload);
      if (validationErrors.length > 0) {
        setSkillSettingsError(validationErrors.join(' '));
        return;
      }
      const saved = await saveLocationSkills(activeLocationId, payload);
      setSkillSettings(mergeSkillRecords(saved.map((skill) => skill.name), saved, activeTribeNames));
      const oneEightSkill = saved.find((skill) => skill.name === 'ONE EIGHT');
      if (oneEightSkill && Array.isArray(oneEightSkill.fixedRotations) && oneEightSkill.fixedRotations.length > 0) {
        setOneEightRotation(oneEightSkill.fixedRotations[0]);
      }
      setMessage('Skill settings saved.');
    } catch (error) {
      setSkillSettingsError(error.message || 'Failed to save skill settings.');
    } finally {
      setIsSavingSkills(false);
    }
  };

  // Handle file upload for campers. This reads the CSV file using
  // Papa.parse and converts each row into a camper object. Invalid rows are
  // skipped. After parsing, campers state is updated.
  const loadCamperRowsIntoState = async (rows, sourceLabel) => {
    const activeTribes = activeLocation?.tribes || createDefaultTribes();
    const importedAt = new Date().toISOString();
    const data = finalizeImportedCampers(rows, activeTribes, {
      sourceLabel,
      sourceType: 'csv',
      importedAt,
    });
    const localizedCampers = activeLocation ? mapCampersToLocation(data, activeLocation) : data;
    const outsideCoverage = findCampersOutsideLocationCoverage(localizedCampers, activeLocation);
    if (outsideCoverage.length > 0) {
      const sample = outsideCoverage
        .slice(0, 3)
        .map((camper) => `${camper.name} (${camper.grade === 0 ? 'K' : camper.grade})`)
        .join(', ');
      setMessage(`Import blocked. ${outsideCoverage.length} campers fall outside the covered grades or tribe coverage for ${activeLocation?.name || 'the active location'}: ${sample}${outsideCoverage.length > 3 ? ', ...' : ''}`);
      return;
    }
    setCampers(localizedCampers);
    if (activeLocationId) {
      try {
        await saveLocationCampers(activeLocationId, localizedCampers);
        const bootstrap = await fetchSettingsBootstrap();
        applyBootstrapState(bootstrap);
      } catch (error) {
        setMessage(`Loaded ${localizedCampers.length} campers from ${sourceLabel}, but could not save them: ${error.message}`);
        return;
      }
    }
    setMessage(`Loaded ${localizedCampers.length} campers from ${sourceLabel}.`);
  };

  const handleCampersUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const lowerName = file.name.toLowerCase();
    if (lowerName.endsWith('.xlsx') || lowerName.endsWith('.xls')) {
      const reader = new FileReader();
      reader.onload = () => {
        try {
          const workbook = readWorkbookFromArrayBuffer(reader.result);
          const sheetNames = getWorkbookSheetNames(workbook);
          if (sheetNames.length === 0) {
            setMessage('Workbook has no sheets to import.');
            return;
          }
          if (sheetNames.length === 1) {
            const rows = filterWorkbookRows(sheetToRowObjects(workbook, sheetNames[0]));
            loadCamperRowsIntoState(rows, `${file.name} / ${sheetNames[0]}`);
            return;
          }
          setPendingWorkbookImport({
            fileName: file.name,
            workbook,
            sheetNames,
            selectedSheet: sheetNames[0],
          });
        } catch (error) {
          setMessage(`Error parsing workbook: ${error.message}`);
        }
      };
      reader.onerror = () => {
        setMessage('Could not read workbook file.');
      };
      reader.readAsArrayBuffer(file);
      return;
    }
    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      complete: (results) => {
        loadCamperRowsIntoState(filterWorkbookRows(results.data), file.name);
      },
      error: (err) => {
        setMessage(`Error parsing campers CSV: ${err.message}`);
      },
    });
  };

  const applySchedulingResult = (
    pipelineResult,
    cleanedCampers,
    cancelled = false,
    optimizationStopped = false
  ) => {
    const rawGroups = pipelineResult.groups || Array.from({ length: 6 }, () => ({}));
    const resultAssignments = pipelineResult.assignments || {};
    const resultUnplaced = pipelineResult.unplaced || [];
    const rawStaff = pipelineResult.staffAssignments || {};
    const resultPrefHits = pipelineResult.preferenceHits || null;
    const resultDiagnostics = pipelineResult.diagnostics || null;
    const normalized = normalizeStaffingForRenderedSchedule(rawGroups, rawStaff);
    const resultGroups = normalized.groups;
    const resultStaff = normalized.staffAssignments;

    const pStats = computePackStats(cleanedCampers, groupMax, genderedPacks, activeTribeNames);
    const rStats = computeRotationStats(resultGroups, groupMin, groupMax);
    setPackStats(pStats);
    setRotationStats(rStats);
    setGroups(resultGroups);
    setAssignments(resultAssignments);
    setPreferenceHits(resultPrefHits);
    setUnplaced(resultUnplaced);
    setStaffAssignmentsState(resultStaff);
    setSolverDiagnostics(resultDiagnostics);
    setLastRunSummary({
      cancelled,
      unplacedCount: resultUnplaced.length,
      preferenceHits: normalizePreferenceHits(resultPrefHits),
    });

    const unstaffed = [];
    for (let r = 0; r < resultGroups.length; r++) {
      const rotationGroups = resultGroups[r] || {};
      Object.keys(rotationGroups).forEach((gKey) => {
        const grp = rotationGroups[gKey];
        if (!isGroupEnabled(grp)) return;
        if ((grp.campers || []).length === 0) return;
        if (!grp.staff) {
          unstaffed.push({ rotation: r, groupKey: gKey });
        }
      });
    }
    setUnstaffedGroups(unstaffed);
    setShowUnstaffedModal(unstaffed.length > 0);
    setShowUnplacedModal(resultUnplaced.length > 0);
    setSelectedRotationDetail(0);
    setSelectedSkillDetail(null);
    setSelectedCamperDetail(null);

    const messagePrefix = cancelled
      ? 'Deep repair stopped. Loaded the latest validated repair checkpoint.'
      : optimizationStopped
        ? 'Optimization stopped; deep repair completed.'
        : 'Scheduling complete.';
    const finalSwapAttempts = resultDiagnostics?.stageCounters?.finalSwapAttempts || 0;
    const finalSwapResolved = resultDiagnostics?.stageCounters?.finalSwapResolved || 0;
    const finalSwapMessage = finalSwapAttempts > 0
      ? ` Final swap phase: ${finalSwapResolved} resolved (${finalSwapAttempts} attempts).`
      : '';
    setMessage(`${messagePrefix} ${resultUnplaced.length} campers need manual placement.${finalSwapMessage}`);
  };

  const cleanupSchedulerWorker = () => {
    if (schedulerWorkerRef.current) {
      schedulerWorkerRef.current.stopOptimizationRequested = true;
      schedulerWorkerRef.current.stopDeepRepairRequested = true;
      if (schedulerWorkerRef.current.worker) {
        try {
          schedulerWorkerRef.current.worker.terminate();
        } catch (e) {
          // Ignore worker teardown errors.
        }
      }
      schedulerWorkerRef.current = null;
    }
  };

  const cancelScheduleRun = () => {
    if (!isScheduling) return;
    const runToken = schedulerWorkerRef.current;
    if (runToken) {
      if (runToken.phase === 'deep-repair') {
        runToken.stopDeepRepairRequested = true;
        if (runToken.worker) {
          try {
            runToken.worker.terminate();
          } catch (e) {
            // Ignore worker teardown errors.
          }
          runToken.worker = null;
        }
        if (runToken.phaseTwoCheckpoint && runToken.finishWithCheckpoint) {
          runToken.finishWithCheckpoint(buildStoppedDeepRepairCheckpointResult(
            runToken.phaseTwoCheckpoint
          ));
        } else {
          setMessage('Deep repair stop requested. Waiting for the latest validated checkpoint...');
        }
        return;
      }
      runToken.stopOptimizationRequested = true;
      if (runToken.worker) {
        try {
          runToken.worker.postMessage({ type: 'stop-optimization' });
        } catch (e) {
          // Ignore messaging errors during cancellation.
        }
      }
      setMessage('Optimization stop requested. Deep repair will start from the best schedule found so far...');
    }
  };

  // Perform the scheduling when the user clicks the process button. This
  // computes assignments, updates state, and resets any previous manual
  // overrides.
  const processSchedule = () => {
    if (isScheduling) return;
    if (campers.length === 0) {
      setMessage('Please upload campers CSV before scheduling.');
      return;
    }
    // Build staffers list from selected persisted staff teams.
    const selectedStaffers = buildSelectedStaffers();
    // Update staffers state
    setStaffers(selectedStaffers);
    const currentStaffers = selectedStaffers;
    if (currentStaffers.length === 0) {
      setMessage('Please select at least one staff team before scheduling.');
      return;
    }
    const parsedRuntime = Number(maxRuntimeSeconds);
    const maxRuntimeMs = Number.isFinite(parsedRuntime) && parsedRuntime > 0
      ? Math.floor(parsedRuntime * 1000)
      : 120000;
    setLastRunSummary(null);

    const prepared = prepareSchedulingInputs(campers, currentStaffers, {
      tribeNames: activeTribeNames,
      skillSettings,
    });
    const workerInput = {
      campers: prepared.cleanedCampers,
      staffers: currentStaffers,
      skillsConfig: prepared.dynamicConfig,
      skillSettings: prepared.mergedSkillSettings,
      settings: {
        groupMin,
        groupMax,
        oneEightRotation,
        dynamicMinDelta: 7,
        locationConfig: activeLocation,
      },
    };
    const requestCoverageWarnings = findCampersWithInsufficientUniquePreferences(prepared.cleanedCampers);
    setIsScheduling(true);
    setScheduleProgress({
      phase: 'optimization',
      iteration: 0,
      elapsedMs: 0,
      bestUnplaced: null,
      bestScore: null,
      requestCoverageWarnings,
    });
    setSolverDiagnostics(null);
    setMessage(requestCoverageWarnings.length > 0
      ? `Optimizer started. ${requestCoverageWarnings.length} camper(s) have fewer than six unique eligible requested skills and may be impossible to complete automatically.`
      : 'Optimizer started...');
    cleanupSchedulerWorker();
    const runToken = {
      phase: 'optimization',
      stopOptimizationRequested: false,
      stopDeepRepairRequested: false,
      phaseTwoCheckpoint: null,
      finishWithCheckpoint: null,
    };
    schedulerWorkerRef.current = runToken;
    const handleResultPayload = (payload) => {
      if (schedulerWorkerRef.current !== runToken) return;
      cleanupSchedulerWorker();
      setIsScheduling(false);
      setScheduleProgress(null);
      const solution = (payload && payload.solution) || {};
      const pipelineResult = {
        cleanedCampers: prepared.cleanedCampers,
        assignments: solution.assignments || {},
        groups: solution.groups || Array.from({ length: 6 }, () => ({})),
        unplaced: solution.unplaced || [],
        preferenceHits: solution.preferenceHits || null,
        staffAssignments: solution.staffAssignments || {},
        diagnostics: (payload && payload.diagnostics) || null,
        stageMetrics: [],
        skillsConfigSnapshot: prepared.dynamicConfig,
      };
      applySchedulingResult(
        pipelineResult,
        prepared.cleanedCampers,
        !!(payload && payload.cancelled),
        !!(payload && payload.optimizationStopped)
      );
    };
    runToken.finishWithCheckpoint = handleResultPayload;

    const handleProgressPayload = (payload) => {
      if (schedulerWorkerRef.current !== runToken) return;
      const previousPhase = runToken.phase;
      runToken.phase = payload.phase || 'optimization';
      if (payload.checkpointResult) runToken.phaseTwoCheckpoint = payload.checkpointResult;
      setScheduleProgress({
        phase: payload.phase || 'optimization',
        iteration: payload.iteration || 0,
        elapsedMs: payload.elapsedMs || 0,
        bestUnplaced: payload.bestUnplaced ?? null,
        bestScore: payload.bestScore || null,
        holesAtStart: payload.holesAtStart,
        holesRemaining: payload.holesRemaining,
        currentDepth: payload.currentDepth,
        pathsAccepted: payload.pathsAccepted,
        nodesExpanded: payload.nodesExpanded,
        requestCoverageWarnings: payload.requestCoverageWarnings || requestCoverageWarnings,
      });
      if (runToken.phase === 'deep-repair' && previousPhase !== 'deep-repair' && runToken.worker) {
        setTimeout(() => {
          if (schedulerWorkerRef.current !== runToken
            || runToken.phase !== 'deep-repair'
            || runToken.stopDeepRepairRequested
            || !runToken.worker) return;
          try {
            runToken.worker.postMessage({ type: 'begin-deep-repair' });
          } catch (e) {
            // Ignore messaging errors; worker failure handling remains active.
          }
        }, 500);
      }
    };

    const runAsyncFallback = () => {
      runWorkerSolveJob(
        {
          input: workerInput,
          options: {
            maxRuntimeMs,
            startsPerSlice: 1,
            sliceRuntimeMs: 320,
          },
        },
        (payload) => {
          if (schedulerWorkerRef.current !== runToken) return;
          if (payload.type === 'progress') {
            handleProgressPayload(payload);
          }
        },
        {
          shouldStopOptimization: () => runToken.stopOptimizationRequested,
          shouldStopDeepRepair: () => runToken.stopDeepRepairRequested,
          shouldStartDeepRepair: () => true,
        }
      )
        .then(handleResultPayload)
        .catch((err) => {
          cleanupSchedulerWorker();
          setIsScheduling(false);
          setScheduleProgress(null);
          setMessage(`Optimizer run failed: ${err.message}`);
        });
    };

    if (typeof Worker !== 'undefined') {
      import('./workerFactory')
        .then((mod) => {
          if (schedulerWorkerRef.current !== runToken) return;
          const worker = mod.createSchedulerWorker();
          runToken.worker = worker;
          worker.onmessage = (event) => {
            if (schedulerWorkerRef.current !== runToken) return;
            const payload = event.data || {};
            if (payload.type === 'progress') {
              handleProgressPayload(payload);
              return;
            }
            if (payload.type === 'error') {
              worker.terminate();
              runToken.worker = null;
              runAsyncFallback();
              return;
            }
            if (payload.type === 'result') {
              handleResultPayload(payload);
            }
          };
          worker.onerror = () => {
            try {
              worker.terminate();
            } catch (e) {
              // Ignore worker teardown errors.
            }
            runToken.worker = null;
            runAsyncFallback();
          };
          worker.postMessage({
            type: 'start',
            payload: {
              input: workerInput,
              options: {
                maxRuntimeMs,
                startsPerSlice: 1,
                sliceRuntimeMs: 320,
              },
            },
          });
        })
        .catch(() => {
          runAsyncFallback();
        });
    } else {
      runAsyncFallback();
    }
  };

  const handleTryAgain = () => {
    setShowUnplacedModal(false);
    processSchedule();
  };

  // Provide available rotation options (indices) for a camper based on their
  // current assignments.
  const availableRotationsForCamper = (camper, assignmentsOverride = assignments) => {
    const assigned = assignmentsOverride ? assignmentsOverride[getCamperAssignmentKey(camper)] : {};
    const rotations = [];
    for (let r = 0; r < 6; r++) {
      // Skip rotations already assigned for this camper
      if (assigned && assigned[r] !== undefined) continue;
      rotations.push(r);
    }
    return rotations;
  };

  const getAssignedSkillsForCamper = (
    camper,
    assignmentsOverride = assignments,
    groupsOverride = groups
  ) => {
    if (!assignmentsOverride || !groupsOverride || !camper) return [];
    const camAssignments = assignmentsOverride[getCamperAssignmentKey(camper)] || {};
    return Object.keys(camAssignments)
      .map((rIdx) => Number(rIdx))
      .sort((a, b) => a - b)
      .map((rotationIndex) => {
        const gKey = camAssignments[rotationIndex];
        const group = groupsOverride[rotationIndex] && groupsOverride[rotationIndex][gKey];
        return group && group.skill ? group.skill : null;
      })
      .filter((skill) => !!skill);
  };

  const getRequestedNotYetAssignedSkills = (camper) => {
    const assignedSet = new Set(getAssignedSkillsForCamper(camper));
    return (camper.preferences || []).filter((skill) => !assignedSet.has(skill));
  };

  // Provide a list of skills that a camper can be manually assigned to for a
  // given rotation. This is a manual override list and intentionally returns
  // every enabled group in the selected rotation except skills already
  // assigned to that camper in other rotations.
  const availableSkillsForCamperAndRotation = (
    camper,
    rotationIndex,
    assignmentsOverride = assignments,
    groupsOverride = groups
  ) => {
    if (!groupsOverride || rotationIndex === null || rotationIndex === undefined) return [];
    const rotationGroups = groupsOverride[rotationIndex] || {};
    return Object.keys(rotationGroups).filter((groupKey) => {
      const candidate = buildManualPlacementCandidate({
        camper,
        rotationIndex,
        groupKey,
        assignmentsOverride,
        groupsOverride,
        defaultGroupMin: groupMin,
        defaultGroupMax: groupMax,
        activeLocation,
        skillConfig: skillsConfig,
      });
      return !!candidate;
    });
  };

  const evaluateManualPlacementCandidate = (
    camper,
    rotationIndex,
    groupKey,
    assignmentsOverride = assignments,
    groupsOverride = groups
  ) => buildManualPlacementCandidate({
    camper,
    rotationIndex,
    groupKey,
    assignmentsOverride,
    groupsOverride,
    defaultGroupMax: groupMax,
    defaultGroupMin: groupMin,
    activeLocation,
    skillConfig: skillsConfig,
  });

  const compareManualCandidates = (a, b) => {
    // Requested legal options remain the primary manual-repair choices. An
    // unrequested skill may still be shown for an explicit operator decision,
    // but it must never outrank or be labelled as the recommendation.
    if (a.notRequestedBreak !== b.notRequestedBreak) {
      return a.notRequestedBreak ? 1 : -1;
    }
    // Within the requested/unrequested tier, prefer fewer rule breaks.
    if (a.breakCount !== b.breakCount) {
      return a.breakCount - b.breakCount;
    }
    // Tiebreak when break counts are equal: avoid harder-to-break rules first.
    if (a.tribeOverrideBreak !== b.tribeOverrideBreak) {
      return a.tribeOverrideBreak ? 1 : -1;
    }
    if (a.groupMaxBreak !== b.groupMaxBreak) {
      return a.groupMaxBreak ? 1 : -1;
    }
    if (a.underMinBreak !== b.underMinBreak) {
      return a.underMinBreak ? 1 : -1;
    }
    if (a.overflow !== b.overflow) {
      return a.overflow - b.overflow;
    }
    if (a.balanceDelta !== b.balanceDelta) {
      return a.balanceDelta - b.balanceDelta;
    }
    if (a.projectedSize !== b.projectedSize) {
      return a.projectedSize - b.projectedSize;
    }
    // Preference is only a light tie-break signal (never counted as a break).
    const rankA = a.preferenceRank || 99;
    const rankB = b.preferenceRank || 99;
    if (rankA !== rankB) {
      return rankA - rankB;
    }
    if (a.rotationIndex !== b.rotationIndex) {
      return a.rotationIndex - b.rotationIndex;
    }
    return a.skill.localeCompare(b.skill);
  };

  const getRankedManualCandidatesForCamper = (
    camper,
    rotationOverride = null,
    assignmentsOverride = assignments,
    groupsOverride = groups
  ) => {
    const rotations = rotationOverride === null
      ? availableRotationsForCamper(camper, assignmentsOverride)
      : [rotationOverride];
    const candidates = [];
    rotations.forEach((rotationIndex) => {
      const optionKeys = availableSkillsForCamperAndRotation(
        camper,
        rotationIndex,
        assignmentsOverride,
        groupsOverride
      );
      optionKeys.forEach((groupKey) => {
        const candidate = evaluateManualPlacementCandidate(
          camper,
          rotationIndex,
          groupKey,
          assignmentsOverride,
          groupsOverride
        );
        if (candidate) candidates.push(candidate);
      });
    });
    candidates.sort(compareManualCandidates);
    return candidates;
  };

  // Handle manual assignment of a camper to a rotation and group. This
  // updates the assignments and group structures accordingly. After manual
  // assignment, the camper is removed from the unplaced list if fully
  // assigned.
  const cloneGroupsForUiState = (sourceGroups) => sourceGroups.map((rot) => {
    const copy = {};
    Object.keys(rot).forEach((key) => {
      copy[key] = {
        ...rot[key],
        campers: [...(rot[key].campers || [])],
        tribes: { ...(rot[key].tribes || {}) },
      };
    });
    return copy;
  });

  const handleManualAssign = (camper, rotationIndex, groupKey) => {
    if (!assignments || !groups || !groupKey) return;
    const candidate = evaluateManualPlacementCandidate(camper, rotationIndex, groupKey, assignments, groups);
    if (!candidate) return;
    const newAssignments = { ...assignments };
    const newGroups = cloneGroupsForUiState(groups);
    const applied = applyCamperPlacementToState(
      camper,
      rotationIndex,
      groupKey,
      newAssignments,
      newGroups
    );
    if (!applied) return;
    const camperAssign = newAssignments[getCamperAssignmentKey(camper)] || {};
    const remaining = updateUnplacedListAfterRemoval(unplaced, camper, camperAssign);
    setAssignments(newAssignments);
    setGroups(newGroups);
    setUnplaced(remaining);
    const newStaffAssignments = assignStaffersToGroups(newGroups, staffers);
    setStaffAssignmentsState(newStaffAssignments);
  };

  const handleAutoSelectRecommended = () => {
    if (!assignments || !groups || unplaced.length === 0) return;
    const nextAssignments = { ...assignments };
    const nextGroups = cloneGroupsForUiState(groups);
    let placementsMade = 0;

    unplaced.forEach((camper) => {
      const rankedCandidates = getRankedManualCandidatesForCamper(
        camper,
        null,
        nextAssignments,
        nextGroups
      );
      const bestCandidate = selectRecommendedManualCandidate(rankedCandidates);
      if (!bestCandidate) return;
      const applied = applyCamperPlacementToState(
        camper,
        bestCandidate.rotationIndex,
        bestCandidate.groupKey,
        nextAssignments,
        nextGroups
      );
      if (applied) placementsMade += 1;
    });

    const nextUnplaced = unplaced.filter((camper) => {
      const camAssign = nextAssignments[getCamperAssignmentKey(camper)] || {};
      return Object.keys(camAssign).length < 6;
    });
    const nextStaffAssignments = assignStaffersToGroups(nextGroups, staffers);
    setAssignments(nextAssignments);
    setGroups(nextGroups);
    setUnplaced(nextUnplaced);
    setStaffAssignmentsState(nextStaffAssignments);
    setMessage(`Auto-selected recommended options for ${placementsMade} campers.`);
  };

  // Export the camper assignments as a CSV file. Uses the browser's file
  // saving capability via file-saver.
  const handleDownloadSolverDiagnostics = () => {
    if (!solverDiagnostics) return;
    const blob = new Blob([serializeSolverDiagnostics(solverDiagnostics)], {
      type: 'application/json;charset=utf-8;',
    });
    saveAs(blob, 'scheduler_run_diagnostics.json');
    setMessage('Run diagnostics downloaded.');
  };

  const handleExportCSV = () => {
    if (!assignments || !groups) return;
    const audit = currentScheduleAudit || buildExportValidationReport({
      campers,
      staffers,
      assignments: assignments || {},
      groups: groups || [],
      skillsConfigMap: configuredSkillsConfig,
      settings: {
        groupMin,
        groupMax,
        oneEightRotation,
        locationConfig: activeLocation ? { tribes: activeLocation.tribes || [] } : undefined,
      },
    });
    if (!audit.canExport) {
      setMessage(`Export blocked. ${audit.blockingIssues.map((issue) => issue.message).join(' ')}`);
      return;
    }
    const csvString = assignmentsToCSV(campers, assignments, groups);
    const blob = new Blob([csvString], { type: 'text/csv;charset=utf-8;' });
    const filename = buildScheduleExportFilename(
      activeLocation?.name,
      new Date().getFullYear(),
      'Camper Schedule',
      'csv'
    );
    saveAs(blob, filename);
    const exceptionSummary = audit.manualExceptions.length > 0
      ? ` ${audit.manualExceptions.map((item) => item.message).join(' ')}`
      : '';
    setMessage(`Camper schedule exported.${exceptionSummary}`);
  };

  const handleExportStaffCSV = () => {
    if (!staffAssignmentsState || !groups) return;
    const audit = currentScheduleAudit || buildExportValidationReport({
      campers,
      staffers,
      assignments: assignments || {},
      groups: groups || [],
      skillsConfigMap: configuredSkillsConfig,
      settings: {
        groupMin,
        groupMax,
        oneEightRotation,
        locationConfig: activeLocation ? { tribes: activeLocation.tribes || [] } : undefined,
      },
    });
    if (!audit.canExport) {
      setMessage(`Export blocked. ${audit.blockingIssues.map((issue) => issue.message).join(' ')}`);
      return;
    }
    const csvString = staffAssignmentsToCSV(staffAssignmentsState, groups);
    const blob = new Blob([csvString], { type: 'text/csv;charset=utf-8;' });
    saveAs(blob, 'staff_schedule.csv');
    const exceptionSummary = audit.manualExceptions.length > 0
      ? ` ${audit.manualExceptions.map((item) => item.message).join(' ')}`
      : '';
    setMessage(`Staff schedule exported.${exceptionSummary}`);
  };

  const handleExportSkillWorkbook = () => {
    if (!assignments || !groups) return;
    const audit = currentScheduleAudit || buildExportValidationReport({
      campers,
      staffers,
      assignments: assignments || {},
      groups: groups || [],
      skillsConfigMap: configuredSkillsConfig,
      settings: {
        groupMin,
        groupMax,
        oneEightRotation,
        locationConfig: activeLocation ? { tribes: activeLocation.tribes || [] } : undefined,
      },
    });
    if (!audit.canExport) {
      setMessage(`Export blocked. ${audit.blockingIssues.map((issue) => issue.message).join(' ')}`);
      return;
    }
    const workbook = buildSkillRosterWorkbook(campers, assignments, groups);
    const arrayBuffer = writeWorkbookArrayBuffer(workbook);
    const blob = new Blob([arrayBuffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });
    const filename = buildScheduleExportFilename(
      activeLocation?.name,
      new Date().getFullYear(),
      'Skill Rosters',
      'xlsx'
    );
    saveAs(blob, filename);
    const exceptionSummary = audit.manualExceptions.length > 0
      ? ` ${audit.manualExceptions.map((item) => item.message).join(' ')}`
      : '';
    setMessage(`Skill roster workbook exported.${exceptionSummary}`);
  };

  // Remove a camper's assignment for a specific rotation. Updates assignments and groups.
  const removeAssignmentForCamper = (camper, rotationIndex) => {
    if (!assignments || !groups) return;
    const currentAssignments = { ...assignments };
    const newGroups = cloneGroupsForUiState(groups);
    const previous = (currentAssignments[getCamperAssignmentKey(camper)] || {})[rotationIndex];
    if (previous === undefined) return;
    applyCamperPlacementToState(camper, rotationIndex, null, currentAssignments, newGroups);
    const camAssign = currentAssignments[getCamperAssignmentKey(camper)] || {};
    setAssignments(currentAssignments);
    setGroups(newGroups);
    const newStaff = assignStaffersToGroups(newGroups, staffers);
    setStaffAssignmentsState(newStaff);
    setUnplaced((prev) => updateUnplacedListAfterRemoval(prev, camper, camAssign));
  };

  // Move a camper to a new group in a rotation. Removes existing assignment for that rotation and
  // assigns to the new group in one transactional state update.
  const moveCamperToGroup = (camper, rotationIndex, newGroupKey) => {
    if (!assignments || !groups) return;
    if (newGroupKey) {
      const candidate = evaluateManualPlacementCandidate(camper, rotationIndex, newGroupKey, assignments, groups);
      if (!candidate) return;
    }
    const nextAssignments = { ...assignments };
    const nextGroups = cloneGroupsForUiState(groups);
    applyCamperPlacementToState(
      camper,
      rotationIndex,
      newGroupKey || null,
      nextAssignments,
      nextGroups
    );
    const camAssign = nextAssignments[getCamperAssignmentKey(camper)] || {};
    setAssignments(nextAssignments);
    setGroups(nextGroups);
    setUnplaced((prev) => updateUnplacedListAfterRemoval(prev, camper, camAssign));
    const nextStaff = assignStaffersToGroups(nextGroups, staffers);
    setStaffAssignmentsState(nextStaff);
  };

  const closeStaffTeamModal = () => {
    setShowStaffListModal(false);
    setEditingStaffTeamId(null);
    setEditingStaffTeamName('');
    setEditedStaffList([]);
  };

  const openStaffTeamEditor = (teamId = null) => {
    const team = staffTeams.find((entry) => entry.id === teamId) || null;
    setEditingStaffTeamId(team?.id ?? null);
    setEditingStaffTeamName(team?.name || '');
    setEditedStaffList((team?.staff || []).map((staffer) => ({
      name: staffer.name,
      skills: [...(staffer.skills || [])],
    })));
    setShowStaffListModal(true);
  };

  const saveEditedStaffTeam = async () => {
    const payload = {
      name: editingStaffTeamName.trim(),
      staff: editedStaffList
        .map((st) => ({
          name: st.name ? st.name.trim() : '',
          skills: (st.skills || []).map((skill) => (skill ? skill.trim().toUpperCase() : '')).filter(Boolean),
        }))
        .filter((st) => st.name),
    };
    if (!payload.name) {
      setMessage('Staff team name is required.');
      return;
    }
    try {
      if (editingStaffTeamId == null) {
        await createStaffTeamRecord(payload);
      } else {
        await updateStaffTeamRecord(editingStaffTeamId, payload);
      }
      const bootstrap = await fetchSettingsBootstrap();
      applyBootstrapState(bootstrap);
      setMessage(`Staff team ${editingStaffTeamId == null ? 'created' : 'saved'}.`);
      closeStaffTeamModal();
    } catch (error) {
      const details = error.validationErrors?.length ? ` ${error.validationErrors.join(' ')}` : '';
      setMessage(`${error.message || 'Failed to save staff team.'}${details}`);
    }
  };

  const handleDeleteStaffTeam = async (teamId) => {
    try {
      await deleteStaffTeamRecord(teamId);
      const bootstrap = await fetchSettingsBootstrap();
      applyBootstrapState(bootstrap);
      setMessage('Staff team deleted.');
    } catch (error) {
      setMessage(error.message || 'Failed to delete staff team.');
    }
  };

  const toggleStaffTeamSelection = async (teamId, checked) => {
    const nextTeamIds = checked
      ? Array.from(new Set([...activeStaffTeamIds, teamId]))
      : activeStaffTeamIds.filter((id) => id !== teamId);
    setActiveStaffTeamIds(nextTeamIds);
  };

  // Assign a staffer to an unstaffed group. Updates groups and staff assignments state.
  const handleAssignUnstaffedGroup = (rotation, groupKey, staffName) => {
    if (!staffAssignmentsState) return;
    const rotationGroups = groups ? groups[rotation] : null;
    const targetGroup = rotationGroups ? rotationGroups[groupKey] : null;
    if (!targetGroup) return;
    const stafferObj = staffers.find((st) => st.name === staffName);
    if (!stafferObj || !stafferCanTeachSkill(stafferObj, targetGroup.skill)) {
      setMessage('Selected staffer is not qualified for this skill.');
      return;
    }
    // Copy groups and staff assignments
    const newGroups = groups.map((rot) => {
      const copy = {};
      Object.keys(rot).forEach((key) => {
        copy[key] = { ...rot[key], campers: [...rot[key].campers], tribes: { ...rot[key].tribes }, staff: rot[key].staff };
      });
      return copy;
    });
    const newStaffAssignments = {};
    Object.keys(staffAssignmentsState).forEach((sn) => {
      newStaffAssignments[sn] = [...staffAssignmentsState[sn]];
    });
    // Assign staffer
    if (!newStaffAssignments[staffName]) {
      newStaffAssignments[staffName] = Array(6).fill(null);
    }
    newGroups[rotation][groupKey].staff = staffName;
    newStaffAssignments[staffName][rotation] = groupKey;
    // Update state
    setGroups(newGroups);
    setStaffAssignmentsState(newStaffAssignments);
    // Remove from unstaffed list
    setUnstaffedGroups((prev) => prev.filter((g) => !(g.rotation === rotation && g.groupKey === groupKey)));
    setSelectedUnstaffedStaffers((prev) => {
      const copy = { ...prev };
      delete copy[`${rotation}_${groupKey}`];
      return copy;
    });
    // Close modal if no more unstaffed groups
    if (unstaffedGroups.length <= 1) {
      setShowUnstaffedModal(false);
    }
  };

  // Remove a staff assignment for a specific rotation
  const removeStaffAssignment = (staffer, rotationIndex) => {
    const stName = staffer.name;
    if (!staffAssignmentsState || !groups) return;
    if (!staffAssignmentsState[stName] || staffAssignmentsState[stName][rotationIndex] == null) return;
    const gKey = staffAssignmentsState[stName][rotationIndex];
    // Copy groups and staff assignments
    const newGroups = groups.map((rot) => {
      const copy = {};
      Object.keys(rot).forEach((key) => {
        copy[key] = { ...rot[key], campers: [...rot[key].campers], tribes: { ...rot[key].tribes }, staff: rot[key].staff };
      });
      return copy;
    });
    const newStaffAssignments = {};
    Object.keys(staffAssignmentsState).forEach((sn) => {
      newStaffAssignments[sn] = [...staffAssignmentsState[sn]];
    });
    // Remove assignment
    newGroups[rotationIndex][gKey].staff = null;
    newStaffAssignments[stName][rotationIndex] = null;
    // Update state
    setGroups(newGroups);
    setStaffAssignmentsState(newStaffAssignments);
    // Add to unstaffedGroups and show modal
    setUnstaffedGroups((prev) => {
      const exists = prev.some((g) => g.rotation === rotationIndex && g.groupKey === gKey);
      if (!exists) {
        return [...prev, { rotation: rotationIndex, groupKey: gKey }];
      }
      return prev;
    });
    setShowUnstaffedModal(true);
  };

  // Move a staffer to a new group (rotation and group key)
  const moveStafferToNewGroup = (staffer, rotationIndex, newGroupKey) => {
    const stName = staffer.name;
    if (!staffAssignmentsState || !groups) return;
    // Remove existing assignment if any
    const currentKey = staffAssignmentsState[stName] ? staffAssignmentsState[stName][rotationIndex] : null;
    if (currentKey === newGroupKey) return;
    // Copy groups and staff assignments
    const newGroups = groups.map((rot) => {
      const copy = {};
      Object.keys(rot).forEach((key) => {
        copy[key] = { ...rot[key], campers: [...rot[key].campers], tribes: { ...rot[key].tribes }, staff: rot[key].staff };
      });
      return copy;
    });
    const newStaffAssignments = {};
    Object.keys(staffAssignmentsState).forEach((sn) => {
      newStaffAssignments[sn] = [...staffAssignmentsState[sn]];
    });
    // Remove current assignment if exists
    if (currentKey) {
      newGroups[rotationIndex][currentKey].staff = null;
      newStaffAssignments[stName][rotationIndex] = null;
      // Add old group to unstaffed list
      setUnstaffedGroups((prev) => {
        const exists = prev.some((g) => g.rotation === rotationIndex && g.groupKey === currentKey);
        if (!exists) {
          return [...prev, { rotation: rotationIndex, groupKey: currentKey }];
        }
        return prev;
      });
    }
    // Assign to new group
    newGroups[rotationIndex][newGroupKey].staff = stName;
    newStaffAssignments[stName][rotationIndex] = newGroupKey;
    setGroups(newGroups);
    setStaffAssignmentsState(newStaffAssignments);
    // Remove new group from unstaffed list if present
    setUnstaffedGroups((prev) => prev.filter((g) => !(g.rotation === rotationIndex && g.groupKey === newGroupKey)));
  };

  // Render the UI. The layout consists of an upload section on the left
  // and a results/management section on the right. The results section
  // displays unplaced campers for manual assignment and statistics when
  // available.
  return (
    <div className="max-w-7xl mx-auto p-4">
      <h1 className="text-3xl font-bold mb-6 text-center">Camp Rotation Scheduler</h1>
      <div className="flex flex-wrap gap-2 justify-center mb-6">
        <button
          className={`px-3 py-1 rounded text-sm ${viewMode === 'locations' ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-800'}`}
          onClick={() => setViewMode('locations')}
        >
          Location Settings
        </button>
        <button
          className={`px-3 py-1 rounded text-sm ${viewMode === 'summary' ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-800'}`}
          onClick={() => setViewMode('summary')}
        >
          Summary
        </button>
        <button
          className={`px-3 py-1 rounded text-sm ${viewMode === 'schedule' ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-800'}`}
          onClick={() => setViewMode('schedule')}
        >
          Skill Options
        </button>
        <button
          className={`px-3 py-1 rounded text-sm ${viewMode === 'analysis' ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-800'}`}
          onClick={() => setViewMode('analysis')}
        >
          Analysis
        </button>
        {assignments && groups && (
          <button
            className={`px-3 py-1 rounded text-sm ${viewMode === 'detailed' ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-800'}`}
            onClick={() => setViewMode('detailed')}
          >
            Detailed
          </button>
        )}
        {assignments && staffAssignmentsState && groups && (
          <button
            className={`px-3 py-1 rounded text-sm ${viewMode === 'staff' ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-800'}`}
            onClick={() => setViewMode('staff')}
          >
            Staff
          </button>
        )}
      </div>
      {viewMode === 'locations' && (
        <div className="bg-white shadow p-4 rounded">
          <div className="grid gap-6 lg:grid-cols-[320px,1fr]">
            <div className="border rounded p-4">
              <h2 className="text-xl font-semibold mb-3">Locations</h2>
              {isLoadingSettings ? (
                <p className="text-sm text-gray-600">Loading location settings...</p>
              ) : (
                <>
                  <label className="block text-sm font-medium mb-1" htmlFor="activeLocation">Active Location</label>
                  <select
                    id="activeLocation"
                    value={activeLocationId || ''}
                    className="w-full border border-gray-300 rounded px-3 py-2"
                    onChange={(e) => handleLocationSelect(e.target.value)}
                  >
                    {locations.map((location) => (
                      <option key={location.id} value={location.id}>
                        {location.name}
                      </option>
                    ))}
                  </select>
                  <div className="mt-4">
                    <label className="block text-sm font-medium mb-1" htmlFor="newLocationName">Create Location</label>
                    <div className="flex gap-2">
                      <input
                        id="newLocationName"
                        type="text"
                        value={newLocationName}
                        onChange={(e) => setNewLocationName(e.target.value)}
                        className="flex-1 border border-gray-300 rounded px-3 py-2"
                        placeholder="New location name"
                      />
                      <button
                        onClick={handleCreateLocation}
                        disabled={isCreatingLocation}
                        className={`px-3 py-2 rounded text-white ${isCreatingLocation ? 'bg-blue-300 cursor-not-allowed' : 'bg-blue-600 hover:bg-blue-700'}`}
                      >
                        Create
                      </button>
                    </div>
                  </div>
                  <div className="mt-4 grid grid-cols-2 gap-2">
                    <button
                      onClick={handleCopyLocation}
                      disabled={!activeLocationId}
                      className={`px-3 py-2 rounded text-sm text-white ${activeLocationId ? 'bg-gray-700 hover:bg-gray-800' : 'bg-gray-300 cursor-not-allowed'}`}
                    >
                      Copy Active
                    </button>
                    <button
                      onClick={handleDeleteLocation}
                      disabled={!activeLocationId}
                      className={`px-3 py-2 rounded text-sm text-white ${activeLocationId ? 'bg-red-600 hover:bg-red-700' : 'bg-red-300 cursor-not-allowed'}`}
                    >
                      Delete Active
                    </button>
                  </div>
                  <div className="mt-4 text-xs text-gray-600">
                    These settings persist in the local scheduler database and will drive tribe mapping and future skill rules.
                  </div>
                </>
              )}
            </div>

            <div className="border rounded p-4">
              <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
                <div>
                  <h2 className="text-xl font-semibold">Location Configuration</h2>
                  <p className="text-sm text-gray-600">
                    Define grade coverage, tribe ranges, and default group sizes for the active location.
                  </p>
                </div>
                <button
                  onClick={handleSaveLocationSettings}
                  disabled={isLoadingSettings || isSavingLocation || !locationDraft.id}
                  className={`px-4 py-2 rounded text-white ${isLoadingSettings || isSavingLocation || !locationDraft.id ? 'bg-green-300 cursor-not-allowed' : 'bg-green-600 hover:bg-green-700'}`}
                >
                  {isSavingLocation ? 'Saving...' : 'Save Location Settings'}
                </button>
              </div>

              {locationSettingsError && (
                <div className="mb-4 rounded border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-700">
                  {locationSettingsError}
                </div>
              )}

              {!locationDraft.id ? (
                <p className="text-sm text-gray-600">Create or select a location to edit its settings.</p>
              ) : (
                <>
                  <div className="grid gap-4 md:grid-cols-2">
                    <div>
                      <label className="block text-sm font-medium mb-1" htmlFor="locationName">Location Name</label>
                      <input
                        id="locationName"
                        type="text"
                        value={locationDraft.name}
                        onChange={(e) => updateLocationDraftField('name', e.target.value)}
                        className="w-full border border-gray-300 rounded px-3 py-2"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1" htmlFor="coveredGradeMin">Covered Grade Min</label>
                      <input
                        id="coveredGradeMin"
                        type="number"
                        min={0}
                        value={locationDraft.coveredGradeMin}
                        onChange={(e) => updateLocationDraftField('coveredGradeMin', parseInt(e.target.value, 10) || 0)}
                        className="w-full border border-gray-300 rounded px-3 py-2"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1" htmlFor="coveredGradeMax">Covered Grade Max</label>
                      <input
                        id="coveredGradeMax"
                        type="number"
                        min={0}
                        value={locationDraft.coveredGradeMax}
                        onChange={(e) => updateLocationDraftField('coveredGradeMax', parseInt(e.target.value, 10) || 0)}
                        className="w-full border border-gray-300 rounded px-3 py-2"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1" htmlFor="locationGroupMin">Default Group Min</label>
                      <input
                        id="locationGroupMin"
                        type="number"
                        min={1}
                        value={locationDraft.defaultGroupMin}
                        onChange={(e) => updateLocationDraftField('defaultGroupMin', parseInt(e.target.value, 10) || 0)}
                        className="w-full border border-gray-300 rounded px-3 py-2"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1" htmlFor="locationGroupMax">Default Group Max</label>
                      <input
                        id="locationGroupMax"
                        type="number"
                        min={1}
                        value={locationDraft.defaultGroupMax}
                        onChange={(e) => updateLocationDraftField('defaultGroupMax', parseInt(e.target.value, 10) || 0)}
                        className="w-full border border-gray-300 rounded px-3 py-2"
                      />
                    </div>
                  </div>

                  <div className="mt-6">
                    <div className="flex items-center justify-between mb-2">
                      <div>
                        <h3 className="font-medium">Tribes</h3>
                        <p className="text-sm text-gray-600">
                          Tribe order determines adjacency. Each grade may belong to only one tribe.
                        </p>
                      </div>
                      <button
                        onClick={addLocationDraftTribe}
                        className="bg-gray-200 hover:bg-gray-300 text-gray-800 px-3 py-1 rounded text-sm"
                      >
                        Add Tribe
                      </button>
                    </div>
                    <div className="overflow-auto">
                      <table className="min-w-full text-sm border">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="border px-2 py-2 text-left">Order</th>
                            <th className="border px-2 py-2 text-left">Name</th>
                            <th className="border px-2 py-2 text-left">Start Grade</th>
                            <th className="border px-2 py-2 text-left">End Grade</th>
                            <th className="border px-2 py-2 text-left">Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {normalizeTribes(locationDraft.tribes).map((tribe, index) => (
                            <tr key={`${tribe.id || 'new'}-${index}`}>
                              <td className="border px-2 py-2 whitespace-nowrap">#{index + 1}</td>
                              <td className="border px-2 py-2">
                                <input
                                  type="text"
                                  value={tribe.name}
                                  onChange={(e) => updateLocationDraftTribe(index, 'name', e.target.value)}
                                  className="w-full border border-gray-300 rounded px-2 py-1"
                                />
                              </td>
                              <td className="border px-2 py-2">
                                <input
                                  type="number"
                                  min={0}
                                  value={tribe.startGrade}
                                  onChange={(e) => updateLocationDraftTribe(index, 'startGrade', e.target.value)}
                                  className="w-full border border-gray-300 rounded px-2 py-1"
                                />
                              </td>
                              <td className="border px-2 py-2">
                                <input
                                  type="number"
                                  min={0}
                                  value={tribe.endGrade}
                                  onChange={(e) => updateLocationDraftTribe(index, 'endGrade', e.target.value)}
                                  className="w-full border border-gray-300 rounded px-2 py-1"
                                />
                              </td>
                              <td className="border px-2 py-2">
                                <div className="flex flex-wrap gap-2">
                                  <button
                                    onClick={() => moveLocationDraftTribe(index, -1)}
                                    disabled={index === 0}
                                    className={`px-2 py-1 rounded text-xs ${index === 0 ? 'bg-gray-100 text-gray-400' : 'bg-gray-200 hover:bg-gray-300 text-gray-800'}`}
                                  >
                                    Up
                                  </button>
                                  <button
                                    onClick={() => moveLocationDraftTribe(index, 1)}
                                    disabled={index === locationDraft.tribes.length - 1}
                                    className={`px-2 py-1 rounded text-xs ${index === locationDraft.tribes.length - 1 ? 'bg-gray-100 text-gray-400' : 'bg-gray-200 hover:bg-gray-300 text-gray-800'}`}
                                  >
                                    Down
                                  </button>
                                  <button
                                    onClick={() => removeLocationDraftTribe(index)}
                                    disabled={locationDraft.tribes.length <= 1}
                                    className={`px-2 py-1 rounded text-xs ${locationDraft.tribes.length <= 1 ? 'bg-red-100 text-red-300' : 'bg-red-100 hover:bg-red-200 text-red-700'}`}
                                  >
                                    Remove
                                  </button>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}
      {viewMode === 'analysis' && (
        <div className="space-y-6">
          <div className="bg-white shadow p-4 rounded">
            <div className="flex flex-wrap items-end gap-4">
              <div className="min-w-[260px] flex-1">
                <label className="block font-medium mb-1" htmlFor="analysisScheduleFile">Final Schedule CSV</label>
                <input
                  id="analysisScheduleFile"
                  type="file"
                  accept=".csv"
                  onChange={handleAnalysisScheduleUpload}
                  className="w-full border border-gray-300 rounded px-3 py-2"
                />
                {analysisScheduleSource && (
                  <div className="mt-1 text-xs text-gray-600">{analysisScheduleSource}</div>
                )}
              </div>
              <div className="min-w-[260px] flex-1">
                <label className="block font-medium mb-1" htmlFor="analysisRegistrationFile">Registration File (ACTIVE CSV / XLSX)</label>
                <input
                  id="analysisRegistrationFile"
                  type="file"
                  accept=".csv,.xlsx,.xls"
                  onChange={handleAnalysisRegistrationUpload}
                  className="w-full border border-gray-300 rounded px-3 py-2"
                />
                {analysisRegistrationSource && (
                  <div className="mt-1 text-xs text-gray-600">{analysisRegistrationSource}</div>
                )}
              </div>
              <button
                onClick={runAnalysisReport}
                className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded"
              >
                Run Analysis
              </button>
            </div>
            {analysisMessage && (
              <div className="mt-3 text-sm text-gray-700">{analysisMessage}</div>
            )}
          </div>

          {analysisReport && (
            <>
              <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
                {[
                  ['Matched Campers', `${analysisReport.summary.matchedCampers}/${analysisReport.summary.registrationCampers}`],
                  ['Fully Placed', analysisReport.summary.campersFullyPlaced],
                  ['Missing Slots', analysisReport.summary.missingSlots],
                  ['Avg Pref Rank', analysisReport.summary.avgPreferenceRank == null ? 'n/a' : analysisReport.summary.avgPreferenceRank.toFixed(2)],
                  ['Requested Assignments', analysisReport.summary.requestedAssignments],
                  ['Not Requested', analysisReport.summary.notRequestedAssignments],
                  ['Excluded Skills', analysisReport.summary.excludedAssignments],
                  ['Repeated Skills', analysisReport.summary.repeatedSkillViolations],
                  ['Top 3 Hit Campers', analysisReport.summary.campersWithTop3],
                ].map(([label, value]) => (
                  <div key={label} className="bg-white shadow rounded p-4">
                    <div className="text-xs uppercase tracking-wide text-gray-500">{label}</div>
                    <div className="mt-2 text-2xl font-semibold">{value}</div>
                  </div>
                ))}
              </div>

              <div className="grid gap-6 lg:grid-cols-2">
                <div className="bg-white shadow rounded p-4">
                  <h2 className="text-lg font-semibold mb-3">Preference Hits</h2>
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    {Array.from({ length: 10 }, (_, idx) => idx + 1).map((rank) => (
                      <div key={rank} className="flex justify-between border-b py-1">
                        <span>Preference #{rank}</span>
                        <span>{analysisReport.preferenceHits[rank] || 0}</span>
                      </div>
                    ))}
                    <div className="flex justify-between border-b py-1">
                      <span>Not requested</span>
                      <span>{analysisReport.preferenceHits.none || 0}</span>
                    </div>
                    <div className="flex justify-between border-b py-1">
                      <span>Excluded</span>
                      <span>{analysisReport.preferenceHits.excluded || 0}</span>
                    </div>
                  </div>
                </div>

                <div className="bg-white shadow rounded p-4">
                  <h2 className="text-lg font-semibold mb-3">Per Rotation</h2>
                  <table className="min-w-full text-sm border">
                    <thead>
                      <tr>
                        <th className="border px-2 py-1">Rotation</th>
                        <th className="border px-2 py-1">Filled</th>
                        <th className="border px-2 py-1">Blank</th>
                        <th className="border px-2 py-1">Requested</th>
                        <th className="border px-2 py-1">Not Requested</th>
                        <th className="border px-2 py-1">Excluded</th>
                      </tr>
                    </thead>
                    <tbody>
                      {analysisReport.perRotation.map((rotation) => (
                        <tr key={rotation.rotation}>
                          <td className="border px-2 py-1 text-center">{rotation.rotation}</td>
                          <td className="border px-2 py-1 text-center">{rotation.filled}</td>
                          <td className="border px-2 py-1 text-center">{rotation.blank}</td>
                          <td className="border px-2 py-1 text-center">{rotation.requested}</td>
                          <td className="border px-2 py-1 text-center">{rotation.notRequested}</td>
                          <td className="border px-2 py-1 text-center">{rotation.excluded || 0}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="grid gap-6 lg:grid-cols-2">
                <div className="bg-white shadow rounded p-4">
                  <h2 className="text-lg font-semibold mb-3">Top Assigned Skills</h2>
                  <table className="min-w-full text-sm border">
                    <thead>
                      <tr>
                        <th className="border px-2 py-1 text-left">Skill</th>
                        <th className="border px-2 py-1 text-center">Count</th>
                      </tr>
                    </thead>
                    <tbody>
                      {analysisReport.skillCounts.slice(0, 20).map((entry) => (
                        <tr key={entry.skill}>
                          <td className="border px-2 py-1">{entry.skill}</td>
                          <td className="border px-2 py-1 text-center">{entry.count}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <div className="bg-white shadow rounded p-4">
                  <h2 className="text-lg font-semibold mb-3">Matching Issues</h2>
                  <div className="text-sm space-y-3">
                    <div>
                      <div className="font-medium">Missing from Schedule: {analysisReport.summary.missingFromSchedule}</div>
                      <div className="text-gray-600 text-xs">
                        {analysisReport.missingCampers.slice(0, 15).join(', ') || 'None'}
                      </div>
                    </div>
                    <div>
                      <div className="font-medium">Extra in Schedule: {analysisReport.summary.extraInSchedule}</div>
                      <div className="text-gray-600 text-xs">
                        {analysisReport.extraCampers.slice(0, 15).join(', ') || 'None'}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      )}
      {/* Show summary content only when in summary mode */}
      {viewMode === 'summary' && (
      <div className="grid md:grid-cols-2 gap-6">
        {/* Upload and process section */}
        <div className="bg-white shadow p-4 rounded">
          <h2 className="text-xl font-semibold mb-4">Upload Campers</h2>
          {activeLocation && (
            <div className="mb-4 rounded border border-blue-200 bg-blue-50 px-3 py-2 text-sm text-blue-900">
              Active location: <span className="font-medium">{activeLocation.name}</span>. Covered grades: {normalizeCoveredGradeRange(activeLocation).coveredGradeMin === 0 ? 'K' : normalizeCoveredGradeRange(activeLocation).coveredGradeMin} to {normalizeCoveredGradeRange(activeLocation).coveredGradeMax}.
            </div>
          )}
          <div className="mb-4">
            <label className="block font-medium mb-1" htmlFor="campersFile">Campers CSV / XLSX</label>
            <input
              id="campersFile"
              type="file"
              accept=".csv,.xlsx,.xls"
              onChange={handleCampersUpload}
              className="w-full border border-gray-300 rounded px-3 py-2"
            />
          </div>
          <div className="mb-4">
            <div className="flex items-center justify-between mb-2">
              <div>
                <label className="block font-medium">Staff Teams</label>
                <p className="text-xs text-gray-600">
                  Select one or more saved staff teams for this scheduling run.
                </p>
              </div>
              <button
                className="bg-gray-800 hover:bg-gray-900 text-white px-3 py-1 rounded text-sm"
                onClick={() => openStaffTeamEditor(null)}
              >
                New Team
              </button>
            </div>
            <div className="space-y-2 rounded border border-gray-200 p-3">
              {staffTeams.length === 0 ? (
                <p className="text-sm text-gray-500">No saved staff teams.</p>
              ) : (
                staffTeams.map((team) => (
                  <div key={team.id} className="flex items-start justify-between gap-3 rounded border border-gray-200 px-3 py-2">
                    <label className="flex items-start gap-3 flex-1">
                      <input
                        type="checkbox"
                        className="mt-1"
                        checked={activeStaffTeamIds.includes(team.id)}
                        onChange={(e) => toggleStaffTeamSelection(team.id, e.target.checked)}
                      />
                      <div>
                        <div className="font-medium">{team.name}</div>
                        <div className="text-xs text-gray-600">
                          {(team.staff || []).length} staffers
                        </div>
                      </div>
                    </label>
                    <div className="flex items-center gap-2">
                      <button
                        className="bg-gray-500 hover:bg-gray-600 text-white px-3 py-1 rounded text-xs"
                        onClick={() => openStaffTeamEditor(team.id)}
                      >
                        Edit
                      </button>
                      <button
                        className="bg-red-600 hover:bg-red-700 text-white px-3 py-1 rounded text-xs"
                        onClick={() => handleDeleteStaffTeam(team.id)}
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
          {/* Configurable settings */}
          <div className="mb-4">
            <label className="block font-medium mb-1" htmlFor="groupMin">Group Min Size</label>
            <input
              id="groupMin"
              type="number"
              value={groupMin}
              min={1}
              className="w-full border border-gray-300 rounded px-3 py-2"
              onChange={(e) => setGroupMin(parseInt(e.target.value) || 0)}
            />
          </div>
          
          <div className="mb-4">
            <label className="block font-medium mb-1" htmlFor="groupMax">Group Max Size</label>
            <input
              id="groupMax"
              type="number"
              value={groupMax}
              min={groupMin}
              className="w-full border border-gray-300 rounded px-3 py-2"
              onChange={(e) => setGroupMax(parseInt(e.target.value) || 0)}
            />
          </div>
          <div className="mb-4 flex items-center space-x-2">
            <input
              id="genderedPacks"
              type="checkbox"
              checked={genderedPacks}
              onChange={(e) => setGenderedPacks(e.target.checked)}
            />
            <label htmlFor="genderedPacks" className="font-medium">Gendered Packs</label>
          </div>
          <div className="mb-4">
            <label className="block font-medium mb-1" htmlFor="maxRuntimeSeconds">Optimizer Runtime (seconds)</label>
            <input
              id="maxRuntimeSeconds"
              type="number"
              min={1}
              value={maxRuntimeSeconds}
              className="w-full border border-gray-300 rounded px-3 py-2"
              onChange={(e) => setMaxRuntimeSeconds(parseInt(e.target.value, 10) || 1)}
            />
          </div>
          <button
            onClick={processSchedule}
            disabled={isScheduling}
            className={`px-4 py-2 rounded text-white ${isScheduling ? 'bg-blue-300 cursor-not-allowed' : 'bg-blue-600 hover:bg-blue-700'}`}
          >
            {isScheduling
              ? scheduleProgress?.phase === 'deep-repair' ? 'Deep Repair Running...' : 'Optimizing...'
              : 'Auto Assign'}
          </button>
          {isScheduling && (
            <button
              onClick={cancelScheduleRun}
              className="ml-2 bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded"
            >
              {scheduleProgress?.phase === 'deep-repair' ? 'Stop Deep Repair' : 'Stop Optimizer & Start Deep Repair'}
            </button>
          )}
          {scheduleProgress && (
            <div className="mt-3 text-xs text-gray-700 bg-gray-100 rounded p-2">
              <div className="font-medium">
                {scheduleProgress.phase === 'deep-repair' ? 'Deep requested repair' : 'Schedule optimization'}
              </div>
              <div>Iterations: {scheduleProgress.iteration ?? 0}</div>
              <div>Elapsed: {((scheduleProgress.elapsedMs || 0) / 1000).toFixed(1)}s</div>
              <div>Best Unplaced: {scheduleProgress.bestUnplaced ?? '—'}</div>
              {scheduleProgress.phase === 'deep-repair' && (
                <>
                  <div>Runs until all holes are filled, no requested-only path remains, or you stop it.</div>
                  <div>Holes: {scheduleProgress.holesRemaining ?? '—'} / {scheduleProgress.holesAtStart ?? '—'} remaining</div>
                  <div>Current depth: {scheduleProgress.currentDepth ?? '—'}</div>
                  <div>Accepted paths: {scheduleProgress.pathsAccepted ?? 0}</div>
                  <div>Nodes expanded: {scheduleProgress.nodesExpanded ?? 0}</div>
                </>
              )}
              {(scheduleProgress.requestCoverageWarnings || []).length > 0 && (
                <div className="mt-2 rounded border border-amber-300 bg-amber-50 p-2 text-amber-950">
                  <div className="font-medium">
                    {scheduleProgress.requestCoverageWarnings.length} camper(s) have fewer than six unique eligible requested skills.
                  </div>
                  <div>
                    {scheduleProgress.requestCoverageWarnings.map((entry) => (
                      `${entry.camperName} (${entry.uniqueRequestedSkillCount})`
                    )).join(', ')}
                  </div>
                </div>
              )}
            </div>
          )}
          {message && <p className="mt-4 text-sm text-gray-700">{message}</p>}
          {solverDiagnostics && (
            <div className="mt-2 text-xs text-gray-600">
              <div>Seed: {solverDiagnostics.seed}</div>
              <div>Solver iterations: {solverDiagnostics.iterations}</div>
              <div>Solver elapsed: {((solverDiagnostics.elapsedMs || 0) / 1000).toFixed(2)}s</div>
              {(solverDiagnostics.requestCoverageWarnings || []).length > 0 && (
                <div className="mt-2 rounded border border-amber-300 bg-amber-50 p-2 text-amber-950">
                  <div className="font-medium">
                    Cannot satisfy six unique requested skills for {solverDiagnostics.requestCoverageWarnings.length} camper(s):
                  </div>
                  <ul className="list-disc pl-5">
                    {solverDiagnostics.requestCoverageWarnings.map((entry) => (
                      <li key={entry.camperId}>
                        {entry.camperName}: {entry.uniqueRequestedSkillCount} unique eligible requested skills ({entry.shortfall} short)
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              <button
                type="button"
                onClick={handleDownloadSolverDiagnostics}
                className="mt-1 rounded bg-slate-700 px-2 py-1 text-xs text-white hover:bg-slate-800"
              >
                Download Run Diagnostics
              </button>
              {solverDiagnostics.stageCounters && (
                <>
                  <div className="mt-1 font-medium text-slate-700">Solver attempt totals (all starts)</div>
                  <div>Final swap attempts: {solverDiagnostics.stageCounters.finalSwapAttempts || 0}</div>
                  <div>Final swap resolved: {solverDiagnostics.stageCounters.finalSwapResolved || 0}</div>
                  <div>Constrained commitments: {solverDiagnostics.stageCounters.constrainedCommitments || 0}</div>
                  <div>Target minimum placements: {solverDiagnostics.stageCounters.targetMinimumPlacements || 0}</div>
                  <div>Target balance placements: {solverDiagnostics.stageCounters.targetBalancePlacements || 0}</div>
                  <div>Commitment seat shortfall: {solverDiagnostics.stageCounters.commitmentShortfalls || 0}</div>
                  <div>Layout mutations accepted: {solverDiagnostics.stageCounters.layoutMutationAccepted || 0}</div>
                </>
              )}
              {solverDiagnostics.stageTimingsMs && (
                <div>
                  Layout mutation time: {((solverDiagnostics.stageTimingsMs.layoutMutationMs || 0) / 1000).toFixed(2)}s
                </div>
              )}
              {deepRepairSummary && (
                <div className="mt-2 rounded border border-indigo-200 bg-indigo-50 p-2 text-indigo-950">
                  <div className="font-medium">
                    Deep repair resolved {deepRepairSummary.holesResolved} of {deepRepairSummary.holesAtStart} holes.
                  </div>
                  <div>
                    {deepRepairSummary.directInsertions} direct, {deepRepairSummary.capacityChainsAccepted} capacity chains, {deepRepairSummary.crossRotationPivotsAccepted} cross-rotation pivots, {deepRepairSummary.minimumBackfillChainsAccepted} minimum-backfill chains.
                  </div>
                  <div>
                    {deepRepairSummary.holesRemaining} holes remain; searched {deepRepairSummary.nodesExpanded} nodes through depth {deepRepairSummary.maxDepthAttempted}.
                  </div>
                  {deepRepairSummary.budgetExhausted && (
                    <div>The bounded search reached its time or node budget; a deeper requested-only solution may still exist.</div>
                  )}
                  {deepRepairSummary.cancelled && <div>Deep repair stopped at the operator's request.</div>}
                </div>
              )}
              {Object.keys(demandDiagnosticSummary.phases).length > 0 && (
                <div className="mt-2 rounded border border-slate-200 bg-slate-50 p-2">
                  <div className="font-medium text-slate-800">Placement phase holes</div>
                  <div className="mt-1 grid grid-cols-2 gap-x-3 gap-y-1">
                    {Object.entries(demandDiagnosticSummary.phases).map(([phase, snapshot]) => (
                      <div key={phase}>
                        {phase.replace(/([A-Z])/g, ' $1').replace(/^./, (value) => value.toUpperCase())}: {snapshot.missingSlots || 0}
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {demandDiagnosticSummary.targetRunOutcomes.length > 0 && (
                <div className="mt-2 overflow-x-auto rounded border border-slate-200 bg-white p-2">
                  <div className="mb-1 font-medium text-slate-800">Target run outcomes</div>
                  <table className="min-w-full text-left text-xs">
                    <thead>
                      <tr className="border-b border-slate-200 text-slate-600">
                        <th className="pr-3 py-1">Skill</th>
                        <th className="pr-3 py-1">Demand</th>
                        <th className="pr-3 py-1">Target</th>
                        <th className="pr-3 py-1">Committed</th>
                        <th className="pr-3 py-1">Survived</th>
                        <th className="pr-3 py-1">Shortfall</th>
                        <th className="py-1">Reason</th>
                      </tr>
                    </thead>
                    <tbody>
                      {demandDiagnosticSummary.targetRunOutcomes.map((entry) => (
                        <tr
                          key={`target-run-${entry.skill}`}
                          className={(entry.targetRunShortfall || 0) > 0 ? 'bg-amber-50 text-amber-950' : 'border-b border-slate-100'}
                        >
                          <td className="pr-3 py-1 font-medium">{entry.skill}</td>
                          <td className="pr-3 py-1">{entry.eligibleRequestCount || 0}</td>
                          <td className="pr-3 py-1">{entry.targetRuns || 0}</td>
                          <td className="pr-3 py-1">{entry.committedRuns || 0}</td>
                          <td className="pr-3 py-1">{entry.survivingRuns || 0}</td>
                          <td className="pr-3 py-1">{entry.targetRunShortfall || 0}</td>
                          <td className="py-1">{entry.targetRunReason || '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              {demandDiagnosticSummary.hasIssues && (
                <div className="mt-2 rounded border border-amber-200 bg-amber-50 p-2 text-amber-900">
                  <div className="font-medium">Demand/capacity notes</div>
                  {demandDiagnosticSummary.saturatedHighDemand.slice(0, 3).map((entry) => (
                    <div key={`shortfall-${entry.skill}`}>
                      {entry.skill}: demand exceeds planned capacity; {entry.fullRuns || 0}/{entry.plannedRuns || 0} runs full.
                    </div>
                  ))}
                  {demandDiagnosticSummary.underfilledLowDemand.slice(0, 3).map((entry) => (
                    <div key={`underfilled-${entry.skill}`}>
                      {entry.skill}: lower utilization ({entry.filledSeats || 0}/{entry.plannedCapacity || 0} seats filled).
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
          {lastRunSummary && (lastRunSummary.cancelled || lastRunSummary.unplacedCount === 0) && (
            <div className="mt-3 rounded border border-gray-300 bg-gray-50 p-3 text-sm">
              <div className="font-medium">Run Results</div>
              <div className="text-xs text-gray-600 mb-2">
                {lastRunSummary.cancelled
                  ? 'Stopped and returned best-so-far.'
                  : 'Optimizer reached zero unplaced campers.'}
              </div>
              <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
                {Array.from({ length: 10 }, (_, idx) => {
                  const rank = idx + 1;
                  return (
                    <div key={rank}>Pref #{rank}: {lastRunSummary.preferenceHits[rank] || 0}</div>
                  );
                })}
                <div>Fallback: {lastRunSummary.preferenceHits.none || 0}</div>
              </div>
              <button
                onClick={handleTryAgain}
                disabled={isScheduling}
                className={`mt-3 w-full rounded px-3 py-2 text-white ${isScheduling ? 'bg-blue-300 cursor-not-allowed' : 'bg-blue-600 hover:bg-blue-700'}`}
              >
                Try Again
              </button>
            </div>
          )}
            {assignments && unplaced.length > 0 && (
              <div className="mt-3 rounded border border-amber-300 bg-amber-50 p-3 text-sm">
                <div className="font-medium text-amber-900">Unplaced campers remaining: {unplaced.length}</div>
                <div className="text-xs text-amber-800 mt-1">
                  Exports are locked until all campers are placed.
                </div>
                <button
                  onClick={() => setShowUnplacedModal(true)}
                  className="mt-2 bg-amber-600 hover:bg-amber-700 text-white px-3 py-2 rounded"
                >
                  Open Unplaced Campers
                </button>
              </div>
            )}
            {assignments && (
              <div className="mt-4 flex flex-col space-y-2">
                <button
                  onClick={handleExportCSV}
                  disabled={unplaced.length > 0}
                  className={`px-4 py-2 rounded text-white ${unplaced.length > 0 ? 'bg-green-300 cursor-not-allowed' : 'bg-green-600 hover:bg-green-700'}`}
                >
                  Export Camper Schedule
                </button>
                {staffAssignmentsState && (
                  <>
                    <button
                      onClick={handleExportStaffCSV}
                      disabled={unplaced.length > 0}
                      className={`px-4 py-2 rounded text-white ${unplaced.length > 0 ? 'bg-indigo-300 cursor-not-allowed' : 'bg-indigo-600 hover:bg-indigo-700'}`}
                    >
                      Export Staff Schedule CSV
                    </button>
                    <button
                      onClick={handleExportSkillWorkbook}
                      disabled={unplaced.length > 0}
                      className={`px-4 py-2 rounded text-white ${unplaced.length > 0 ? 'bg-violet-300 cursor-not-allowed' : 'bg-violet-600 hover:bg-violet-700'}`}
                    >
                      Export Skill Rosters (XLSX)
                    </button>
                    <button
                      onClick={() => setViewMode('detailed')}
                      className="bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 rounded"
                    >
                      Detailed View
                    </button>
                    <button
                      onClick={() => setViewMode('staff')}
                      className="bg-teal-600 hover:bg-teal-700 text-white px-4 py-2 rounded"
                    >
                      Staff Overview
                    </button>
                    <button
                      onClick={() => setViewMode('schedule')}
                      className="bg-gray-800 hover:bg-gray-900 text-white px-4 py-2 rounded"
                    >
                      Skill Options
                    </button>
                  </>
                )}
              </div>
            )}
        </div>

        {/* Results and manual assignment section */}
        <div className="bg-white shadow p-4 rounded overflow-auto max-h-[80vh]">
          {assignments ? (
            <>
              <h2 className="text-xl font-semibold mb-2">Schedule Summary</h2>
              {/* Preference hit statistics */}
              {preferenceHits && (
                (() => {
                  const normalized = normalizePreferenceHits(preferenceHits);
                  return (
                    <div className="mb-4">
                      <h3 className="font-medium">Preference Hit Stats</h3>
                      <ul className="list-disc list-inside text-sm">
                        {Array.from({ length: 10 }, (_, idx) => {
                          const rank = idx + 1;
                          return (
                            <li key={rank}>
                              Pref #{rank}: {normalized[rank] || 0}
                            </li>
                          );
                        })}
                        <li>Fallback: {normalized.none || 0}</li>
                      </ul>
                    </div>
                  );
                })()
              )}
              {demandDiagnosticSummary.hasIssues && (
                <div className="mb-4 rounded border border-amber-200 bg-amber-50 p-3 text-sm">
                  <h3 className="font-medium text-amber-950">Demand and Capacity</h3>
                  {demandDiagnosticSummary.saturatedHighDemand.length > 0 && (
                    <div className="mt-2">
                      <div className="text-xs font-medium uppercase text-amber-800">Popular skills with unmet demand</div>
                      <ul className="list-disc list-inside">
                        {demandDiagnosticSummary.saturatedHighDemand.map((entry) => (
                          <li key={entry.skill}>
                            {entry.skill}: {entry.filledSeats || 0}/{entry.plannedCapacity || 0} seats filled, unmet weighted demand {entry.unmetWeightedDemand || 0}.
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {demandDiagnosticSummary.underfilledLowDemand.length > 0 && (
                    <div className="mt-2">
                      <div className="text-xs font-medium uppercase text-amber-800">Lower-utilization runs</div>
                      <ul className="list-disc list-inside">
                        {demandDiagnosticSummary.underfilledLowDemand.map((entry) => (
                          <li key={entry.skill}>
                            {entry.skill}: {entry.filledSeats || 0}/{entry.plannedCapacity || 0} seats filled across {entry.plannedRuns || 0} planned runs.
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              )}
              {/* Unplaced campers table */}
              {unplaced.length > 0 ? (
                <div className="text-sm text-gray-700">
                  <div>{unplaced.length} campers still need placement.</div>
                  <button
                    onClick={() => setShowUnplacedModal(true)}
                    className="mt-2 bg-amber-600 hover:bg-amber-700 text-white px-3 py-1 rounded"
                  >
                    Open Unplaced Campers
                  </button>
                </div>
              ) : (
                <p>All campers have been placed.</p>
              )}
              {/* Pack statistics */}
              {packStats && (
                <div className="mt-6">
                  <h3 className="font-medium mb-2">Pack Statistics</h3>
                  <table className="min-w-full text-sm border">
                    <thead>
                      <tr>
                        <th className="border px-2 py-1">Tribe</th>
                        <th className="border px-2 py-1">Campers</th>
                        <th className="border px-2 py-1">Packs</th>
                        <th className="border px-2 py-1">Avg Size</th>
                        {genderedPacks && (
                          <>
                            <th className="border px-2 py-1">Male (campers/packs)</th>
                            <th className="border px-2 py-1">Female (campers/packs)</th>
                          </>
                        )}
                      </tr>
                    </thead>
                    <tbody>
                      {Object.keys(packStats).map((tribe) => {
                        const stat = packStats[tribe];
                        return (
                          <tr key={tribe} className="border-b">
                            <td className="border px-2 py-1">{tribe}</td>
                            <td className="border px-2 py-1 text-center">{stat.total}</td>
                            <td className="border px-2 py-1 text-center">{stat.packs}</td>
                            <td className="border px-2 py-1 text-center">{stat.avgSize}</td>
                            {genderedPacks && (
                              <>
                                <td className="border px-2 py-1 text-center">{stat.maleCampers} / {stat.malePacks}</td>
                                <td className="border px-2 py-1 text-center">{stat.femaleCampers} / {stat.femalePacks}</td>
                              </>
                            )}
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
              {/* Rotation statistics */}
              {rotationStats && (
                <div className="mt-6">
                  <h3 className="font-medium mb-2">Rotation Statistics</h3>
                  <table className="min-w-full text-sm border">
                    <thead>
                      <tr>
                        <th className="border px-2 py-1">Rotation</th>
                        <th className="border px-2 py-1">Total Assigned</th>
                        <th className="border px-2 py-1">Groups Over Max</th>
                        <th className="border px-2 py-1">Groups Under Min</th>
                      </tr>
                    </thead>
                    <tbody>
                      {rotationStats.map((stat, idx) => {
                        const groups = Object.values(stat.groups);
                        const overMax = groups.filter((g) => g.overMax).length;
                        const underMin = groups.filter((g) => g.underMin).length;
                        return (
                          <tr key={idx} className="border-b">
                            <td className="border px-2 py-1 text-center">{idx + 1}</td>
                            <td className="border px-2 py-1 text-center">{stat.totalAssigned}</td>
                            <td className="border px-2 py-1 text-center">{overMax}</td>
                            <td className="border px-2 py-1 text-center">{underMin}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
              {rotationSkillMatrix && rotationSkillMatrix.skills.length > 0 && (
                <div className="mt-6">
                  <div className="flex items-end justify-between gap-3 mb-2">
                    <div>
                      <h3 className="font-medium">Rotation-by-Skill Matrix</h3>
                      <p className="text-xs text-gray-600">
                        Counts, staffing, and group-size warnings for every scheduled skill in every rotation.
                      </p>
                    </div>
                  </div>
                  <div className="overflow-auto border rounded">
                    <table className="min-w-full text-xs">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="border px-2 py-2 text-left sticky left-0 bg-gray-50">Skill</th>
                          {rotationSkillMatrix.rotations.map((_, rotationIndex) => (
                            <th key={rotationIndex} className="border px-2 py-2 text-center min-w-[170px]">
                              Rotation {rotationIndex + 1}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {rotationSkillMatrix.skills.map((skill) => (
                          <tr key={skill} className="border-b align-top">
                            <td className="border px-2 py-2 font-medium sticky left-0 bg-white whitespace-nowrap">{skill}</td>
                            {rotationSkillMatrix.rotations.map((rotation, rotationIndex) => {
                              const cell = rotation[skill];
                              if (!cell) {
                                return (
                                  <td key={`${skill}-${rotationIndex}`} className="border px-2 py-2 text-center text-gray-400">
                                    —
                                  </td>
                                );
                              }
                              const hasProblem = cell.overMaxGroups > 0 || cell.underMinGroups > 0 || cell.unstaffedGroups > 0;
                              const hasManualException = cell.manualExceptionGroups > 0;
                              return (
                                <td
                                  key={`${skill}-${rotationIndex}`}
                                  className={`border px-2 py-2 ${hasProblem ? 'bg-red-50' : hasManualException ? 'bg-amber-50' : 'bg-white'}`}
                                >
                                  <div className="font-medium">{cell.camperCount} campers</div>
                                  <div className="text-[11px] text-gray-600">{cell.groupCount} group{cell.groupCount === 1 ? '' : 's'}</div>
                                  <div className="mt-1 flex flex-wrap gap-1">
                                    {cell.unstaffedGroups > 0 && (
                                      <span className="rounded bg-red-100 px-1.5 py-0.5 text-[10px] text-red-700">
                                        {cell.unstaffedGroups} unstaffed
                                      </span>
                                    )}
                                    {cell.overMaxGroups > 0 && (
                                      <span className="rounded bg-amber-100 px-1.5 py-0.5 text-[10px] text-amber-700">
                                        {cell.overMaxGroups} over max
                                      </span>
                                    )}
                                    {cell.underMinGroups > 0 && (
                                      <span className="rounded bg-orange-100 px-1.5 py-0.5 text-[10px] text-orange-700">
                                        {cell.underMinGroups} under min
                                      </span>
                                    )}
                                    {cell.staffedGroups > 0 && cell.unstaffedGroups === 0 && (
                                      <span className="rounded bg-green-100 px-1.5 py-0.5 text-[10px] text-green-700">
                                        staffed
                                      </span>
                                    )}
                                  </div>
                                </td>
                              );
                            })}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
              {constraintAuditSummary && (
                <div className="mt-6">
                  <div className={`rounded border p-4 ${currentScheduleAudit?.canExport ? 'border-green-300 bg-green-50' : 'border-red-300 bg-red-50'}`}>
                    <div className="flex flex-wrap items-start justify-between gap-3">
                      <div>
                        <h3 className="font-medium">{constraintAuditSummary.statusLabel}</h3>
                        <p className="text-xs text-gray-700 mt-1">
                          {constraintAuditSummary.blockingCount} blocking issue categories, {constraintAuditSummary.manualExceptionCount} manual-exception categories.
                        </p>
                      </div>
                      <div className="text-xs text-gray-600">
                        Optimizer violations are defects. Manual exceptions are operator decisions.
                      </div>
                    </div>
                    <div className="mt-3 grid gap-3 md:grid-cols-2">
                      {constraintAuditSummary.sections.map((section) => (
                        <div key={section.key} className="rounded border bg-white p-3">
                          <div className="text-xs font-medium uppercase tracking-wide text-gray-700">{section.title}</div>
                          {section.items.length > 0 ? (
                            <ul className="mt-2 list-disc pl-5 text-xs text-gray-800">
                              {section.items.map((item) => (
                                <li key={item.code}>{item.message}</li>
                              ))}
                            </ul>
                          ) : (
                            <div className="mt-2 text-xs text-gray-500">{section.emptyLabel}</div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </>
          ) : (
            <p className="text-gray-600">Upload your files and process to see results.</p>
          )}
        </div>
      </div>
      )}
      {/* Detailed view: rotations and rosters */}
      {viewMode === 'detailed' && assignments && groups && (
        <div className="bg-white shadow p-4 rounded mt-4">
          <div className="flex mb-4 justify-between items-center">
            <h2 className="text-xl font-semibold">Detailed Rotation View</h2>
            <div className="space-x-2">
              <button
                onClick={() => setViewMode('staff')}
                className="bg-teal-600 hover:bg-teal-700 text-white px-3 py-1 rounded text-sm"
              >
                Staff Overview
              </button>
              <button
                onClick={() => setViewMode('summary')}
                className="bg-gray-200 hover:bg-gray-300 text-gray-700 px-3 py-1 rounded text-sm"
              >
                Back to Summary
              </button>
            </div>
          </div>
          <div className="flex overflow-auto" style={{ minHeight: '60vh' }}>
            {/* Rotations list */}
            <div className="w-1/6 pr-2 border-r">
              <h3 className="font-medium mb-2">Rotations</h3>
              <ul className="space-y-1 text-sm">
                {[0,1,2,3,4,5].map((r) => (
                  <li
                    key={r}
                    className={
                      'cursor-pointer px-2 py-1 rounded ' +
                      (selectedRotationDetail === r ? 'bg-blue-600 text-white' : 'hover:bg-gray-100')
                    }
                    onClick={() => {
                      setSelectedRotationDetail(r);
                      setSelectedSkillDetail(null);
                      setSelectedCamperDetail(null);
                    }}
                  >
                    Rotation {r + 1}
                  </li>
                ))}
              </ul>
            </div>
            {/* Skills list for selected rotation */}
            <div className="w-2/6 px-2 border-r">
              <h3 className="font-medium mb-2">Skills (Rotation {selectedRotationDetail + 1})</h3>
              {(() => {
                const rotationGroups = groups[selectedRotationDetail] || {};
                // Build aggregated skills: skill -> { groups: [group], tribes: {} }
                const skills = {};
                Object.keys(rotationGroups).forEach((gKey) => {
                  const g = rotationGroups[gKey];
                  const skill = g.skill;
                  if (!skills[skill]) {
                    skills[skill] = { groups: [], tribes: {} };
                  }
                  skills[skill].groups.push({ key: gKey, group: g });
                  Object.keys(g.tribes).forEach((t) => {
                    skills[skill].tribes[t] = (skills[skill].tribes[t] || 0) + g.tribes[t];
                  });
                });
                const skillNames = Object.keys(skills).sort();
                return (
                  <ul className="space-y-1 text-sm">
                    {skillNames.map((skill) => {
                      const tribesObj = skills[skill].tribes;
                      const tribeList = Object.keys(tribesObj)
                        .sort()
                        .map((t) => `${t}`)
                        .join(', ');
                      return (
                        <li
                          key={skill}
                          className={
                            'cursor-pointer px-2 py-1 rounded ' +
                            (selectedSkillDetail === skill ? 'bg-green-600 text-white' : 'hover:bg-gray-100')
                          }
                          onClick={() => {
                            setSelectedSkillDetail(skill);
                            setSelectedCamperDetail(null);
                          }}
                        >
                          <div className="flex flex-col">
                            <span className="font-medium">{skill}</span>
                            <span className="text-xs text-gray-600">{tribeList || '—'}</span>
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                );
              })()}
            </div>
            {/* Group roster & camper details */}
            <div className="w-3/6 pl-2 overflow-auto">
              {selectedSkillDetail ? (
                <>
                  <h3 className="font-medium mb-2">{selectedSkillDetail} Roster (Rotation {selectedRotationDetail + 1})</h3>
                  {(() => {
                    const rotationGroups = groups[selectedRotationDetail] || {};
                    // List groups for this skill
                    const groupEntries = Object.keys(rotationGroups)
                      .filter((gKey) => rotationGroups[gKey].skill === selectedSkillDetail)
                      .sort();
                    return (
                      <div className="space-y-4">
                        {groupEntries.map((gKey, idx) => {
                          const grp = rotationGroups[gKey];
                          return (
                            <div key={gKey} className="border p-2 rounded">
                              <div className="flex justify-between items-center mb-1">
                                <span className="font-medium">Group {idx + 1}</span>
                                <div className="flex items-center gap-3">
                                  <span className="text-xs text-gray-600">Staff: {grp.staff || '—'}</span>
                                </div>
                              </div>
                              <table className="min-w-full text-xs">
                                <thead>
                                  <tr>
                                    <th className="border px-2 py-1 text-left">Name</th>
                                    <th className="border px-2 py-1 text-left">Tribe</th>
                                    <th className="border px-2 py-1 text-left">Gender</th>
                                    <th className="border px-2 py-1 text-left">Grade</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {grp.campers.map((c) => (
                                    <tr
                                      key={c.name}
                                      className="border-b cursor-pointer hover:bg-gray-100"
                                      onClick={() => {
                                        setSelectedCamperDetail(c);
                                      }}
                                    >
                                      <td className="border px-2 py-1 whitespace-nowrap">{c.name}</td>
                                      <td className="border px-2 py-1 whitespace-nowrap">{c.tribe}</td>
                                      <td className="border px-2 py-1 whitespace-nowrap">{c.gender}</td>
                                      <td className="border px-2 py-1 whitespace-nowrap">{c.grade === 0 ? 'K' : c.grade}</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          );
                        })}
                      </div>
                    );
                  })()}
                </>
              ) : (
                <p className="text-gray-600">Select a skill to view rosters.</p>
              )}
              {/* Camper detail overlay is handled outside the flex container */}
            </div>
          </div>
        </div>
      )}

      {requiredScheduleSkillName && (
        <RequiredScheduleModal
          skill={skillSettings.find((skill) => skill.name === requiredScheduleSkillName) || null}
          onUpdate={(updater) => updateSkillSetting(requiredScheduleSkillName, updater)}
          onClose={() => setRequiredScheduleSkillName(null)}
        />
      )}

      {/* Skill options */}
      {viewMode === 'schedule' && (
        <div className="bg-white shadow p-4 rounded">
          <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
            <div>
              <h2 className="text-xl font-semibold">Skill Options</h2>
              <p className="text-sm text-gray-600">
                Configure eligibility, allowed run rotations, group limits, and required-skill behavior.
              </p>
            </div>
            <button
              onClick={handleSaveSkillSettings}
              disabled={isSavingSkills || !activeLocationId}
              className={`px-4 py-2 rounded text-white ${isSavingSkills || !activeLocationId ? 'bg-green-300 cursor-not-allowed' : 'bg-green-600 hover:bg-green-700'}`}
            >
              {isSavingSkills ? 'Saving...' : 'Save Skill Options'}
            </button>
          </div>
          {skillSettingsError && (
            <div className="mb-4 rounded border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-700">
              {skillSettingsError}
            </div>
          )}
          <div className="space-y-6">
            <div className="overflow-auto border rounded">
              <table className="min-w-full text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="border px-2 py-2 text-left">Skill</th>
                    <th className="border px-2 py-2 text-left">Required</th>
                    <th className="border px-2 py-2 text-left">Repeatable</th>
                    <th className="border px-2 py-2 text-left">Gender</th>
                    <th className="border px-2 py-2 text-left">Tribes</th>
                    <th className="border px-2 py-2 text-left">Min / Max</th>
                    <th className="border px-2 py-2 text-left">Run Rotations</th>
                    <th className="border px-2 py-2 text-left">Overrides</th>
                  </tr>
                </thead>
                <tbody>
                  {skillSettings.map((skill) => (
                    <React.Fragment key={skill.name}>
                    <tr className="align-top">
                      <td className="border px-2 py-2 font-medium whitespace-nowrap">{skill.name}</td>
                      <td className="border px-2 py-2">
                        <div className="space-y-2">
                          <label className="inline-flex items-center gap-2">
                            <input
                              type="checkbox"
                              checked={skill.requiredForEligible}
                              onChange={(e) => updateSkillSetting(skill.name, (prev) => ({ ...prev, requiredForEligible: e.target.checked }))}
                            />
                            <span className="text-xs">Eligible campers must receive it</span>
                          </label>
                          {skill.requiredForEligible && (
                            <button
                              type="button"
                              onClick={() => setRequiredScheduleSkillName(skill.name)}
                              className="block rounded border border-blue-300 bg-blue-50 px-2 py-1 text-xs font-medium text-blue-800 hover:bg-blue-100"
                            >
                              Configure required schedule
                            </button>
                          )}
                        </div>
                      </td>
                      <td className="border px-2 py-2">
                        <label className="inline-flex items-center gap-2">
                          <input
                            type="checkbox"
                            checked={skill.repeatable === true}
                            onChange={(e) => updateSkillSetting(skill.name, (prev) => ({ ...prev, repeatable: e.target.checked }))}
                          />
                          <span className="text-xs">May repeat across rotations</span>
                        </label>
                      </td>
                      <td className="border px-2 py-2">
                        <div className="space-y-1">
                          {['Male', 'Female'].map((gender) => (
                            <label key={gender} className="flex items-center gap-2 text-xs">
                              <input
                                type="checkbox"
                                checked={skill.allowedGenders.includes(gender)}
                                onChange={(e) => updateSkillSetting(skill.name, (prev) => ({
                                  ...prev,
                                  allowedGenders: e.target.checked
                                    ? Array.from(new Set([...prev.allowedGenders, gender]))
                                    : prev.allowedGenders.filter((value) => value !== gender),
                                }))}
                              />
                              {gender}
                            </label>
                          ))}
                        </div>
                      </td>
                      <td className="border px-2 py-2">
                        <div className="grid grid-cols-2 gap-1">
                          {activeTribeNames.map((tribe) => (
                            <label key={tribe} className="flex items-center gap-2 text-xs">
                              <input
                                type="checkbox"
                                checked={skill.allowedTribes.includes(tribe)}
                                onChange={(e) => updateSkillSetting(skill.name, (prev) => ({
                                  ...prev,
                                  allowedTribes: e.target.checked
                                    ? Array.from(new Set([...prev.allowedTribes, tribe]))
                                    : prev.allowedTribes.filter((value) => value !== tribe),
                                }))}
                              />
                              {tribe}
                            </label>
                          ))}
                        </div>
                      </td>
                      <td className="border px-2 py-2">
                        <div className="flex gap-2">
                          <input
                            type="number"
                            min={1}
                            value={skill.minGroupSize ?? ''}
                            onChange={(e) => updateSkillSetting(skill.name, (prev) => ({ ...prev, minGroupSize: e.target.value === '' ? null : parseInt(e.target.value, 10) || null }))}
                            className="w-20 border border-gray-300 rounded px-2 py-1"
                            placeholder="Min"
                          />
                          <input
                            type="number"
                            min={1}
                            value={skill.maxGroupSize ?? ''}
                            onChange={(e) => updateSkillSetting(skill.name, (prev) => ({ ...prev, maxGroupSize: e.target.value === '' ? null : parseInt(e.target.value, 10) || null }))}
                            className="w-20 border border-gray-300 rounded px-2 py-1"
                            placeholder="Max"
                          />
                        </div>
                      </td>
                      <td className="border px-2 py-2">
                        {skill.requiredForEligible
                          && normalizeRequiredSchedule(skill.requiredSchedule, skill.fixedRotations).mode === REQUIRED_SCHEDULE_MODE_PER_BLOCK ? (
                            <div className="text-xs text-gray-500">
                              Managed by the required schedule popup.
                            </div>
                          ) : (
                            <div className="grid min-w-[7rem] grid-cols-3 gap-1">
                              {[0, 1, 2, 3, 4, 5].map((rotationIndex) => (
                                <label key={rotationIndex} className="flex items-center gap-1 text-xs">
                                  <input
                                    type="checkbox"
                                    checked={skill.fixedRotations.includes(rotationIndex)}
                                    onChange={(event) => updateSkillSetting(skill.name, (previous) => ({
                                      ...previous,
                                      fixedRotations: event.target.checked
                                        ? Array.from(new Set([...previous.fixedRotations, rotationIndex])).sort((a, b) => a - b)
                                        : previous.fixedRotations.filter((value) => value !== rotationIndex),
                                    }))}
                                  />
                                  R{rotationIndex + 1}
                                </label>
                              ))}
                            </div>
                          )}
                      </td>
                      <td className="border px-2 py-2">
                        <label className="flex items-center gap-2 text-xs mb-2">
                          <input
                            type="checkbox"
                            checked={skill.enableRotationOverrides}
                            onChange={(e) => updateSkillSetting(skill.name, (prev) => ({
                              ...prev,
                              enableRotationOverrides: e.target.checked,
                              rotationOverrides: e.target.checked ? (prev.rotationOverrides || []) : [],
                            }))}
                          />
                          Enable per-rotation overrides
                        </label>
                        <label className="block text-xs">
                          <span className="block mb-1">Manual overfill policy</span>
                          <select
                            value={skill.manualOverfillPolicy || 'disallow'}
                            onChange={(e) => updateSkillSetting(skill.name, (prev) => ({ ...prev, manualOverfillPolicy: e.target.value === 'allow' ? 'allow' : 'disallow' }))}
                            className="w-full border border-gray-300 rounded px-2 py-1 text-xs"
                          >
                            <option value="disallow">Disallow</option>
                            <option value="allow">Allow over max</option>
                          </select>
                        </label>
                      </td>
                    </tr>
                    {skill.enableRotationOverrides && (
                      <tr>
                        <td className="border px-2 py-2 bg-gray-50 text-xs text-gray-700" colSpan={8}>
                          <div className="font-medium mb-2">Per-Rotation Overrides</div>
                          <div className="grid gap-2 md:grid-cols-2 xl:grid-cols-3">
                            {[0, 1, 2, 3, 4, 5].map((rotationIndex) => {
                              const override = (skill.rotationOverrides || []).find((entry) => Number(entry.rotationIndex) === rotationIndex)
                                || { rotationIndex, enabled: true, override: {} };
                              return (
                                <div key={rotationIndex} className="rounded border bg-white p-2">
                                  <div className="font-medium mb-2">Rotation {rotationIndex + 1}</div>
                                  <label className="flex items-center gap-2 text-xs mb-2">
                                    <input
                                      type="checkbox"
                                      checked={override.enabled !== false}
                                      onChange={(e) => updateSkillRotationOverride(skill.name, rotationIndex, (current) => ({
                                        ...current,
                                        enabled: e.target.checked,
                                      }))}
                                    />
                                    Enabled
                                  </label>
                                  <div className="grid grid-cols-2 gap-2">
                                    <input
                                      type="number"
                                      min={1}
                                      value={override.override?.minGroupSize ?? ''}
                                      onChange={(e) => updateSkillRotationOverride(skill.name, rotationIndex, (current) => ({
                                        ...current,
                                        override: {
                                          ...(current.override || {}),
                                          minGroupSize: e.target.value === '' ? null : parseInt(e.target.value, 10) || null,
                                        },
                                      }))}
                                      className="border border-gray-300 rounded px-2 py-1 text-xs"
                                      placeholder="Min"
                                    />
                                    <input
                                      type="number"
                                      min={1}
                                      value={override.override?.maxGroupSize ?? ''}
                                      onChange={(e) => updateSkillRotationOverride(skill.name, rotationIndex, (current) => ({
                                        ...current,
                                        override: {
                                          ...(current.override || {}),
                                          maxGroupSize: e.target.value === '' ? null : parseInt(e.target.value, 10) || null,
                                        },
                                      }))}
                                      className="border border-gray-300 rounded px-2 py-1 text-xs"
                                      placeholder="Max"
                                    />
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </td>
                      </tr>
                    )}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Staff overview view */}
      {viewMode === 'staff' && assignments && staffAssignmentsState && groups && (
        <div className="bg-white shadow p-4 rounded mt-4">
          <div className="flex mb-4 justify-between items-center">
            <h2 className="text-xl font-semibold">Staff Schedule Overview</h2>
            <button
              onClick={() => setViewMode('summary')}
              className="bg-gray-200 hover:bg-gray-300 text-gray-700 px-3 py-1 rounded text-sm"
            >
              Back to Summary
            </button>
          </div>
          <div className="overflow-auto">
            <table className="min-w-full text-sm border">
              <thead>
                <tr>
                  <th className="border px-2 py-1">Staffer</th>
                  {[0,1,2,3,4,5].map((r) => (
                    <th key={r} className="border px-2 py-1 text-center">Rot {r + 1}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {staffers.map((staffer) => {
                  const assigns = staffAssignmentsState[staffer.name] || [];
                  return (
                    <tr key={staffer.name}>
                      <td className="border px-2 py-1 whitespace-nowrap cursor-pointer text-blue-600 underline" onClick={() => setSelectedStafferDetail(staffer)}>{staffer.name}</td>
                      {[0,1,2,3,4,5].map((r) => {
                        const gKey = assigns[r];
                        let skill = '';
                        if (gKey) {
                          const grp = groups[r][gKey];
                          skill = grp ? grp.skill : '';
                        }
                        return (
                          <td key={r} className="border px-2 py-1 text-center whitespace-nowrap">
                            {skill || '—'}
                          </td>
                        );
                      })}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Camper detail overlay modal */}
      {selectedCamperDetail && assignments && groups && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white rounded shadow-lg w-11/12 max-w-3xl max-h-[90vh] overflow-auto p-4 relative">
            <div className="flex justify-between items-start mb-4">
              <div>
                <h3 className="text-xl font-semibold mb-1">{selectedCamperDetail.name}</h3>
                <p className="text-sm text-gray-600 mb-1">Tribe: {selectedCamperDetail.tribe}, Gender: {selectedCamperDetail.gender}</p>
                <p className="text-sm text-gray-600 mb-2">Preferences: {selectedCamperDetail.preferences.join(', ')}</p>
              </div>
              <button
                className="text-gray-600 hover:text-gray-800 text-xl font-bold"
                onClick={() => setSelectedCamperDetail(null)}
              >
                ×
              </button>
            </div>
            <div className="overflow-auto">
              <table className="min-w-full text-sm border">
                <thead>
                  <tr>
                    <th className="border px-2 py-1">Rotation</th>
                    <th className="border px-2 py-1">Current Skill</th>
                    <th className="border px-2 py-1">Change Group</th>
                    <th className="border px-2 py-1">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {[0,1,2,3,4,5].map((r) => {
                    // Determine if camper can be assigned to this rotation
                    const assignedRotation = assignments[getCamperAssignmentKey(selectedCamperDetail)] || {};
                    const currentGroupKey = assignedRotation[r];
                    const currentSkill = currentGroupKey && groups[r] && groups[r][currentGroupKey] ? groups[r][currentGroupKey].skill : '';
                    const options = getRankedManualCandidatesForCamper(selectedCamperDetail, r);
                    return (
                      <tr key={r} className="border-b">
                        <td className="border px-2 py-1 text-center">{r + 1}</td>
                        <td className="border px-2 py-1 whitespace-nowrap">{currentSkill || '—'}</td>
                        <td className="border px-2 py-1">
                          {options.length > 0 ? (
                            <select
                              className="border rounded px-1 py-0.5"
                              value={currentGroupKey || ''}
                              onChange={(e) => {
                                const newKey = e.target.value;
                                // If currently assigned, move to new group; otherwise assign
                                moveCamperToGroup(selectedCamperDetail, r, newKey);
                              }}
                            >
                              <option value="">-- Select --</option>
                              {options.map((option) => {
                                const g = groups[r][option.groupKey];
                                const ruleLabel = option.ruleBreaks.length > 0
                                  ? ` - ${option.ruleBreaks.join(', ')}`
                                  : '';
                                const label = `${g.skill} (${g.campers.length})${ruleLabel}`;
                                return (
                                  <option key={option.groupKey} value={option.groupKey}>{label}</option>
                                );
                              })}
                            </select>
                          ) : (
                            <span className="text-gray-400">N/A</span>
                          )}
                        </td>
                        <td className="border px-2 py-1 text-center">
                          {currentGroupKey ? (
                            <button
                              className="bg-red-500 hover:bg-red-600 text-white px-2 py-1 rounded text-xs"
                              onClick={() => removeAssignmentForCamper(selectedCamperDetail, r)}
                            >
                              Remove
                            </button>
                          ) : (
                            <span className="text-gray-400">—</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Modal for assigning staffers to unstaffed groups */}
      {showUnstaffedModal && unstaffedGroups.length > 0 && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white rounded shadow-lg w-11/12 max-w-3xl max-h-[90vh] overflow-auto p-4 relative">
            <div className="flex justify-between items-start mb-4">
              <h3 className="text-xl font-semibold">Assign Staffers to Unstaffed Groups</h3>
              <button
                className="text-gray-600 hover:text-gray-800 text-xl font-bold"
                onClick={() => setShowUnstaffedModal(false)}
              >
                ×
              </button>
            </div>
            <div className="space-y-4">
              {unstaffedGroups.map((info) => {
                const { rotation, groupKey } = info;
                const group = groups[rotation][groupKey];
                const skill = group.skill;
                // Build list of available staffers: any staffer without a current assignment in this rotation
                const available = staffers.filter((st) => {
                  const assignmentsForStaffer = staffAssignmentsState[st.name];
                  const isFree = !assignmentsForStaffer || assignmentsForStaffer[rotation] === null;
                  if (!isFree) return false;
                  return stafferCanTeachSkill(st, skill);
                });
                const selectKey = `${rotation}_${groupKey}`;
                return (
                  <div key={selectKey} className="border p-2 rounded">
                    <div className="mb-2">
                      <span className="font-medium">Rotation {rotation + 1} – {skill} (Group {groupKey})</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <select
                        className="border rounded px-2 py-1"
                        value={selectedUnstaffedStaffers[selectKey] || ''}
                        onChange={(e) => {
                          const chosen = e.target.value;
                          setSelectedUnstaffedStaffers((prev) => ({
                            ...prev,
                            [selectKey]: chosen,
                          }));
                        }}
                      >
                        <option value="">Select Staffer</option>
                        {available.map((st) => (
                          <option key={st.name} value={st.name}>{st.name}</option>
                        ))}
                      </select>
                      <button
                        className="bg-blue-600 hover:bg-blue-700 text-white px-3 py-1 rounded text-sm"
                        disabled={!selectedUnstaffedStaffers[selectKey]}
                        onClick={() => {
                          const chosen = selectedUnstaffedStaffers[selectKey];
                          if (chosen) {
                            handleAssignUnstaffedGroup(rotation, groupKey, chosen);
                          }
                        }}
                      >
                        Assign
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* Modal for managing unplaced campers (blocking until all are assigned) */}
      {showUnplacedModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-60">
          <div className="bg-white rounded shadow-lg w-[96vw] max-w-[1500px] h-[94vh] overflow-hidden p-4 relative flex flex-col">
            <div className="flex justify-between items-start mb-4">
              <h3 className="text-xl font-semibold">Unplaced Campers ({unplaced.length})</h3>
              <button
                className="text-gray-600 hover:text-gray-800 text-xl font-bold"
                onClick={() => setShowUnplacedModal(false)}
                title="Close"
              >
                ×
              </button>
            </div>
            {assignments && groups ? (
              <div className="flex-1 min-h-0 overflow-auto">
                {lastRunSummary && lastRunSummary.cancelled && (
                  <div className="mb-4 rounded border border-gray-300 bg-gray-50 p-3 text-sm">
                    <div className="font-medium">Stopped Run Stats</div>
                    <div className="text-xs text-gray-600 mb-2">Placements by preference rank:</div>
                    <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
                      {Array.from({ length: 10 }, (_, idx) => {
                        const rank = idx + 1;
                        return (
                          <div key={rank}>Pref #{rank}: {lastRunSummary.preferenceHits[rank] || 0}</div>
                        );
                      })}
                      <div>Fallback: {lastRunSummary.preferenceHits.none || 0}</div>
                    </div>
                    <button
                      onClick={handleTryAgain}
                      disabled={isScheduling}
                      className={`mt-3 rounded px-3 py-2 text-white ${isScheduling ? 'bg-blue-300 cursor-not-allowed' : 'bg-blue-600 hover:bg-blue-700'}`}
                    >
                      Try Again
                    </button>
                  </div>
                )}
                {unplaced.length > 0 && (
                  <div className="mb-3 flex items-center justify-between gap-3">
                    <p className="text-xs text-gray-600">Click a skill option button to assign instantly.</p>
                    <button
                      onClick={handleAutoSelectRecommended}
                      disabled={isScheduling || unplaced.length === 0}
                      className={`rounded px-3 py-2 text-xs text-white ${isScheduling || unplaced.length === 0 ? 'bg-gray-300 cursor-not-allowed' : 'bg-blue-600 hover:bg-blue-700'}`}
                    >
                      Auto-Select Recommended
                    </button>
                  </div>
                )}
                {unplaced.length === 0 ? (
                  <div className="flex justify-end">
                    <button
                      className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded"
                      onClick={() => setShowUnplacedModal(false)}
                    >
                      Done
                    </button>
                  </div>
                ) : (
                  <div className="overflow-x-hidden">
                    <table className="w-full table-fixed text-sm">
                      <colgroup>
                        <col style={{ width: '9%' }} />
                        <col style={{ width: '10%' }} />
                        <col style={{ width: '20%' }} />
                        <col style={{ width: '23%' }} />
                        <col style={{ width: '8%' }} />
                        <col style={{ width: '30%' }} />
                      </colgroup>
                      <thead className="sticky top-0 z-10 bg-gray-100">
                        <tr>
                          <th className="border px-2 py-1 text-left">Name</th>
                          <th className="border px-2 py-1 text-left">Tribe/Gender</th>
                          <th className="border px-2 py-1 text-left">Assigned Skills</th>
                          <th className="border px-2 py-1 text-left">Requested Not Yet Assigned</th>
                          <th className="border px-2 py-1 text-left">Rotation</th>
                          <th className="border px-2 py-1 text-left">Skill Options</th>
                        </tr>
                      </thead>
                      <tbody>
                        {unplaced.map((camper) => {
                          const assignedSkills = getAssignedSkillsForCamper(camper);
                          const requestedNotAssigned = getRequestedNotYetAssignedSkills(camper);
                          const rankedCandidates = getRankedManualCandidatesForCamper(camper);
                          const rotationIndex = rankedCandidates.length > 0
                            ? rankedCandidates[0].rotationIndex
                            : null;
                          const rankedOptions = rotationIndex !== null
                            ? getRankedManualCandidatesForCamper(camper, rotationIndex)
                            : [];
                          const recommendedCandidate = selectRecommendedManualCandidate(rankedOptions);
                          return (
                            <tr key={getCamperAssignmentKey(camper)} className="border-b align-top">
                              <td className="border px-2 py-1 whitespace-nowrap font-medium truncate" title={camper.name}>
                                {camper.name}
                              </td>
                              <td className="border px-2 py-1 text-xs whitespace-nowrap">
                                {camper.tribe} / {camper.gender}
                              </td>
                              <td className="border px-2 py-1">
                                <div className="max-h-20 overflow-y-auto pr-1 text-[11px] leading-4 break-words">
                                  {assignedSkills.length > 0 ? assignedSkills.join(', ') : '—'}
                                </div>
                              </td>
                              <td className="border px-2 py-1">
                                <div className="max-h-20 overflow-y-auto pr-1 text-[11px] leading-4 break-words">
                                  {requestedNotAssigned.length > 0 ? requestedNotAssigned.join(', ') : '—'}
                                </div>
                              </td>
                              <td className="border px-2 py-1">
                                {rotationIndex === null ? (
                                  <span className="text-xs text-gray-500">No open rotation</span>
                                ) : (
                                  <span className="text-xs font-medium">Rotation {rotationIndex + 1}</span>
                                )}
                              </td>
                              <td className="border px-2 py-1">
                                {rotationIndex === null || rankedOptions.length === 0 ? (
                                  <span className="text-xs text-gray-500">No eligible skills</span>
                                ) : (
                                  <div className="max-h-24 overflow-y-auto space-y-1 pr-1">
                                    {rankedOptions.map((option) => {
                                      const group = groups[rotationIndex][option.groupKey];
                                      if (!group) return null;
                                      const isRecommended = recommendedCandidate?.groupKey === option.groupKey;
                                      const meta = [];
                                      meta.push(`breaks:${option.breakCount}`);
                                      if (option.overflow > 0) meta.push(`+${option.overflow} over max`);
                                      if (option.underMinBreak) meta.push(`below min ${option.projectedSize}/${option.minSize}`);
                                      if (option.tribeOverrideBreak) meta.push('tribe override');
                                      if (option.notRequested) meta.push('not requested');
                                      if (option.preferenceRank) meta.push(`pref#${option.preferenceRank}`);
                                      return (
                                        <button
                                          key={option.groupKey}
                                          type="button"
                                          className={`w-full rounded border px-2 py-1 text-left text-[11px] leading-4 ${isRecommended ? 'border-green-500 bg-green-50 hover:bg-green-100' : 'border-gray-300 bg-white hover:bg-gray-50'}`}
                                          onClick={() => handleManualAssign(camper, rotationIndex, option.groupKey)}
                                        >
                                          <div className="font-medium">
                                            {isRecommended ? 'Rec • ' : ''}
                                            {option.skill} ({group.campers.length})
                                          </div>
                                          <div className="text-[10px] text-gray-600">
                                            {meta.join(' · ')}
                                          </div>
                                        </button>
                                      );
                                    })}
                                  </div>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            ) : (
              <p className="text-gray-600">Scheduling not yet performed.</p>
            )}
          </div>
        </div>
      )}

      {/* Modal for editing a staffer's schedule */}
      {selectedStafferDetail && staffAssignmentsState && groups && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white rounded shadow-lg w-11/12 max-w-3xl max-h-[90vh] overflow-auto p-4 relative">
            <div className="flex justify-between items-start mb-4">
              <div>
                <h3 className="text-xl font-semibold mb-1">{selectedStafferDetail.name}</h3>
                <p className="text-sm text-gray-600 mb-1">Skills: {selectedStafferDetail.skills.join(', ')}</p>
              </div>
              <button
                className="text-gray-600 hover:text-gray-800 text-xl font-bold"
                onClick={() => setSelectedStafferDetail(null)}
              >
                ×
              </button>
            </div>
            <div className="overflow-auto">
              <table className="min-w-full text-sm border">
                <thead>
                  <tr>
                    <th className="border px-2 py-1">Rotation</th>
                    <th className="border px-2 py-1">Current Skill</th>
                    <th className="border px-2 py-1">Change to Group</th>
                    <th className="border px-2 py-1">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {[0,1,2,3,4,5].map((r) => {
                    const stName = selectedStafferDetail.name;
                    const currentKey = staffAssignmentsState[stName] ? staffAssignmentsState[stName][r] : null;
                    let currentSkill = '';
                    if (currentKey && groups[r] && groups[r][currentKey]) {
                      currentSkill = groups[r][currentKey].skill;
                    }
                    // Determine available groups for this staffer in this rotation
                    let availableGroupKeys = [];
                    const rotationGroups = groups[r] || {};
                    Object.keys(rotationGroups).forEach((gKey) => {
                      const g = rotationGroups[gKey];
                      const canTeach = selectedStafferDetail.skills.includes(g.skill);
                      if (canTeach) {
                        availableGroupKeys.push(gKey);
                      }
                    });
                    return (
                      <tr key={r} className="border-b">
                        <td className="border px-2 py-1 text-center">{r + 1}</td>
                        <td className="border px-2 py-1 whitespace-nowrap">{currentSkill || '—'}</td>
                        <td className="border px-2 py-1">
                          <select
                            className="border rounded px-1 py-0.5"
                            value={currentKey || ''}
                            onChange={(e) => {
                              const newKey = e.target.value;
                              if (newKey) {
                                moveStafferToNewGroup(selectedStafferDetail, r, newKey);
                              }
                            }}
                          >
                            <option value="">-- Select --</option>
                            {availableGroupKeys.map((gKey) => {
                              const g = groups[r][gKey];
                              const label = `${g.skill} (${gKey})`;
                              return (
                                <option key={gKey} value={gKey}>{label}</option>
                              );
                            })}
                          </select>
                        </td>
                        <td className="border px-2 py-1 text-center">
                          {currentKey ? (
                            <button
                              className="bg-red-500 hover:bg-red-600 text-white px-2 py-1 rounded text-xs"
                              onClick={() => removeStaffAssignment(selectedStafferDetail, r)}
                            >
                              Remove
                            </button>
                          ) : (
                            <span className="text-gray-400">—</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Modal for creating or editing a persisted staff team. */}
      {showStaffListModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white rounded shadow-lg w-11/12 max-w-4xl max-h-[90vh] overflow-auto p-4 relative">
            <div className="flex justify-between items-start mb-4">
              <div>
                <h3 className="text-xl font-semibold">{editingStaffTeamId == null ? 'Create Staff Team' : 'Edit Staff Team'}</h3>
                <p className="text-sm text-gray-600">Ordered skills are saved with this team and reused across locations.</p>
              </div>
              <button
                className="text-gray-600 hover:text-gray-800 text-xl font-bold"
                onClick={closeStaffTeamModal}
              >
                ×
              </button>
            </div>
            <div className="mb-4">
              <label className="block text-sm font-medium mb-1" htmlFor="staffTeamName">Team Name</label>
              <input
                id="staffTeamName"
                type="text"
                value={editingStaffTeamName}
                onChange={(e) => setEditingStaffTeamName(e.target.value)}
                className="w-full border border-gray-300 rounded px-3 py-2"
              />
            </div>
            <div className="overflow-auto">
              <table className="min-w-full text-sm border">
                <thead>
                  <tr>
                    <th className="border px-2 py-1">Name</th>
                    <th className="border px-2 py-1">Ordered Skills</th>
                    <th className="border px-2 py-1">Delete</th>
                  </tr>
                </thead>
                <tbody>
                  {editedStaffList.map((st, idx) => (
                    <tr key={idx}>
                      <td className="border px-2 py-1">
                        <input
                          type="text"
                          value={st.name}
                          onChange={(e) => {
                            const val = e.target.value;
                            setEditedStaffList((prev) => {
                              const copy = prev.map((s) => ({ name: s.name, skills: [...(s.skills || [])] }));
                              copy[idx].name = val;
                              return copy;
                            });
                          }}
                          className="w-full border border-gray-300 rounded px-2 py-1"
                        />
                      </td>
                      <td className="border px-2 py-1">
                        <input
                          type="text"
                          value={(st.skills || []).join(', ')}
                          onChange={(e) => {
                            const val = e.target.value;
                            setEditedStaffList((prev) => {
                              const copy = prev.map((s) => ({ name: s.name, skills: [...(s.skills || [])] }));
                              copy[idx].skills = val.split(',').map((value) => value.trim()).filter(Boolean);
                              return copy;
                            });
                          }}
                          className="w-full border border-gray-300 rounded px-2 py-1"
                          placeholder="Primary skill first, then fallback skills"
                        />
                        <div className="mt-1 text-xs text-gray-500">
                          The first skill is the strong default. Later skills are fallbacks only.
                        </div>
                      </td>
                      <td className="border px-2 py-1 text-center">
                        <button
                          className="bg-red-500 hover:bg-red-600 text-white px-2 py-1 rounded"
                          onClick={() => {
                            setEditedStaffList((prev) => prev.filter((_, i) => i !== idx));
                          }}
                        >
                          Delete
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="mt-4 flex justify-between items-center">
              <button
                className="bg-blue-600 hover:bg-blue-700 text-white px-3 py-1 rounded text-sm"
                onClick={() => {
                  // Add a new empty staffer row
                  setEditedStaffList((prev) => [...prev, { name: '', skills: [] }]);
                }}
              >
                Add Staffer
              </button>
              <div className="flex space-x-2">
                <button
                  className="bg-gray-500 hover:bg-gray-600 text-white px-4 py-2 rounded"
                  onClick={closeStaffTeamModal}
                >
                  Cancel
                </button>
                <button
                  className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded"
                  onClick={saveEditedStaffTeam}
                >
                  Save
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {pendingWorkbookImport && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white rounded shadow-lg w-11/12 max-w-lg p-4">
            <div className="flex justify-between items-start mb-4">
              <div>
                <h3 className="text-xl font-semibold">Choose Import Sheet</h3>
                <p className="text-sm text-gray-600">{pendingWorkbookImport.fileName}</p>
              </div>
              <button
                className="text-gray-600 hover:text-gray-800 text-xl font-bold"
                onClick={() => setPendingWorkbookImport(null)}
              >
                ×
              </button>
            </div>
            <label className="block text-sm font-medium mb-1" htmlFor="workbookSheetSelect">Sheet</label>
            <select
              id="workbookSheetSelect"
              value={pendingWorkbookImport.selectedSheet}
              onChange={(e) => setPendingWorkbookImport((prev) => ({ ...prev, selectedSheet: e.target.value }))}
              className="w-full border border-gray-300 rounded px-3 py-2"
            >
              {pendingWorkbookImport.sheetNames.map((sheetName) => (
                <option key={sheetName} value={sheetName}>{sheetName}</option>
              ))}
            </select>
            <div className="mt-4 flex justify-end gap-2">
              <button
                className="bg-gray-500 hover:bg-gray-600 text-white px-4 py-2 rounded"
                onClick={() => setPendingWorkbookImport(null)}
              >
                Cancel
              </button>
              <button
                className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded"
                onClick={() => {
                  const rows = filterWorkbookRows(
                    sheetToRowObjects(
                      pendingWorkbookImport.workbook,
                      pendingWorkbookImport.selectedSheet
                    )
                  );
                  loadCamperRowsIntoState(
                    rows,
                    `${pendingWorkbookImport.fileName} / ${pendingWorkbookImport.selectedSheet}`
                  );
                  setPendingWorkbookImport(null);
                }}
              >
                Import Sheet
              </button>
            </div>
          </div>
        </div>
      )}
      {pendingAnalysisWorkbook && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white rounded shadow-lg w-11/12 max-w-lg p-4">
            <div className="flex justify-between items-start mb-4">
              <div>
                <h3 className="text-xl font-semibold">Choose Registration Sheet</h3>
                <p className="text-sm text-gray-600">{pendingAnalysisWorkbook.fileName}</p>
              </div>
              <button
                className="text-gray-600 hover:text-gray-800 text-xl font-bold"
                onClick={() => setPendingAnalysisWorkbook(null)}
              >
                ×
              </button>
            </div>
            <label className="block text-sm font-medium mb-1" htmlFor="analysisWorkbookSheetSelect">Sheet</label>
            <select
              id="analysisWorkbookSheetSelect"
              value={pendingAnalysisWorkbook.selectedSheet}
              onChange={(e) => setPendingAnalysisWorkbook((prev) => ({ ...prev, selectedSheet: e.target.value }))}
              className="w-full border border-gray-300 rounded px-3 py-2"
            >
              {pendingAnalysisWorkbook.sheetNames.map((sheetName) => (
                <option key={sheetName} value={sheetName}>{sheetName}</option>
              ))}
            </select>
            <div className="mt-4 flex justify-end gap-2">
              <button
                className="bg-gray-500 hover:bg-gray-600 text-white px-4 py-2 rounded"
                onClick={() => setPendingAnalysisWorkbook(null)}
              >
                Cancel
              </button>
              <button
                className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded"
                onClick={() => {
                  const rows = filterWorkbookRows(
                    sheetToRowObjects(
                      pendingAnalysisWorkbook.workbook,
                      pendingAnalysisWorkbook.selectedSheet
                    )
                  );
                  setAnalysisRegistrationRows(rows);
                  setAnalysisRegistrationSource(`${pendingAnalysisWorkbook.fileName} / ${pendingAnalysisWorkbook.selectedSheet}`);
                  setAnalysisReport(null);
                  setAnalysisMessage(`Loaded registration workbook: ${pendingAnalysisWorkbook.fileName}`);
                  setPendingAnalysisWorkbook(null);
                }}
              >
                Load Sheet
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export {
  assignStaffersToGroups,
  buildStaffersFromTeams,
  buildConstraintAuditSummary,
  buildManualPlacementCandidate,
  selectRecommendedManualCandidate,
  applyCamperPlacementToState,
  buildExportValidationReport,
  buildRotationSkillMatrix,
  getManualSkillRepeatLimit,
  stafferCanTeachSkill,
  assignmentsToCSV,
  staffAssignmentsToCSV,
  updateUnplacedListAfterRemoval,
  summarizeDemandDiagnostics,
  serializeSolverDiagnostics,
  summarizeDeepRepairDiagnostics,
  buildStoppedDeepRepairCheckpointResult,
  parseCamperRow,
  parseStafferRow,
  finalizeImportedCampers,
  buildPresetStaffers,
  prepareSchedulingInputs,
  runSchedulingPipeline,
};
